// Generated from the API's enums by apps/api/Tests/Architecture/WireTypesTests.cs. Do not edit.
// Regenerate: WIRE_TYPES_REGEN=1 dotnet test apps/api/Tests/HooverCanvassingApi.Tests.csproj --filter FullyQualifiedName~WireTypesTests

/**
 * HooverCanvassingApi.Features.Finance.Plaid.AccountSelectionStatus
 *
 * Whether account selection completed, or the connection wasn't found in this campaign.
 */
export const AccountSelectionStatus = {
  Completed: 'Completed',
  ConnectionNotFound: 'ConnectionNotFound',
} as const;
export type AccountSelectionStatus = (typeof AccountSelectionStatus)[keyof typeof AccountSelectionStatus];
export declare namespace AccountSelectionStatus {
  type Completed = 'Completed';
  type ConnectionNotFound = 'ConnectionNotFound';
}

/**
 * HooverCanvassingApi.Features.Finance.ActivationType
 *
 * When a reporting-schedule entry applies. There is no `ElectionYearOnly`: pre-election cadences select via explicit window bounds on `ReportingScheduleEntry` (`windowStart`/`windowEndDaysBeforeElection`), and fixed-date entries that need election-year-only filtering use the same bounds; the deadline generator checks whether the due date's offset to the candidate election falls in the window.
 */
export const ActivationType = {
  Always: 'Always',
  OffYearOnly: 'OffYearOnly',
  PreElectionWindow: 'PreElectionWindow',
  PostElectionWindow: 'PostElectionWindow',
  Once: 'Once',
  /** Mississippi's off-year annual. Like `ActivationType.OffYearOnly` it fires only when the campaign is OFF the ballot, but it keys that per-year check on the COVERAGE year (the evaluated period's start), not the due-date year, on the month/day fixed-date path. Mississippi's annual covers a calendar year but is due the FOLLOWING January 31: keyed on the due-date year (as OffYearOnly is) a 2026-coverage annual due January 2027 would be judged against 2027, a statewide election year, and wrongly suppressed. This is the coverage-year keying the last-weekday generator already uses, brought to the month/day path under a new, opt-in name so OffYearOnly (Georgia, North Carolina) is left exactly as it is. APPENDED, never reordered: the value persists as a string on generated deadlines. See `DeadlineService.IsYearActivationSatisfied` and `GenerateFixedDueDate`. */
  OffYearByCoverageYear: 'OffYearByCoverageYear',
} as const;
export type ActivationType = (typeof ActivationType)[keyof typeof ActivationType];
export declare namespace ActivationType {
  type Always = 'Always';
  type OffYearOnly = 'OffYearOnly';
  type PreElectionWindow = 'PreElectionWindow';
  type PostElectionWindow = 'PostElectionWindow';
  type Once = 'Once';
  type OffYearByCoverageYear = 'OffYearByCoverageYear';
}

/**
 * HooverCanvassingApi.Features.Fundraising.ActivityTone
 *
 * How a last-activity line reads.
 */
export const ActivityTone = {
  /** Nothing to act on. */
  Muted: 'Muted',
  /** Money promised. */
  Good: 'Good',
  /** Something is owed: a call back, or a dial nobody logged. */
  Warn: 'Warn',
  /** A no: declined, wrong number, or asked not to be called. */
  Bad: 'Bad',
} as const;
export type ActivityTone = (typeof ActivityTone)[keyof typeof ActivityTone];
export declare namespace ActivityTone {
  type Muted = 'Muted';
  type Good = 'Good';
  type Warn = 'Warn';
  type Bad = 'Bad';
}

/**
 * HooverCanvassingApi.Common.Agent.AgentWriteKind
 *
 * Which questions a write's check asks. A disowning write ("not mine") also asks whether the member said the thing is not theirs, since an explanation of a charge otherwise reads as agreement with whatever the run did about it.
 */
export const AgentWriteKind = {
  Change: 'Change',
  Disown: 'Disown',
} as const;
export type AgentWriteKind = (typeof AgentWriteKind)[keyof typeof AgentWriteKind];
export declare namespace AgentWriteKind {
  type Change = 'Change';
  type Disown = 'Disown';
}

/** HooverCanvassingApi.Common.Agent.AgentWriteVerdict */
export const AgentWriteVerdict = {
  Clear: 'Clear',
  NeedsConfirmation: 'NeedsConfirmation',
} as const;
export type AgentWriteVerdict = (typeof AgentWriteVerdict)[keyof typeof AgentWriteVerdict];
export declare namespace AgentWriteVerdict {
  type Clear = 'Clear';
  type NeedsConfirmation = 'NeedsConfirmation';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.Alabama.Xml.AlabamaXmlCommitteeShape
 *
 * The AL FCPA submission flow treats Principal Campaign Committees (a candidate's committee) and Political Action Committees (independent expenditure groups) differently — the v6.0 spec's cross-record validity rules (e.g. exPurpose=04 Contribution is PAC-only, exPurpose=11 Charitable Contribution is PCC-only, cbContributionSource=04 PAC is valid only on PCCs) hinge on the recipient committee's shape.
 *
 * This enum is consumed by `AlabamaXmlCrossRecordValidator` and the builder. The default for canvasser-app campaigns is `AlabamaXmlCommitteeShape.PrincipalCampaignCommittee` because the app currently targets candidate campaigns; PAC support requires the campaign-shape field to be added to `CampaignFinanceSettings` as a follow-up.
 */
export const AlabamaXmlCommitteeShape = {
  PrincipalCampaignCommittee: 'PrincipalCampaignCommittee',
  PoliticalActionCommittee: 'PoliticalActionCommittee',
} as const;
export type AlabamaXmlCommitteeShape = (typeof AlabamaXmlCommitteeShape)[keyof typeof AlabamaXmlCommitteeShape];
export declare namespace AlabamaXmlCommitteeShape {
  type PrincipalCampaignCommittee = 'PrincipalCampaignCommittee';
  type PoliticalActionCommittee = 'PoliticalActionCommittee';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.AmendmentDiffCategory
 *
 * Which of the amendment's money schedules a diff section covers. Deliberately the three the file path freezes child evidence for and the assembly reproduces faithfully.
 *
 * LOANS are excluded. Every ruleset that can reach this surface — federal, LA, VA, NC, all of which make an in-system amendment child — declares `amendmentLoanBalanceBasis: FrozenPeriodClose`, so their amendments report the balance reconciled at file time, NOT what the live loan carries. Comparing the frozen balance against the live one would report every post-filing repayment as a change the amendment is making. (Alabama is the lone `LiveCurrent` declaration, and its PortalOnly amendment model never creates a child that gets here. Should a future `LiveCurrent` jurisdiction file in-system amendments, a loans section becomes meaningful for it — and would have to be gated on that declared policy, never on a state name.)
 */
export const AmendmentDiffCategory = {
  Contributions: 'Contributions',
  Expenditures: 'Expenditures',
  Refunds: 'Refunds',
} as const;
export type AmendmentDiffCategory = (typeof AmendmentDiffCategory)[keyof typeof AmendmentDiffCategory];
export declare namespace AmendmentDiffCategory {
  type Contributions = 'Contributions';
  type Expenditures = 'Expenditures';
  type Refunds = 'Refunds';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.AmendmentDiffChange
 *
 * What happened to one row between the two filings.
 */
export const AmendmentDiffChange = {
  /** On the amendment, on no earlier filing. */
  Added: 'Added',
  /** On both filings, at a different amount or date. */
  Altered: 'Altered',
  /** On the earlier filing, off the amendment. */
  Removed: 'Removed',
} as const;
export type AmendmentDiffChange = (typeof AmendmentDiffChange)[keyof typeof AmendmentDiffChange];
export declare namespace AmendmentDiffChange {
  type Added = 'Added';
  type Altered = 'Altered';
  type Removed = 'Removed';
}

/**
 * HooverCanvassingApi.Features.Finance.AmendmentLoanBalanceBasis
 *
 * FEC-018 (D9/K2) — the per-jurisdiction loan-balance basis used by Form-10 / Schedule-C debts on an amendment. Serialized as a string in ruleset JSON.
 */
export const AmendmentLoanBalanceBasis = {
  /** Federal — debts use the frozen period-close balance (re-materialize + overwrite). */
  FrozenPeriodClose: 'FrozenPeriodClose',
  /** Alabama — debts use the live current balance (no re-materialize; byte-identical). */
  LiveCurrent: 'LiveCurrent',
} as const;
export type AmendmentLoanBalanceBasis = (typeof AmendmentLoanBalanceBasis)[keyof typeof AmendmentLoanBalanceBasis];
export declare namespace AmendmentLoanBalanceBasis {
  type FrozenPeriodClose = 'FrozenPeriodClose';
  type LiveCurrent = 'LiveCurrent';
}

/**
 * HooverCanvassingApi.Features.Finance.AmendmentModel
 *
 * ADR-021 — how a jurisdiction files an AMENDMENT, the `filingProfile.amendmentModel` axis. The decision site (`ReportFilingService.AmendReportAsync`) branches on it so amendment behaviour is a DECLARED per-jurisdiction policy, never an `isLouisiana`/state boolean. It is the one amendment axis: stamped on a filed package's manifest and the `X-Amendment-Model` response header, and read wherever the treasurer UI decides the amendment workflow, which is why federal and LA/VA stay distinct there.
 *
 * - `AmendmentModel.SelfContainedUpload` (LA, VA, MD, NC) — the format carries the amendment metadata (e.g. the `.PDP` `CVR` amended flag, the `.cfd` `COVER`), so an amendment is a fresh in-system child that regenerates the WHOLE report and is re-sent (Supersede). A treasurer-attested source CAN be amended in-product.
 * - `AmendmentModel.ElectronicResubmit` (federal) — the `.fec` is resubmitted as an amendment that supersedes the prior filing. Also amendable in-product.
 * - `AmendmentModel.PortalOnly` (Alabama) — the FCPA portal is append-only (ADR-020), so a re-upload would duplicate every row; corrections are made directly in the portal. A treasurer-attested source — one finalized by an attestation, so the app holds no package to rebuild from — is blocked.
 *
 * The axis is RESOLVED on every origin that reaches the amend path, and the block above applies to the package-less attested origins (`FilingOriginExtensions.IsTreasurerAttested`) — see `ReportFilingService.AmendReportAsync` for why those two are separate. Plain enum (a parse target, not JSON-bound — the raw axis stays a `string?` on `FilingProfile` so an unrecognised value throws on-use naming the axis, ADR-021).
 */
export const AmendmentModel = {
  SelfContainedUpload: 'SelfContainedUpload',
  ElectronicResubmit: 'ElectronicResubmit',
  PortalOnly: 'PortalOnly',
} as const;
export type AmendmentModel = (typeof AmendmentModel)[keyof typeof AmendmentModel];
export declare namespace AmendmentModel {
  type SelfContainedUpload = 'SelfContainedUpload';
  type ElectronicResubmit = 'ElectronicResubmit';
  type PortalOnly = 'PortalOnly';
}

/**
 * HooverCanvassingApi.Features.Analytics.FlagSeverity
 *
 * Severity level for attention-needed flags. Serialized as lowercase to match the frontend TypeScript type: 'warning' | 'critical'.
 */
export const AnalyticsFlagSeverity = {
  Warning: 'warning',
  Critical: 'critical',
} as const;
export type AnalyticsFlagSeverity = (typeof AnalyticsFlagSeverity)[keyof typeof AnalyticsFlagSeverity];
export declare namespace AnalyticsFlagSeverity {
  type Warning = 'warning';
  type Critical = 'critical';
}

/**
 * HooverCanvassingApi.Features.Assistant.ApprovalCeiling
 *
 * The loosest approval a kind ever allows, ordered so the effective policy is the minimum.
 */
export const ApprovalCeiling = {
  AlwaysAsk: 'AlwaysAsk',
  AutoAllow: 'AutoAllow',
} as const;
export type ApprovalCeiling = (typeof ApprovalCeiling)[keyof typeof ApprovalCeiling];
export declare namespace ApprovalCeiling {
  type AlwaysAsk = 'AlwaysAsk';
  type AutoAllow = 'AutoAllow';
}

/**
 * HooverCanvassingApi.Features.Assistant.Domains.Finance.ApproverKind
 *
 * Whose tap an answer still needs: the filer's own, a finance manager's, or nobody's because it is recorded.
 */
export const ApproverKind = {
  You: 'You',
  FinanceManager: 'FinanceManager',
  Nobody: 'Nobody',
} as const;
export type ApproverKind = (typeof ApproverKind)[keyof typeof ApproverKind];
export declare namespace ApproverKind {
  type You = 'You';
  type FinanceManager = 'FinanceManager';
  type Nobody = 'Nobody';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.AsFiledInputBasis
 *
 * Whether a report's assembled loan + debt inputs are the ones its FILING carried. Those two streams are not in the `Confirmed→Reported` file-flip that freezes contributions, expenditures and refunds, so they are the only ones a post-filing read can silently get wrong.
 */
export const AsFiledInputBasis = {
  /** A not-yet-filed report: live IS the right basis, because nothing has filed. */
  Live: 'Live',
  /** A post-filing report replaying its own package's frozen loan/debt evidence — the basis in which its displayed cover equals the file it came from. */
  Frozen: 'Frozen',
  /** A post-filing report with NO frozen loan/debt evidence to replay: no package at all (an ADR-018 externally-filed backfill, or a treasurer-attested "marked filed in portal" filing), or a package generated before the freeze existed. The loans/debts fell back to live, so the assembled set is a recompute — usable, but a caller must not present it as as-filed. */
  Unavailable: 'Unavailable',
} as const;
export type AsFiledInputBasis = (typeof AsFiledInputBasis)[keyof typeof AsFiledInputBasis];
export declare namespace AsFiledInputBasis {
  type Live = 'Live';
  type Frozen = 'Frozen';
  type Unavailable = 'Unavailable';
}

/**
 * HooverCanvassingApi.Features.Fundraising.CallTime.AskBasis
 *
 * What an `CallQueueEntryDto.Ask` was sized from, so the Caller can trust the number they say out loud. A closed set that grows: an Ask Override and, once a Prospect is tied to money received, their largest gift to us join it.
 */
export const AskBasis = {
  /** The largest itemized gift in the Prospect's accepted federal Giving History. */
  FederalLargestGift: 'FederalLargestGift',
} as const;
export type AskBasis = (typeof AskBasis)[keyof typeof AskBasis];
export declare namespace AskBasis {
  type FederalLargestGift = 'FederalLargestGift';
}

/** HooverCanvassingApi.Features.Assistant.AssistantMessageRole */
export const AssistantMessageRole = {
  User: 'User',
  Assistant: 'Assistant',
  Moment: 'Moment',
} as const;
export type AssistantMessageRole = (typeof AssistantMessageRole)[keyof typeof AssistantMessageRole];
export declare namespace AssistantMessageRole {
  type User = 'User';
  type Assistant = 'Assistant';
  type Moment = 'Moment';
}

/**
 * HooverCanvassingApi.Features.Assistant.AssistantNoteKind
 *
 * `AssistantNoteKind.UserStated`: what the user said ("only pledges over $500"). `AssistantNoteKind.ModelObserved`: what the model noticed about working patterns ("the candidate usually calls around 2pm").
 */
export const AssistantNoteKind = {
  UserStated: 'UserStated',
  ModelObserved: 'ModelObserved',
} as const;
export type AssistantNoteKind = (typeof AssistantNoteKind)[keyof typeof AssistantNoteKind];
export declare namespace AssistantNoteKind {
  type UserStated = 'UserStated';
  type ModelObserved = 'ModelObserved';
}

/**
 * HooverCanvassingApi.Features.Messaging.AttestationSignerRole
 *
 * The signer's role in the attestation.
 */
export const AttestationSignerRole = {
  Candidate: 'Candidate',
  Treasurer: 'Treasurer',
  CampaignManager: 'CampaignManager',
  AuthorizedStaff: 'AuthorizedStaff',
  Other: 'Other',
} as const;
export type AttestationSignerRole = (typeof AttestationSignerRole)[keyof typeof AttestationSignerRole];
export declare namespace AttestationSignerRole {
  type Candidate = 'Candidate';
  type Treasurer = 'Treasurer';
  type CampaignManager = 'CampaignManager';
  type AuthorizedStaff = 'AuthorizedStaff';
  type Other = 'Other';
}

/**
 * HooverCanvassingApi.Features.Assistant.Audience
 *
 * Who is there when a run speaks: the user reading a chat, or nobody. A maintenance run also does no work the member would see.
 */
export const Audience = {
  Chat: 'Chat',
  Unattended: 'Unattended',
  Maintenance: 'Maintenance',
} as const;
export type Audience = (typeof Audience)[keyof typeof Audience];
export declare namespace Audience {
  type Chat = 'Chat';
  type Unattended = 'Unattended';
  type Maintenance = 'Maintenance';
}

/**
 * HooverCanvassingApi.Features.Finance.BalanceFormShape
 *
 * FPD-050 — which account form to render in the per-processor balance entry surface (`PaymentProcessor.BalanceFormShape`). Drives the FPD-053 entry UI; FPD-055 reads it when computing in-flight ACH so a `BalanceFormShape.NotApplicable` processor (e.g. ManualP2P) is excluded from the balance-reconciliation math.
 */
export const BalanceFormShape = {
  /** Render one "Available balance" input — the common shape (Numero, WinRed, ActBlue, Anedot, Other). */
  AvailableOnly: 'AvailableOnly',
  /** Render "Available" + "Pending" inputs — Stripe's dashboard surfaces both. */
  AvailableAndPending: 'AvailableAndPending',
  /** Skip the balance prompt for this processor (ManualP2P). */
  NotApplicable: 'NotApplicable',
} as const;
export type BalanceFormShape = (typeof BalanceFormShape)[keyof typeof BalanceFormShape];
export declare namespace BalanceFormShape {
  type AvailableOnly = 'AvailableOnly';
  type AvailableAndPending = 'AvailableAndPending';
  type NotApplicable = 'NotApplicable';
}

/**
 * HooverCanvassingApi.Features.Finance.BankConnectionStatus
 *
 * Health of a `BankConnection` (one Plaid Item) — ADR-070. `BankConnectionStatus.Active`: the access token works and syncs run. `BankConnectionStatus.LoginRequired`: Plaid returned `ITEM_LOGIN_REQUIRED`; the treasurer must reconnect before syncs resume (#560). `BankConnectionStatus.Disconnected`: the Item was removed at Plaid; the connection is retained for history but never synced. Serializes PascalCase; stored as a string.
 */
export const BankConnectionStatus = {
  Active: 'Active',
  LoginRequired: 'LoginRequired',
  Disconnected: 'Disconnected',
} as const;
export type BankConnectionStatus = (typeof BankConnectionStatus)[keyof typeof BankConnectionStatus];
export declare namespace BankConnectionStatus {
  type Active = 'Active';
  type LoginRequired = 'LoginRequired';
  type Disconnected = 'Disconnected';
}

/**
 * HooverCanvassingApi.Features.Finance.BankFileFormat
 *
 * File format a BankSourceProfile parses: the formats `BankFileReader` can read.
 */
export const BankFileFormat = {
  Csv: 'Csv',
  Xlsx: 'Xlsx',
  Ofx: 'Ofx',
} as const;
export type BankFileFormat = (typeof BankFileFormat)[keyof typeof BankFileFormat];
export declare namespace BankFileFormat {
  type Csv = 'Csv';
  type Xlsx = 'Xlsx';
  type Ofx = 'Ofx';
}

/**
 * HooverCanvassingApi.Features.Finance.BankProvider
 *
 * The external service a `BankConnection` pulls transactions from (ADR-070). `BankProvider.Plaid` is the only provider in v1; the enum is a forward seam so a second provider (e.g. Stripe Financial Connections) needs no schema change. Serializes PascalCase on the wire and is stored as a string.
 */
export const BankProvider = {
  Plaid: 'Plaid',
} as const;
export type BankProvider = (typeof BankProvider)[keyof typeof BankProvider];
export declare namespace BankProvider {
  type Plaid = 'Plaid';
}

/**
 * HooverCanvassingApi.Features.Finance.BankTransactionAttribution
 *
 * What a BankTransaction is attributed to after reconciliation. ManualP2P = matched to a hand-entered Venmo/Zelle/Cash App contribution (PPR-015). Unattributed = awaiting treasurer action. Withdrawn = parent import was undone (an import-undo ghost, not a treasurer resolution). ExcludedFromReport = ADR-014's single bank-line non-reporting resolution. DepositOfRecordedChecks = the former lump check-deposit credit line (RETIRED as a live producer 2026-07-14 — see the member remark).
 *
 * ADR-014 retired Dismissed / NonProcessor / InterAccountTransfer as live producers. The three former non-reporting resolutions (Dismiss, Mark as transfer, Pair as transfer) collapsed into `BankTransactionAttribution.ExcludedFromReport`; the EFR-003 migration remapped every live row. The three members survive only for the migration string-compare and pre-collapse audit-JSON parse — do NOT write them from new code. (USI-006 had earlier mapped the retired processor-match attribution to InterAccountTransfer per ADR-011 Option A; that too is now ExcludedFromReport.)
 */
export const BankTransactionAttribution = {
  ManualP2P: 'ManualP2P',
  NonProcessor: 'NonProcessor',
  Unattributed: 'Unattributed',
  Withdrawn: 'Withdrawn',
  InterAccountTransfer: 'InterAccountTransfer',
  Dismissed: 'Dismissed',
  DepositOfRecordedChecks: 'DepositOfRecordedChecks',
  ExcludedFromReport: 'ExcludedFromReport',
} as const;
export type BankTransactionAttribution = (typeof BankTransactionAttribution)[keyof typeof BankTransactionAttribution];
export declare namespace BankTransactionAttribution {
  type ManualP2P = 'ManualP2P';
  type NonProcessor = 'NonProcessor';
  type Unattributed = 'Unattributed';
  type Withdrawn = 'Withdrawn';
  type InterAccountTransfer = 'InterAccountTransfer';
  type Dismissed = 'Dismissed';
  type DepositOfRecordedChecks = 'DepositOfRecordedChecks';
  type ExcludedFromReport = 'ExcludedFromReport';
}

/**
 * HooverCanvassingApi.Features.Finance.BankTransactionClearingStatus
 *
 * Whether a bank row has SETTLED at the bank (ADR-071 pending-ingestion amendment, #566). Orthogonal to `BankTransactionAttribution` (the treasurer's reporting disposition) — this is the BANK'S clearing state, a display/triage signal only.
 *
 * Reporting is settlement-independent (ADR-071): an `BankTransactionClearingStatus.Uncleared` row is a fully first-class bank row, classifiable exactly like a settled one — once the treasurer classifies it into a Contribution/Expenditure, that receipt counts on its receipt date regardless of when the bank clears. So this label gates NOTHING (not cash-on-hand, not filing eligibility); it only lets the treasurer distinguish a not-yet-settled line from a settled one.
 *
 * Every non-Plaid row (OFX/CSV/Xlsx/Manual) is a settled statement line, so the entity default and the migration backfill are `BankTransactionClearingStatus.Cleared`. Only a Plaid `pending` transaction lands `BankTransactionClearingStatus.Uncleared`; when Plaid later posts it (linked via `pending_transaction_id`) the SAME row flips to `BankTransactionClearingStatus.Cleared` in place — never a second row.
 */
export const BankTransactionClearingStatus = {
  /** Settled at the bank — the default for every statement-sourced row. */
  Cleared: 'Cleared',
  /** A Plaid `pending` (authorized, not-yet-posted) line; flips to `BankTransactionClearingStatus.Cleared` in place when it posts. */
  Uncleared: 'Uncleared',
} as const;
export type BankTransactionClearingStatus = (typeof BankTransactionClearingStatus)[keyof typeof BankTransactionClearingStatus];
export declare namespace BankTransactionClearingStatus {
  type Cleared = 'Cleared';
  type Uncleared = 'Uncleared';
}

/**
 * HooverCanvassingApi.Features.Finance.BankTransactionSource
 *
 * How a BankTransaction row entered the ledger. Plaid reserved for V2.
 */
export const BankTransactionSource = {
  Plaid: 'Plaid',
  Ofx: 'Ofx',
  Csv: 'Csv',
  Xlsx: 'Xlsx',
  Manual: 'Manual',
} as const;
export type BankTransactionSource = (typeof BankTransactionSource)[keyof typeof BankTransactionSource];
export declare namespace BankTransactionSource {
  type Plaid = 'Plaid';
  type Ofx = 'Ofx';
  type Csv = 'Csv';
  type Xlsx = 'Xlsx';
  type Manual = 'Manual';
}

/**
 * HooverCanvassingApi.Features.Fundraising.BookTimeZone
 *
 * The zones the Prospects book filters by — a Prospect's IANA zone folded to one of these (`ProspectTimeZone.BookZoneOf`); Arizona's counts as Mountain.
 */
export const BookTimeZone = {
  Eastern: 'Eastern',
  Central: 'Central',
  Mountain: 'Mountain',
  Pacific: 'Pacific',
  Alaska: 'Alaska',
  Hawaii: 'Hawaii',
  /** Neither the ZIP nor the state says. */
  Unknown: 'Unknown',
} as const;
export type BookTimeZone = (typeof BookTimeZone)[keyof typeof BookTimeZone];
export declare namespace BookTimeZone {
  type Eastern = 'Eastern';
  type Central = 'Central';
  type Mountain = 'Mountain';
  type Pacific = 'Pacific';
  type Alaska = 'Alaska';
  type Hawaii = 'Hawaii';
  type Unknown = 'Unknown';
}

/**
 * HooverCanvassingApi.Features.Calendar.CalendarConflictType
 *
 * The kind of a calendar sync conflict.
 */
export const CalendarConflictType = {
  /** External calendar had different time for a shift event. App time was preserved (app-authoritative). */
  ShiftTimeChanged: 'ShiftTimeChanged',
  /** Shift event was deleted in external calendar. Will be recreated on next sync. */
  ShiftEventDeleted: 'ShiftEventDeleted',
  /** Event could not be synced due to provider error. */
  SyncFailed: 'SyncFailed',
  /** Failed to sync a shift to the external calendar. Will be retried by the polling service. */
  OutboundSyncFailed: 'OutboundSyncFailed',
  /** Other conflict type. */
  Other: 'Other',
} as const;
export type CalendarConflictType = (typeof CalendarConflictType)[keyof typeof CalendarConflictType];
export declare namespace CalendarConflictType {
  type ShiftTimeChanged = 'ShiftTimeChanged';
  type ShiftEventDeleted = 'ShiftEventDeleted';
  type SyncFailed = 'SyncFailed';
  type OutboundSyncFailed = 'OutboundSyncFailed';
  type Other = 'Other';
}

/**
 * HooverCanvassingApi.Features.Calendar.CalendarConnectionStatus
 *
 * Connection status for OAuth tokens
 */
export const CalendarConnectionStatus = {
  /** Token is valid and working */
  Active: 'Active',
  /** Token has expired and needs refresh */
  TokenExpired: 'TokenExpired',
  /** User revoked access */
  Revoked: 'Revoked',
  /** Provider returned an error */
  Error: 'Error',
} as const;
export type CalendarConnectionStatus = (typeof CalendarConnectionStatus)[keyof typeof CalendarConnectionStatus];
export declare namespace CalendarConnectionStatus {
  type Active = 'Active';
  type TokenExpired = 'TokenExpired';
  type Revoked = 'Revoked';
  type Error = 'Error';
}

/**
 * HooverCanvassingApi.Features.Calendar.CalendarProvider
 *
 * Supported calendar providers
 */
export const CalendarProvider = {
  Google: 'Google',
  Microsoft: 'Microsoft',
} as const;
export type CalendarProvider = (typeof CalendarProvider)[keyof typeof CalendarProvider];
export declare namespace CalendarProvider {
  type Google = 'Google';
  type Microsoft = 'Microsoft';
}

/**
 * HooverCanvassingApi.Features.Calendar.CalendarSyncState
 *
 * Real-time sync state for UI feedback
 */
export const CalendarSyncState = {
  Idle: 'Idle',
  Syncing: 'Syncing',
  Failed: 'Failed',
} as const;
export type CalendarSyncState = (typeof CalendarSyncState)[keyof typeof CalendarSyncState];
export declare namespace CalendarSyncState {
  type Idle = 'Idle';
  type Syncing = 'Syncing';
  type Failed = 'Failed';
}

/**
 * HooverCanvassingApi.Features.Tasks.CalendarSyncStatus
 *
 * Sync status for a task-calendar link.
 */
export const CalendarSyncStatus = {
  /** Initial state or waiting for first sync attempt. */
  Pending: 'Pending',
  /** Successfully synced to calendar. */
  Synced: 'Synced',
  /** Sync failed, will be retried. */
  Failed: 'Failed',
} as const;
export type CalendarSyncStatus = (typeof CalendarSyncStatus)[keyof typeof CalendarSyncStatus];
export declare namespace CalendarSyncStatus {
  type Pending = 'Pending';
  type Synced = 'Synced';
  type Failed = 'Failed';
}

/**
 * HooverCanvassingApi.Features.Fundraising.CallTime.CallOutcome
 *
 * What came of one `CallAttempt` — a closed set of seven, named for what the donor did. Closed on purpose: a campaign-defined outcome would reach sheets and analytics carrying no meaning any of them could act on.
 */
export const CallOutcome = {
  NoAnswer: 'NoAnswer',
  Voicemail: 'Voicemail',
  CallBackRequested: 'CallBackRequested',
  Committed: 'Committed',
  Declined: 'Declined',
  WrongNumber: 'WrongNumber',
  RemovalRequested: 'RemovalRequested',
} as const;
export type CallOutcome = (typeof CallOutcome)[keyof typeof CallOutcome];
export declare namespace CallOutcome {
  type NoAnswer = 'NoAnswer';
  type Voicemail = 'Voicemail';
  type CallBackRequested = 'CallBackRequested';
  type Committed = 'Committed';
  type Declined = 'Declined';
  type WrongNumber = 'WrongNumber';
  type RemovalRequested = 'RemovalRequested';
}

/**
 * HooverCanvassingApi.Features.Fundraising.CallSheets.CallSheetSort
 *
 * A column a call sheet can be ordered by — the columns of the web's call book. Web only: the phone never reads it and works the book in the order it is served.
 */
export const CallSheetSort = {
  /** The Ask frozen when they were added, held to the ceiling; no Ask goes last either way. */
  Ask: 'Ask',
  /** The person's name, ignoring case. */
  Name: 'Name',
  /** The phone's digits; no phone goes last either way. */
  Phone: 'Phone',
  /** When they were added to the sheet — the book's "#" column. */
  Added: 'Added',
  /** Their last call from this sheet; not called from it counts as the longest ago. */
  FromThisBook: 'FromThisBook',
  /** How many other call sheets they're on. */
  AlsoOn: 'AlsoOn',
} as const;
export type CallSheetSort = (typeof CallSheetSort)[keyof typeof CallSheetSort];
export declare namespace CallSheetSort {
  type Ask = 'Ask';
  type Name = 'Name';
  type Phone = 'Phone';
  type Added = 'Added';
  type FromThisBook = 'FromThisBook';
  type AlsoOn = 'AlsoOn';
}

/**
 * HooverCanvassingApi.Common.Authorization.CampaignAccessResult
 *
 * Result of campaign access validation.
 */
export const CampaignAccessResult = {
  Authorized: 'Authorized',
  Unauthorized: 'Unauthorized',
  CampaignNotFound: 'CampaignNotFound',
} as const;
export type CampaignAccessResult = (typeof CampaignAccessResult)[keyof typeof CampaignAccessResult];
export declare namespace CampaignAccessResult {
  type Authorized = 'Authorized';
  type Unauthorized = 'Unauthorized';
  type CampaignNotFound = 'CampaignNotFound';
}

/**
 * HooverCanvassingApi.Common.Models.CampaignAssets
 *
 * The "what do you already have?" onboarding checklist, stored as a bit set so a single nullable column captures any combination. Folded from the selected asset keys at the API boundary via `CampaignAssetsExtensions.ToCampaignAssets`.
 */
export const CampaignAssets = {
  None: 'None',
  VoterFile: 'VoterFile',
  VolunteerList: 'VolunteerList',
  TurfPlan: 'TurfPlan',
  Script: 'Script',
  Strategy: 'Strategy',
  ComplianceData: 'ComplianceData',
} as const;
export type CampaignAssets = (typeof CampaignAssets)[keyof typeof CampaignAssets];
export declare namespace CampaignAssets {
  type None = 'None';
  type VoterFile = 'VoterFile';
  type VolunteerList = 'VolunteerList';
  type TurfPlan = 'TurfPlan';
  type Script = 'Script';
  type Strategy = 'Strategy';
  type ComplianceData = 'ComplianceData';
}

/** HooverCanvassingApi.Features.Calendar.CampaignCalendarVisibility */
export const CampaignCalendarVisibility = {
  Show: 'Show',
  ShowBusy: 'ShowBusy',
  Hide: 'Hide',
} as const;
export type CampaignCalendarVisibility = (typeof CampaignCalendarVisibility)[keyof typeof CampaignCalendarVisibility];
export declare namespace CampaignCalendarVisibility {
  type Show = 'Show';
  type ShowBusy = 'ShowBusy';
  type Hide = 'Hide';
}

/** HooverCanvassingApi.Common.Models.CampaignCreatorType */
export const CampaignCreatorType = {
  Candidate: 'Candidate',
  CampaignStaff: 'CampaignStaff',
} as const;
export type CampaignCreatorType = (typeof CampaignCreatorType)[keyof typeof CampaignCreatorType];
export declare namespace CampaignCreatorType {
  type Candidate = 'Candidate';
  type CampaignStaff = 'CampaignStaff';
}

/** HooverCanvassingApi.Features.Contacts.CampaignQuestionType */
export const CampaignQuestionType = {
  YesNoUndecided: 'YesNoUndecided',
  MultipleChoice: 'MultipleChoice',
  FreeText: 'FreeText',
} as const;
export type CampaignQuestionType = (typeof CampaignQuestionType)[keyof typeof CampaignQuestionType];
export declare namespace CampaignQuestionType {
  type YesNoUndecided = 'YesNoUndecided';
  type MultipleChoice = 'MultipleChoice';
  type FreeText = 'FreeText';
}

/**
 * HooverCanvassingApi.Common.Models.CanvasserType
 *
 * Whether a campaign user is a volunteer or paid canvasser. Campaign-scoped via CampaignUser — the same person can be volunteer on one campaign and paid on another.
 */
export const CanvasserType = {
  Volunteer: 'Volunteer',
  Paid: 'Paid',
} as const;
export type CanvasserType = (typeof CanvasserType)[keyof typeof CanvasserType];
export declare namespace CanvasserType {
  type Volunteer = 'Volunteer';
  type Paid = 'Paid';
}

/**
 * HooverCanvassingApi.Common.Models.CapabilityArea
 *
 * Areas of campaign functionality that can be permissioned. Stored as string via EF Core conversion for extensibility.
 */
export const CapabilityArea = {
  FieldOps: 'FieldOps',
  Analytics: 'Analytics',
  TeamManagement: 'TeamManagement',
  Finance: 'Finance',
  /** Call Time / Fundraising (ADR-083) — a bounded context of its own, kept off the Finance capability so granting call time never hands over bank transactions and filings. Owners and the FullAdmin preset get Manage automatically (synthesized from this enum); other presets grant it explicitly. First gated surface: the Enrichment results preview. */
  Fundraising: 'Fundraising',
} as const;
export type CapabilityArea = (typeof CapabilityArea)[keyof typeof CapabilityArea];
export declare namespace CapabilityArea {
  type FieldOps = 'FieldOps';
  type Analytics = 'Analytics';
  type TeamManagement = 'TeamManagement';
  type Finance = 'Finance';
  type Fundraising = 'Fundraising';
}

/**
 * HooverCanvassingApi.Common.Models.CapabilityTier
 *
 * Permission tiers within a capability area. Higher tier includes all lower permissions. Stored as int via EF Core.
 */
export const CapabilityTier = {
  None: 0,
  View: 1,
  Do: 2,
  Manage: 3,
} as const;
export type CapabilityTier = (typeof CapabilityTier)[keyof typeof CapabilityTier];
export declare namespace CapabilityTier {
  type None = 0;
  type View = 1;
  type Do = 2;
  type Manage = 3;
}

/**
 * HooverCanvassingApi.Features.Finance.CapturedReceiptLifecycle
 *
 * CAP-001 — lifecycle of a `CapturedReceipt` staging record. `CapturedReceiptLifecycle.Unattached`: sitting in the receipts pocket. `CapturedReceiptLifecycle.Attached`: materialized into an `ExpenditureAttachment` when the treasurer classified the matching bank debit (CAP-006). `CapturedReceiptLifecycle.Discarded`: abandoned. Serializes PascalCase on the wire.
 */
export const CapturedReceiptLifecycle = {
  Unattached: 'Unattached',
  Attached: 'Attached',
  Discarded: 'Discarded',
} as const;
export type CapturedReceiptLifecycle = (typeof CapturedReceiptLifecycle)[keyof typeof CapturedReceiptLifecycle];
export declare namespace CapturedReceiptLifecycle {
  type Unattached = 'Unattached';
  type Attached = 'Attached';
  type Discarded = 'Discarded';
}

/**
 * HooverCanvassingApi.Features.Assistant.Domains.Finance.ChargeAct
 *
 * What someone else did that the card should name.
 */
export const ChargeAct = {
  /** A teammate's explanation claims it and waits for approval. The name is theirs. */
  Answered: 'Answered',
  /** It is in the ledger. The name is whoever approved the explanation, or whoever classified it in the ledger, when known. */
  Recorded: 'Recorded',
  /** My last explanation was turned down; the card is open again. The name is the finance manager who rejected it. */
  NotApproved: 'NotApproved',
  /** I may no longer answer it (`ChargeQuestions.MayAnswer`); the finance managers have it. No name. */
  WithFinanceTeam: 'WithFinanceTeam',
} as const;
export type ChargeAct = (typeof ChargeAct)[keyof typeof ChargeAct];
export declare namespace ChargeAct {
  type Answered = 'Answered';
  type Recorded = 'Recorded';
  type NotApproved = 'NotApproved';
  type WithFinanceTeam = 'WithFinanceTeam';
}

/**
 * HooverCanvassingApi.Features.Assistant.Domains.Finance.ChargeActor
 *
 * Who is acting: a purchaser's tap, a finance manager's tap on their own login, or an assistant run, known from the token.
 */
export const ChargeActor = {
  Purchaser: 'Purchaser',
  FinanceManager: 'FinanceManager',
  Run: 'Run',
} as const;
export type ChargeActor = (typeof ChargeActor)[keyof typeof ChargeActor];
export declare namespace ChargeActor {
  type Purchaser = 'Purchaser';
  type FinanceManager = 'FinanceManager';
  type Run = 'Run';
}

/**
 * HooverCanvassingApi.Features.Assistant.Domains.Finance.ChargeGuessSource
 *
 * Where a guessed value came from: the campaign's past buys from the payee, the bank's data on the line, the model's guess from that data (a category only), or nothing known.
 */
export const ChargeGuessSource = {
  History: 'History',
  Bank: 'Bank',
  Fallback: 'Fallback',
  Model: 'Model',
} as const;
export type ChargeGuessSource = (typeof ChargeGuessSource)[keyof typeof ChargeGuessSource];
export declare namespace ChargeGuessSource {
  type History = 'History';
  type Bank = 'Bank';
  type Fallback = 'Fallback';
  type Model = 'Model';
}

/**
 * HooverCanvassingApi.Features.Assistant.Domains.Finance.ChargeNextAction
 *
 * What a refused charge write can do next. Each names a tool or step the caller has.
 */
export const ChargeNextAction = {
  /** Answer again with corrected details, which amends the caller's own pending answer. */
  Answer: 'Answer',
  /** The charge changed; read it again before acting. */
  Reload: 'Reload',
  /** A run's write may not be what the member meant: ask them to confirm the details, then try again. */
  Confirm: 'Confirm',
  None: 'None',
} as const;
export type ChargeNextAction = (typeof ChargeNextAction)[keyof typeof ChargeNextAction];
export declare namespace ChargeNextAction {
  type Answer = 'Answer';
  type Reload = 'Reload';
  type Confirm = 'Confirm';
  type None = 'None';
}

/** HooverCanvassingApi.Features.Assistant.Domains.Finance.ChargeOp */
export const ChargeOp = {
  Answer: 'Answer',
  WithdrawAnswer: 'WithdrawAnswer',
  NotMine: 'NotMine',
  TakeBack: 'TakeBack',
} as const;
export type ChargeOp = (typeof ChargeOp)[keyof typeof ChargeOp];
export declare namespace ChargeOp {
  type Answer = 'Answer';
  type WithdrawAnswer = 'WithdrawAnswer';
  type NotMine = 'NotMine';
  type TakeBack = 'TakeBack';
}

/**
 * HooverCanvassingApi.Features.Assistant.Domains.Finance.ChargeQuestionState
 *
 * What a recipient's card shows. Derived on read, never stored: nothing has to close the other recipients.
 */
export const ChargeQuestionState = {
  Open: 'Open',
  /** My explanation is waiting for a finance manager. */
  AwaitingApproval: 'AwaitingApproval',
  /** Recorded in the ledger from my answer. */
  Recorded: 'Recorded',
  /** A teammate's live explanation claims it, or the ledger recorded it without me. */
  AlreadyHandled: 'AlreadyHandled',
  NotMine: 'NotMine',
} as const;
export type ChargeQuestionState = (typeof ChargeQuestionState)[keyof typeof ChargeQuestionState];
export declare namespace ChargeQuestionState {
  type Open = 'Open';
  type AwaitingApproval = 'AwaitingApproval';
  type Recorded = 'Recorded';
  type AlreadyHandled = 'AlreadyHandled';
  type NotMine = 'NotMine';
}

/**
 * HooverCanvassingApi.Features.Assistant.Domains.Finance.ChargeRoute
 *
 * Why this member was asked: they make purchases, or no purchaser could answer and they manage the ledger.
 */
export const ChargeRoute = {
  Purchaser: 'Purchaser',
  FinanceFallback: 'FinanceFallback',
} as const;
export type ChargeRoute = (typeof ChargeRoute)[keyof typeof ChargeRoute];
export declare namespace ChargeRoute {
  type Purchaser = 'Purchaser';
  type FinanceFallback = 'FinanceFallback';
}

/**
 * HooverCanvassingApi.Features.Assistant.Domains.Finance.ChargeStanding
 *
 * Where a line stands for the caller, as the transition rules read it.
 */
export const ChargeStanding = {
  Open: 'Open',
  MinePending: 'MinePending',
  NotMine: 'NotMine',
  Recorded: 'Recorded',
  ClaimedByTeammate: 'ClaimedByTeammate',
} as const;
export type ChargeStanding = (typeof ChargeStanding)[keyof typeof ChargeStanding];
export declare namespace ChargeStanding {
  type Open = 'Open';
  type MinePending = 'MinePending';
  type NotMine = 'NotMine';
  type Recorded = 'Recorded';
  type ClaimedByTeammate = 'ClaimedByTeammate';
}

/**
 * HooverCanvassingApi.Features.Finance.ClassificationHintCaution
 *
 * Why a classification hint needs a closer look. A cautioned hint can be reviewed but never accepted in one click, and leaves the purpose to the treasurer.
 */
export const ClassificationHintCaution = {
  /** The payee's past amounts are tightly clustered and this line is far outside them. */
  UnusualAmount: 'UnusualAmount',
  /** The payee's past classifications span more than one category or purpose. */
  VariedHistory: 'VariedHistory',
  /** The payee is an individual, or the line is a person-to-person transfer. */
  PaymentToPerson: 'PaymentToPerson',
  /** The payee's most recent purpose is blank or a placeholder. */
  NoRealPurpose: 'NoRealPurpose',
} as const;
export type ClassificationHintCaution = (typeof ClassificationHintCaution)[keyof typeof ClassificationHintCaution];
export declare namespace ClassificationHintCaution {
  type UnusualAmount = 'UnusualAmount';
  type VariedHistory = 'VariedHistory';
  type PaymentToPerson = 'PaymentToPerson';
  type NoRealPurpose = 'NoRealPurpose';
}

/**
 * HooverCanvassingApi.Features.Finance.ClassificationHintMatchKind
 *
 * How a classification hint identified the bank line's payee.
 */
export const ClassificationHintMatchKind = {
  /** The line's description, as-is or with bank noise stripped, matches a bank line the campaign already classified to an Expenditure. */
  Direct: 'Direct',
  /** Direct matching couldn't identify the line; the model's stored answer named the payee with confidence at or above the hint threshold. The reason reads "Looks like …". */
  Model: 'Model',
} as const;
export type ClassificationHintMatchKind = (typeof ClassificationHintMatchKind)[keyof typeof ClassificationHintMatchKind];
export declare namespace ClassificationHintMatchKind {
  type Direct = 'Direct';
  type Model = 'Model';
}

/** HooverCanvassingApi.Features.Finance.ClassificationHintOutcomeKind */
export const ClassificationHintOutcomeKind = {
  /** Classified with the hint's values unchanged. */
  AcceptedAsIs: 'AcceptedAsIs',
  /** Classified to the hint's payee with some other value changed. */
  AcceptedEdited: 'AcceptedEdited',
  /** Classified to a different payee than the hint suggested. */
  Overridden: 'Overridden',
  /** The hinted line was excluded from report. */
  Excluded: 'Excluded',
  /** An Expenditure classified from the hint was deleted (Undo, or Delete). */
  Undone: 'Undone',
} as const;
export type ClassificationHintOutcomeKind = (typeof ClassificationHintOutcomeKind)[keyof typeof ClassificationHintOutcomeKind];
export declare namespace ClassificationHintOutcomeKind {
  type AcceptedAsIs = 'AcceptedAsIs';
  type AcceptedEdited = 'AcceptedEdited';
  type Overridden = 'Overridden';
  type Excluded = 'Excluded';
  type Undone = 'Undone';
}

/**
 * HooverCanvassingApi.Features.Fundraising.CallTime.CommitmentClosedReason
 *
 * A closed set. `CommitmentClosedReason.ReceivedElsewhere`, `CommitmentClosedReason.Withdrawn` and `CommitmentClosedReason.Lapsed` are a Fundraiser's to set; `CommitmentClosedReason.Superseded` and `CommitmentClosedReason.LoggedInError` only the system sets. A close is final.
 */
export const CommitmentClosedReason = {
  /** The money arrived somewhere this ledger can't see (ActBlue, WinRed). */
  ReceivedElsewhere: 'ReceivedElsewhere',
  Withdrawn: 'Withdrawn',
  Lapsed: 'Lapsed',
  /** A later Commitment from the same donor replaced this one. */
  Superseded: 'Superseded',
  /** The Caller changed the call's outcome away from Committed — a mis-tap, not a Commitment. */
  LoggedInError: 'LoggedInError',
} as const;
export type CommitmentClosedReason = (typeof CommitmentClosedReason)[keyof typeof CommitmentClosedReason];
export declare namespace CommitmentClosedReason {
  type ReceivedElsewhere = 'ReceivedElsewhere';
  type Withdrawn = 'Withdrawn';
  type Lapsed = 'Lapsed';
  type Superseded = 'Superseded';
  type LoggedInError = 'LoggedInError';
}

/** HooverCanvassingApi.Features.Fundraising.CallTime.CommitmentState */
export const CommitmentState = {
  Open: 'Open',
  Closed: 'Closed',
} as const;
export type CommitmentState = (typeof CommitmentState)[keyof typeof CommitmentState];
export declare namespace CommitmentState {
  type Open = 'Open';
  type Closed = 'Closed';
}

/**
 * HooverCanvassingApi.Features.Messaging.CommitteeFilingJurisdiction
 *
 * Where the committee files.
 */
export const CommitteeFilingJurisdiction = {
  Federal: 'Federal',
  State: 'State',
  NotSure: 'NotSure',
} as const;
export type CommitteeFilingJurisdiction = (typeof CommitteeFilingJurisdiction)[keyof typeof CommitteeFilingJurisdiction];
export declare namespace CommitteeFilingJurisdiction {
  type Federal = 'Federal';
  type State = 'State';
  type NotSure = 'NotSure';
}

/**
 * HooverCanvassingApi.Features.Messaging.CommitteeOrganizationType
 *
 * What kind of organisation the committee is, as the Vendor's registration form asks.
 */
export const CommitteeOrganizationType = {
  CandidateCommittee: 'CandidateCommittee',
  PoliticalActionCommittee: 'PoliticalActionCommittee',
  PartyCommittee: 'PartyCommittee',
  NonProfit501c4: 'NonProfit501c4',
  BusinessOrSoleProprietor: 'BusinessOrSoleProprietor',
  Other: 'Other',
} as const;
export type CommitteeOrganizationType = (typeof CommitteeOrganizationType)[keyof typeof CommitteeOrganizationType];
export declare namespace CommitteeOrganizationType {
  type CandidateCommittee = 'CandidateCommittee';
  type PoliticalActionCommittee = 'PoliticalActionCommittee';
  type PartyCommittee = 'PartyCommittee';
  type NonProfit501c4 = 'NonProfit501c4';
  type BusinessOrSoleProprietor = 'BusinessOrSoleProprietor';
  type Other = 'Other';
}

/**
 * HooverCanvassingApi.Features.Finance.CommitteeType
 *
 * The committee type ELEC registers (Form D-1 or D-2).
 */
export const CommitteeType = {
  SingleCandidate: 'SingleCandidate',
  JointCandidates: 'JointCandidates',
} as const;
export type CommitteeType = (typeof CommitteeType)[keyof typeof CommitteeType];
export declare namespace CommitteeType {
  type SingleCandidate = 'SingleCandidate';
  type JointCandidates = 'JointCandidates';
}

/**
 * HooverCanvassingApi.Features.Finance.CompiledDirection
 *
 * ADR-015 D4 — direction of a compiled (off-platform) import, declared at the FILE level (Donations = money-in, Expenses = money-out), never guessed per row. `CompiledImportService` maps the batch's non-processor `Source` to this; the generic content scanner and the detector routing (later slices) consume it too — hence co-located here. No 0-sentinel "unknown": callers map an explicit `ImportSources.Compiled*` source and THROW on anything unmapped (an undeclared direction must fail loud, never silently default to Donations).
 */
export const CompiledDirection = {
  Donations: 'Donations',
  Expenses: 'Expenses',
} as const;
export type CompiledDirection = (typeof CompiledDirection)[keyof typeof CompiledDirection];
export declare namespace CompiledDirection {
  type Donations = 'Donations';
  type Expenses = 'Expenses';
}

/** HooverCanvassingApi.Features.Voice.ConsentType */
export const ConsentType = {
  OneParty: 'OneParty',
  TwoPartyGranted: 'TwoPartyGranted',
} as const;
export type ConsentType = (typeof ConsentType)[keyof typeof ConsentType];
export declare namespace ConsentType {
  type OneParty = 'OneParty';
  type TwoPartyGranted = 'TwoPartyGranted';
}

/**
 * HooverCanvassingApi.Features.Finance.ContactKind
 *
 * Orthogonal to ContactType (Donor/Payee). Drives name-parts vs. organization-name composition and feeds state-spec source codes (e.g., AL EDI cbContributionSource).
 */
export const ContactKind = {
  Individual: 'Individual',
  Organization: 'Organization',
} as const;
export type ContactKind = (typeof ContactKind)[keyof typeof ContactKind];
export declare namespace ContactKind {
  type Individual = 'Individual';
  type Organization = 'Organization';
}

/** HooverCanvassingApi.Features.ContactLeads.ContactLeadStatus */
export const ContactLeadStatus = {
  Pending: 'Pending',
  Merged: 'Merged',
  Dismissed: 'Dismissed',
} as const;
export type ContactLeadStatus = (typeof ContactLeadStatus)[keyof typeof ContactLeadStatus];
export declare namespace ContactLeadStatus {
  type Pending = 'Pending';
  type Merged = 'Merged';
  type Dismissed = 'Dismissed';
}

/**
 * HooverCanvassingApi.Features.Finance.ContactMergeOutcome
 *
 * The outcome of a merge attempt. The client discriminates on this (mirroring ResolutionService's gate-blocked-in-200 shape).
 */
export const ContactMergeOutcome = {
  /** Done: the client toasts and refreshes. */
  Merged: 'Merged',
  /** A filed federal report covers a touched date: the client shows the heads-up and resubmits with `ConfirmAmendment` set. */
  NeedsAmendmentConfirmation: 'NeedsAmendmentConfirmation',
  /** Both contacts are designated: the client shows `BlockReason` and the treasurer resolves it manually. */
  Blocked: 'Blocked',
} as const;
export type ContactMergeOutcome = (typeof ContactMergeOutcome)[keyof typeof ContactMergeOutcome];
export declare namespace ContactMergeOutcome {
  type Merged = 'Merged';
  type NeedsAmendmentConfirmation = 'NeedsAmendmentConfirmation';
  type Blocked = 'Blocked';
}

/** HooverCanvassingApi.Features.Voters.ContactStatus */
export const ContactStatus = {
  Reached: 'Reached',
  NotHome: 'NotHome',
  Refused: 'Refused',
  NeedsFollowUp: 'NeedsFollowUp',
} as const;
export type ContactStatus = (typeof ContactStatus)[keyof typeof ContactStatus];
export declare namespace ContactStatus {
  type Reached = 'Reached';
  type NotHome = 'NotHome';
  type Refused = 'Refused';
  type NeedsFollowUp = 'NeedsFollowUp';
}

/** HooverCanvassingApi.Features.Finance.ContactType */
export const ContactType = {
  Donor: 'Donor',
  Payee: 'Payee',
} as const;
export type ContactType = (typeof ContactType)[keyof typeof ContactType];
export declare namespace ContactType {
  type Donor = 'Donor';
  type Payee = 'Payee';
}

/**
 * HooverCanvassingApi.Features.Finance.ContributionAttachmentKind
 *
 * CAP-001 — kind of a `ContributionAttachment`. v1 only stores `ContributionAttachmentKind.CheckImage`; the enum is cheap future-proofing for receipt images, thank-you letters, or signed forms attached to a contribution. Serializes PascalCase on the wire (`CheckImage`).
 */
export const ContributionAttachmentKind = {
  CheckImage: 'CheckImage',
} as const;
export type ContributionAttachmentKind = (typeof ContributionAttachmentKind)[keyof typeof ContributionAttachmentKind];
export declare namespace ContributionAttachmentKind {
  type CheckImage = 'CheckImage';
}

/**
 * HooverCanvassingApi.Features.Finance.ContributionOrigin
 *
 * How a `Contribution` row entered the ledger (#249). `ContributionOrigin.Unknown` is the default so pre-existing rows (written before this column shipped) read back as Unknown rather than being silently reclassified. StripeConnect marks a row ingested from a Stripe Connect donation-page charge — real money captured online, so the refund dialog allows only a full refund — and is the only origin that carries a frozen `Contribution.AttestationSnapshot`. Stored as string.
 */
export const ContributionOrigin = {
  Unknown: 'Unknown',
  Manual: 'Manual',
  Import: 'Import',
  Check: 'Check',
  StripeConnect: 'StripeConnect',
} as const;
export type ContributionOrigin = (typeof ContributionOrigin)[keyof typeof ContributionOrigin];
export declare namespace ContributionOrigin {
  type Unknown = 'Unknown';
  type Manual = 'Manual';
  type Import = 'Import';
  type Check = 'Check';
  type StripeConnect = 'StripeConnect';
}

/**
 * HooverCanvassingApi.Features.Finance.ContributionRefundKind
 *
 * FPD-037 — structured classifier on the `ContributionRefund` row, denormalized from the typed-prefix `ContributionRefundCreated.Source` audit string so in-product refund reporting can filter by kind without parsing audit-log payload JSON. The `Source` string is the source of truth at write time; the enum is derived from it via `ContributionRefundKindClassifier`.
 *
 * Adding a value = new builder method on `ContributionRefundSources` + a parser branch on `ContributionRefundKindClassifier` + a CLAUDE.md note. Values follow the prefix verb directly so the names stay legible at audit-extraction time (`ManualVoidAfterLongDelay` matches `manual:void-after-long-delay:`).
 */
export const ContributionRefundKind = {
  /** `chargeback:*` — donor-disputed reversal surfaced by a processor file or Stripe dispute row. */
  Chargeback: 'Chargeback',
  /** `refund:*` — processor-originated refund line (WinRed/Stripe/Anedot status flip or refund row). */
  Refund: 'Refund',
  /** `manual:donor-refund:*` — campaign cuts a refund check to a donor (manual entry). */
  ManualDonorRefund: 'ManualDonorRefund',
  /** `manual:orphan-resolution:numero:*` — treasurer manually attributes a Numero summary-rollup refund. */
  ManualOrphanResolution: 'ManualOrphanResolution',
  /** `manual:void-after-long-delay:*` — FPD-021 aged-overdue write-off when the processor payout will never settle. */
  ManualVoidAfterLongDelay: 'ManualVoidAfterLongDelay',
  /** `manual:treasurer-entry:*` — Mode B gap-classification refund the treasurer hand-entered against an existing contribution. */
  ManualTreasurerEntry: 'ManualTreasurerEntry',
} as const;
export type ContributionRefundKind = (typeof ContributionRefundKind)[keyof typeof ContributionRefundKind];
export declare namespace ContributionRefundKind {
  type Chargeback = 'Chargeback';
  type Refund = 'Refund';
  type ManualDonorRefund = 'ManualDonorRefund';
  type ManualOrphanResolution = 'ManualOrphanResolution';
  type ManualVoidAfterLongDelay = 'ManualVoidAfterLongDelay';
  type ManualTreasurerEntry = 'ManualTreasurerEntry';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.ContributorShapeImplication
 *
 * How a `ReceiptNature` implies a contributor's person-vs-organization shape, used only for the agreement check (NOT for the verdict, which is always `ContactKind`). Post-ADR-057 (#344) the person/org gift members were collapsed into `Cash`, so only two outcomes remain live:
 *
 * - `Organization` — the type asserts an organization contributor (`FederalTransfer`, a transfer from another authorized committee).
 * - `Neutral` — every other surviving nature (`Cash`, `InKind`, `Other`, `OffsetToOperating`, `OtherReceipt`) carries no person-vs-org implication; it defers to `ContactKind` and can never disagree with it.
 */
export const ContributorShapeImplication = {
  Individual: 'Individual',
  Organization: 'Organization',
  Neutral: 'Neutral',
} as const;
export type ContributorShapeImplication = (typeof ContributorShapeImplication)[keyof typeof ContributorShapeImplication];
export declare namespace ContributorShapeImplication {
  type Individual = 'Individual';
  type Organization = 'Organization';
  type Neutral = 'Neutral';
}

/** HooverCanvassingApi.Features.Finance.CoverPeriodRuleType */
export const CoverPeriodRuleType = {
  CalendarQuarter: 'CalendarQuarter',
  CalendarYear: 'CalendarYear',
  FiscalYear: 'FiscalYear',
  Custom: 'Custom',
  CommitteeFormationToDueDate: 'CommitteeFormationToDueDate',
  ElectionAnchored: 'ElectionAnchored',
  FixedCalendarPeriod: 'FixedCalendarPeriod',
  ElectionAnchoredWindow: 'ElectionAnchoredWindow',
} as const;
export type CoverPeriodRuleType = (typeof CoverPeriodRuleType)[keyof typeof CoverPeriodRuleType];
export declare namespace CoverPeriodRuleType {
  type CalendarQuarter = 'CalendarQuarter';
  type CalendarYear = 'CalendarYear';
  type FiscalYear = 'FiscalYear';
  type Custom = 'Custom';
  type CommitteeFormationToDueDate = 'CommitteeFormationToDueDate';
  type ElectionAnchored = 'ElectionAnchored';
  type FixedCalendarPeriod = 'FixedCalendarPeriod';
  type ElectionAnchoredWindow = 'ElectionAnchoredWindow';
}

/**
 * HooverCanvassingApi.Features.Finance.Filing.Preflight.CoverageTier
 *
 * The protection tier an [[Emitted entity]] field carries (ADR-042 · ADR-043). Ordered strongest-first. See `Features/Finance/CONTEXT.md` › "Preflight coverage parity".
 */
export const CoverageTier = {
  /** A pre-file hard-stop: a `PreflightHardFailure` at GET and a `SubmissionPackageException` at File. Blanking the field MUST hard-block. */
  Enforce: 'Enforce',
  /** A soft advisory that never blocks filing. Blanking the field must NOT hard-block (a warning may surface). E.g. VA itemized-individual employer/occupation — statutorily required but XSD-optional, enforced by ELECT's >20%-missing completeness rule. */
  Warning: 'Warning',
  /** A hard-fail thrown inside the builder itself (not the preflight). Blanking the field MUST make `BuildAsync` throw. E.g. the federal nameless-loan-lender guard. */
  DefenseInDepth: 'DefenseInDepth',
  /** Emitted but consciously left unenforced — a documented, deliberate posture, NOT a #155 drift gap. Blanking the field must neither hard-block nor make the builder throw (the hollow value is emitted). E.g. federal Schedule D debt creditor / C-2 guarantor / SB17 in-kind offset (ADR-030 · #84). */
  DeliberateGap: 'DeliberateGap',
} as const;
export type CoverageTier = (typeof CoverageTier)[keyof typeof CoverageTier];
export declare namespace CoverageTier {
  type Enforce = 'Enforce';
  type Warning = 'Warning';
  type DefenseInDepth = 'DefenseInDepth';
  type DeliberateGap = 'DeliberateGap';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.NorthCarolina.Cro1000.Cro1000ReportTypeBox
 *
 * One "Type of Report" checkbox on the CRO-1000 cover. The form groups options into a Municipal column and a State/County column (plus a Referendum column a candidate committee never files); the filer checks exactly one. Members are named <column><option> so the cover builder can look up the box's page coordinate.
 */
export const Cro1000ReportTypeBox = {
  StateCountyOrganizational: 'StateCountyOrganizational',
  StateCountyFirst: 'StateCountyFirst',
  StateCountySecond: 'StateCountySecond',
  StateCountyThird: 'StateCountyThird',
  StateCountyFourth: 'StateCountyFourth',
  StateCountyMidYear: 'StateCountyMidYear',
  StateCountyYearEnd: 'StateCountyYearEnd',
  StateCountyFinal: 'StateCountyFinal',
  StateCountySpecial: 'StateCountySpecial',
  MunicipalOrganizational: 'MunicipalOrganizational',
  MunicipalThirtyFiveDay: 'MunicipalThirtyFiveDay',
  MunicipalPrePrimary: 'MunicipalPrePrimary',
  MunicipalPreElection: 'MunicipalPreElection',
  MunicipalPreRunoff: 'MunicipalPreRunoff',
  MunicipalMidYear: 'MunicipalMidYear',
  MunicipalYearEnd: 'MunicipalYearEnd',
  MunicipalFinal: 'MunicipalFinal',
  MunicipalSpecial: 'MunicipalSpecial',
} as const;
export type Cro1000ReportTypeBox = (typeof Cro1000ReportTypeBox)[keyof typeof Cro1000ReportTypeBox];
export declare namespace Cro1000ReportTypeBox {
  type StateCountyOrganizational = 'StateCountyOrganizational';
  type StateCountyFirst = 'StateCountyFirst';
  type StateCountySecond = 'StateCountySecond';
  type StateCountyThird = 'StateCountyThird';
  type StateCountyFourth = 'StateCountyFourth';
  type StateCountyMidYear = 'StateCountyMidYear';
  type StateCountyYearEnd = 'StateCountyYearEnd';
  type StateCountyFinal = 'StateCountyFinal';
  type StateCountySpecial = 'StateCountySpecial';
  type MunicipalOrganizational = 'MunicipalOrganizational';
  type MunicipalThirtyFiveDay = 'MunicipalThirtyFiveDay';
  type MunicipalPrePrimary = 'MunicipalPrePrimary';
  type MunicipalPreElection = 'MunicipalPreElection';
  type MunicipalPreRunoff = 'MunicipalPreRunoff';
  type MunicipalMidYear = 'MunicipalMidYear';
  type MunicipalYearEnd = 'MunicipalYearEnd';
  type MunicipalFinal = 'MunicipalFinal';
  type MunicipalSpecial = 'MunicipalSpecial';
}

/**
 * HooverCanvassingApi.Features.RacePicture.Catalog.CycleKind
 *
 * What sits on the ballot around this election, which sets how far the draw dial reaches.
 */
export const CycleKind = {
  Midterm: 'Midterm',
  Presidential: 'Presidential',
  /** Odd-year, spring and other off-cycle dates. The draw dial uses the midterm marks. */
  OffCycle: 'OffCycle',
} as const;
export type CycleKind = (typeof CycleKind)[keyof typeof CycleKind];
export declare namespace CycleKind {
  type Midterm = 'Midterm';
  type Presidential = 'Presidential';
  type OffCycle = 'OffCycle';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.CycleWindowStrategy
 *
 * ADR-037 — the declared per-jurisdiction cycle-window resolution strategy. The itemization window, Schedule A/B `TotalToDate`, and the Schedule H cash-bridge running totals all aggregate over a jurisdiction-specific window whose START is resolved one of three ways. The strategy is DECLARED per jurisdiction (never an `!isFederal` default): a jurisdiction routed to an election-cycle window with no declared strategy throws (an onboarding/config guard, per the Finance Golden Rule on jurisdiction-variant behavior).
 */
export const CycleWindowStrategy = {
  /** Reset January 1 of the report year (AL, GA, MD). No cycle end, no anchor. */
  CalendarYear: 'CalendarYear',
  /** Derive the cycle from the general-election date (federal; the existing `ElectionCycle.Derive`, unchanged). A null `GeneralDate` is structurally undefined → `MissingGeneralDateException`. */
  DerivedCycle: 'DerivedCycle',
  /** Read a stored per-committee cycle-start anchor (`CampaignFinanceSettings.ElectionCycleStartDate` / the report's frozen `ComplianceReport.ElectionCycleStartDateAtFiling`). VA, NC and LA — any jurisdiction whose cycle is anchored on a human milestone that cannot be derived from a stored election date. The milestone itself differs: VA/NC use January 1 of the year the candidate first sought the office; LA uses the candidacy date itself (see `CycleWindowResolver.RequiresJanuaryFirstCycleStart`). A null anchor is structurally undefined → `MissingElectionCycleStartException`. */
  StoredCycle: 'StoredCycle',
} as const;
export type CycleWindowStrategy = (typeof CycleWindowStrategy)[keyof typeof CycleWindowStrategy];
export declare namespace CycleWindowStrategy {
  type CalendarYear = 'CalendarYear';
  type DerivedCycle = 'DerivedCycle';
  type StoredCycle = 'StoredCycle';
}

/**
 * HooverCanvassingApi.Features.RacePicture.Catalog.DateConfidence
 *
 * How sure the pipeline is of a stage's date.
 */
export const DateConfidence = {
  /** Set by law or an official calendar for that year. */
  Verified: 'Verified',
  /** Worked out from the usual schedule; the state can still move it. */
  Likely: 'Likely',
} as const;
export type DateConfidence = (typeof DateConfidence)[keyof typeof DateConfidence];
export declare namespace DateConfidence {
  type Verified = 'Verified';
  type Likely = 'Likely';
}

/** HooverCanvassingApi.Features.Finance.DeadlineStatus */
export const DeadlineStatus = {
  Upcoming: 'Upcoming',
  Due: 'Due',
  Overdue: 'Overdue',
  Filed: 'Filed',
  Superseded: 'Superseded',
} as const;
export type DeadlineStatus = (typeof DeadlineStatus)[keyof typeof DeadlineStatus];
export declare namespace DeadlineStatus {
  type Upcoming = 'Upcoming';
  type Due = 'Due';
  type Overdue = 'Overdue';
  type Filed = 'Filed';
  type Superseded = 'Superseded';
}

/**
 * HooverCanvassingApi.Features.Finance.DebtSettlementKind
 *
 * How a `Debt` was extinguished for LESS than the amount it was incurred at (52 U.S.C. § 30104(b)(8); 11 CFR § 104.3(d)). Both outcomes leave `Debt.IncurredAmount` untouched — the obligation genuinely existed, and the report still has to say so — and both write the unpaid remainder off on `Debt.SettlementDate`. `null` on the debt means neither happened.
 *
 * This is NOT `Debt.VoidedAtUtc`, which means "entered in error, it never happened", and NOT a debt paid down to a $0 balance, which needs no record at all — it self-suppresses once its payments cover it. Serializes PascalCase; stored as a string.
 */
export const DebtSettlementKind = {
  /** The creditor accepted a reduced sum in full satisfaction (11 CFR § 116.7) — `Debt.SettlementAmount` is what they took. */
  Settled: 'Settled',
  /** The creditor released the committee from the balance without being paid it (11 CFR § 116.8). `Debt.SettlementAmount` is whatever had already been paid, which is $0 for a debt forgiven untouched. */
  Forgiven: 'Forgiven',
} as const;
export type DebtSettlementKind = (typeof DebtSettlementKind)[keyof typeof DebtSettlementKind];
export declare namespace DebtSettlementKind {
  type Settled = 'Settled';
  type Forgiven = 'Forgiven';
}

/**
 * HooverCanvassingApi.Features.Finance.DedupBehavior
 *
 * ADR-021 / ADR-073 — how a jurisdiction's portal DEDUPLICATES what we upload, the `filingProfile.dedupBehavior` axis. Orthogonal to `FilingGranularity`: it names what a re-upload of the same period MEANS to the portal, which is the forcing distinction between Alabama's add-only ledger and Maryland's per-transaction changeset.
 *
 * - `DedupBehavior.Append` (Alabama) — the FCPA portal is append-only (ADR-020); a transaction may be uploaded exactly once, so correctness depends on never re-emitting an uploaded row.
 * - `DedupBehavior.Supersede` (federal, VA, LA) — a re-file replaces the whole prior report; the upload carries the entire period every time.
 * - `DedupBehavior.IdempotentByTransactionId` (Maryland, MDCRIS) — the portal keys on a client-assigned Transaction ID: a re-upload of the same id is a no-op, a changed row is an `Amend`, and a removed row is a `Delete`. This is the value the changeset engine (ADR-073) branches on; Maryland is the first consumer, the forcing function that finally implements the previously-deferred resolver branch.
 * - `DedupBehavior.None` (NC) — the portal does no cross-upload dedup; each file stands alone.
 *
 * Plain enum (a parse target, not JSON-bound — the raw axis stays a `string?` on `FilingProfile` so an unrecognised value throws on-use naming the axis, ADR-021). Parsed narrowly at its decision site (the changeset engine), not in the hot-path `ResolvedFilingProfile` — the Finance golden rule: read a policy only where it branches.
 */
export const DedupBehavior = {
  None: 'None',
  Append: 'Append',
  Supersede: 'Supersede',
  IdempotentByTransactionId: 'IdempotentByTransactionId',
} as const;
export type DedupBehavior = (typeof DedupBehavior)[keyof typeof DedupBehavior];
export declare namespace DedupBehavior {
  type None = 'None';
  type Append = 'Append';
  type Supersede = 'Supersede';
  type IdempotentByTransactionId = 'IdempotentByTransactionId';
}

/** HooverCanvassingApi.Features.Finance.DetectionConfidence */
export const DetectionConfidence = {
  /** No candidate — UI shows the kind picker. */
  None: 'None',
  /** Multiple candidates or weak signal — UI shows the kind picker. */
  Low: 'Low',
  /** Single candidate — UI shows the confirmation banner. */
  High: 'High',
} as const;
export type DetectionConfidence = (typeof DetectionConfidence)[keyof typeof DetectionConfidence];
export declare namespace DetectionConfidence {
  type None = 'None';
  type Low = 'Low';
  type High = 'High';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.DisclosedKind
 *
 * The person-vs-organization verdict for a disclosed contact — the single axis the `DisclosedIdentityResolver` owns. Sourced from `ContactKind`, never from the per-gift `ReceiptNature`.
 */
export const DisclosedKind = {
  Individual: 'Individual',
  Organization: 'Organization',
} as const;
export type DisclosedKind = (typeof DisclosedKind)[keyof typeof DisclosedKind];
export declare namespace DisclosedKind {
  type Individual = 'Individual';
  type Organization = 'Organization';
}

/**
 * HooverCanvassingApi.Features.Finance.Plaid.DisconnectStatus
 *
 * How `IBankConnectionStatusService.DisconnectAsync` resolved.
 */
export const DisconnectStatus = {
  Disconnected: 'Disconnected',
  ConnectionNotFound: 'ConnectionNotFound',
} as const;
export type DisconnectStatus = (typeof DisconnectStatus)[keyof typeof DisconnectStatus];
export declare namespace DisconnectStatus {
  type Disconnected = 'Disconnected';
  type ConnectionNotFound = 'ConnectionNotFound';
}

/**
 * HooverCanvassingApi.Features.Finance.StripeConnect.ChargeCreation.DonationChargeStatus
 *
 * The charge-creation outcome. Each status maps to an HTTP status at the controller. Only `DonationChargeStatus.Created` carries a client secret; only `DonationChargeStatus.Blocked` carries a block result.
 */
export const DonationChargeStatus = {
  Created: 'Created',
  Blocked: 'Blocked',
  NotAvailable: 'NotAvailable',
  NotReady: 'NotReady',
  AttestationMissing: 'AttestationMissing',
  BelowMinimum: 'BelowMinimum',
  /** The gift carried no usable home address. A contribution is itemized against the donor's address, and unlike every other reportable field it cannot be reconstructed afterwards — an address the donor never submitted reaches no log, no processor metadata and no receipt. The donation page gates on it, so this is the server-authoritative backstop for a forged or stale request (mirrors `DonationChargeStatus.RecurringNotAvailable`). */
  AddressMissing: 'AddressMissing',
  /** A recurring gift was requested but the campaign can't accept one right now — the per-campaign feature flag is off, or the committee has no still-future election (so no `cancel_at` cutoff is derivable). The donation page hides the toggle in both cases, so this is the server-authoritative backstop for a forged or stale request. Never returned for a one-time gift. */
  RecurringNotAvailable: 'RecurringNotAvailable',
} as const;
export type DonationChargeStatus = (typeof DonationChargeStatus)[keyof typeof DonationChargeStatus];
export declare namespace DonationChargeStatus {
  type Created = 'Created';
  type Blocked = 'Blocked';
  type NotAvailable = 'NotAvailable';
  type NotReady = 'NotReady';
  type AttestationMissing = 'AttestationMissing';
  type BelowMinimum = 'BelowMinimum';
  type AddressMissing = 'AddressMissing';
  type RecurringNotAvailable = 'RecurringNotAvailable';
}

/**
 * HooverCanvassingApi.Features.Finance.StripeConnect.Dashboard.DonationDisplayStatus
 *
 * Derived display status for a portal donation (§10-D). Purely a function of the gift's non-Voided `ContributionRefund` rows — NEVER `Contribution.ProcessorStatus`. Serializes PascalCase on the wire.
 */
export const DonationDisplayStatus = {
  /** No non-Voided refund or chargeback exists for the gift. */
  Succeeded: 'Succeeded',
  /** A non-Voided `ContributionRefundKind.Refund` exists (and no chargeback). */
  Refunded: 'Refunded',
  /** A non-Voided `ContributionRefundKind.Chargeback` exists (wins over a refund). */
  Disputed: 'Disputed',
} as const;
export type DonationDisplayStatus = (typeof DonationDisplayStatus)[keyof typeof DonationDisplayStatus];
export declare namespace DonationDisplayStatus {
  type Succeeded = 'Succeeded';
  type Refunded = 'Refunded';
  type Disputed = 'Disputed';
}

/** HooverCanvassingApi.Features.Finance.StripeConnect.Ingestion.DonationIngestionOutcome */
export const DonationIngestionOutcome = {
  Ingested: 'Ingested',
  AlreadyIngested: 'AlreadyIngested',
  Quarantined: 'Quarantined',
} as const;
export type DonationIngestionOutcome = (typeof DonationIngestionOutcome)[keyof typeof DonationIngestionOutcome];
export declare namespace DonationIngestionOutcome {
  type Ingested = 'Ingested';
  type AlreadyIngested = 'AlreadyIngested';
  type Quarantined = 'Quarantined';
}

/**
 * HooverCanvassingApi.Features.Finance.StripeConnect.Dashboard.DonationStatusFilter
 *
 * The status tab the feed is narrowed to. `DonationStatusFilter.All` is the unfiltered feed; the other three select exactly the gifts whose derived `DonationDisplayStatus` equals the tab. There is deliberately NO "Failed" tab — a failed charge is never persisted as a contribution.
 */
export const DonationStatusFilter = {
  All: 'All',
  Succeeded: 'Succeeded',
  Refunded: 'Refunded',
  Disputed: 'Disputed',
} as const;
export type DonationStatusFilter = (typeof DonationStatusFilter)[keyof typeof DonationStatusFilter];
export declare namespace DonationStatusFilter {
  type All = 'All';
  type Succeeded = 'Succeeded';
  type Refunded = 'Refunded';
  type Disputed = 'Disputed';
}

/**
 * HooverCanvassingApi.Features.Finance.StripeConnect.Webhooks.DonationWebhookStream
 *
 * Which Stripe event stream a donation webhook arrived on. Destination charges split the platform money stream (payment_intent / charge / dispute lifecycle) from the connect lifecycle stream (account.updated / payout.paid); each is verified against its own per-subsidiary signing secret named by the route.
 */
export const DonationWebhookStream = {
  Money: 'Money',
  Connect: 'Connect',
} as const;
export type DonationWebhookStream = (typeof DonationWebhookStream)[keyof typeof DonationWebhookStream];
export declare namespace DonationWebhookStream {
  type Money = 'Money';
  type Connect = 'Connect';
}

/**
 * HooverCanvassingApi.Features.Finance.DonorGiving
 *
 * Narrows a contact list by giving history, applied to the donor population only — a payee has no contributions by construction and is never hidden by it. See `FinanceContact.HasGivenExpr`.
 */
export const DonorGiving = {
  /** Everyone except donors who have never given — the "our donors" population. */
  HasGiven: 'HasGiven',
  /** Only donors who have never given. */
  NotGiven: 'NotGiven',
} as const;
export type DonorGiving = (typeof DonorGiving)[keyof typeof DonorGiving];
export declare namespace DonorGiving {
  type HasGiven = 'HasGiven';
  type NotGiven = 'NotGiven';
}

/**
 * HooverCanvassingApi.Features.Finance.DonorInfoFieldRequirement
 *
 * Which of a donor's employer/occupation identity fields a jurisdiction requires on an itemized gift. Today the source is the binary `RulesetConfig.EmployerOccupationRequired` bool, so only `DonorInfoFieldRequirement.None` and `DonorInfoFieldRequirement.Both` are produced. The "Donor-info verdict" glossary reserves a third `either` case (employer OR occupation) for a future jurisdiction; adding it is a data + enum change, not a reshape of the readers.
 */
export const DonorInfoFieldRequirement = {
  /** Neither employer nor occupation is asked for (AL §17-5-8, LA, FL). */
  None: 'None',
  /** Both employer AND occupation are required (federal 11 CFR 104.7, VA, NC, …). */
  Both: 'Both',
} as const;
export type DonorInfoFieldRequirement = (typeof DonorInfoFieldRequirement)[keyof typeof DonorInfoFieldRequirement];
export declare namespace DonorInfoFieldRequirement {
  type None = 'None';
  type Both = 'Both';
}

/**
 * HooverCanvassingApi.Features.Finance.DonorInfoRequestChannel
 *
 * How a `DonorInfoRequest` went out to the donor — the channel the treasurer used to ask for a missing occupation / employer. Describes the contact method only; it carries no jurisdiction and no legal conclusion.
 *
 * The channel is recorded because the obligations that read this fact turn on it: 11 CFR 104.7(b)(2) treats a written request differently from an oral one (an oral request has to be documented to count). Nothing in the product draws that distinction yet — the mapping belongs with the rule that needs it, not here. Stored as a string, so adding a member is additive and never renumbers a persisted row.
 */
export const DonorInfoRequestChannel = {
  Email: 'Email',
  Mail: 'Mail',
  Phone: 'Phone',
  InPerson: 'InPerson',
} as const;
export type DonorInfoRequestChannel = (typeof DonorInfoRequestChannel)[keyof typeof DonorInfoRequestChannel];
export declare namespace DonorInfoRequestChannel {
  type Email = 'Email';
  type Mail = 'Mail';
  type Phone = 'Phone';
  type InPerson = 'InPerson';
}

/**
 * HooverCanvassingApi.Features.Finance.StripeConnect.SelfManage.DonorPortalSessionOutcome
 *
 * The outcome of a donor-portal link click — the controller maps each to an HTTP result.
 */
export const DonorPortalSessionOutcome = {
  /** A fresh portal session was minted; redirect the donor to `DonorPortalSessionResult.RedirectUrl`. */
  Redirect: 'Redirect',
  /** The token was malformed, forged, empty, or for an unknown subsidiary. */
  InvalidToken: 'InvalidToken',
  /** The caller IP exceeded the per-minute limit; retry after `DonorPortalSessionResult.RetryAfter`. */
  RateLimited: 'RateLimited',
  /** No locked portal configuration is wired for the subsidiary — a server misconfiguration. */
  Misconfigured: 'Misconfigured',
  /** Stripe failed to mint the session — a transient error. */
  Error: 'Error',
} as const;
export type DonorPortalSessionOutcome = (typeof DonorPortalSessionOutcome)[keyof typeof DonorPortalSessionOutcome];
export declare namespace DonorPortalSessionOutcome {
  type Redirect = 'Redirect';
  type InvalidToken = 'InvalidToken';
  type RateLimited = 'RateLimited';
  type Misconfigured = 'Misconfigured';
  type Error = 'Error';
}

/**
 * HooverCanvassingApi.Features.Finance.StripeConnect.SelfManage.DonorPortalTokenFailure
 *
 * Why a presented token was rejected — surfaced so the endpoint can log/return a precise reason.
 */
export const DonorPortalTokenFailure = {
  None: 'None',
  /** Not the expected `v.payload.sig` shape, wrong version, or un-decodable base64url. */
  Malformed: 'Malformed',
  /** The HMAC did not match — a forged or tampered token. */
  BadSignature: 'BadSignature',
  /** Correctly signed but carries an empty subsidiary or customer id (a corrupt claim set). */
  EmptyClaim: 'EmptyClaim',
} as const;
export type DonorPortalTokenFailure = (typeof DonorPortalTokenFailure)[keyof typeof DonorPortalTokenFailure];
export declare namespace DonorPortalTokenFailure {
  type None = 'None';
  type Malformed = 'Malformed';
  type BadSignature = 'BadSignature';
  type EmptyClaim = 'EmptyClaim';
}

/**
 * HooverCanvassingApi.Features.Finance.DonorResolutionOutcome
 *
 * The outcome of a post-charge `IDonorDedupService.ResolveOrCreateAsync`.
 */
export const DonorResolutionOutcome = {
  /** An unambiguous existing contact was adopted and enriched. */
  MatchedAndEnriched: 'MatchedAndEnriched',
  /** No candidate at all — a fresh contact was created, no flag. */
  CreatedClean: 'CreatedClean',
  /** An ambiguous candidate existed — a fresh contact was created AND flagged. */
  CreatedWithFlag: 'CreatedWithFlag',
} as const;
export type DonorResolutionOutcome = (typeof DonorResolutionOutcome)[keyof typeof DonorResolutionOutcome];
export declare namespace DonorResolutionOutcome {
  type MatchedAndEnriched = 'MatchedAndEnriched';
  type CreatedClean = 'CreatedClean';
  type CreatedWithFlag = 'CreatedWithFlag';
}

/**
 * HooverCanvassingApi.Features.Finance.DonorSourceCategory
 *
 * A legal status of a donor that bans its gift outright whatever its type or amount, such as New Jersey's casino-licensee or regulated-industry bans. Orthogonal to `OrganizationType`: a casino licensee is still a corporation, and a foreign national is still an individual. Which categories a jurisdiction recognizes is declared by `RulesetConfig.SourceCategories`, and a `sourceRestrictions` row keyed on a category is what prohibits it. Stored by name.
 */
export const DonorSourceCategory = {
  CasinoLicensee: 'CasinoLicensee',
  RegulatedCorporation: 'RegulatedCorporation',
  ForeignNational: 'ForeignNational',
  Anonymous: 'Anonymous',
} as const;
export type DonorSourceCategory = (typeof DonorSourceCategory)[keyof typeof DonorSourceCategory];
export declare namespace DonorSourceCategory {
  type CasinoLicensee = 'CasinoLicensee';
  type RegulatedCorporation = 'RegulatedCorporation';
  type ForeignNational = 'ForeignNational';
  type Anonymous = 'Anonymous';
}

/**
 * HooverCanvassingApi.Features.Fundraising.CallTime.DonorTimelineKind
 *
 * Which detail a donor timeline entry carries.
 */
export const DonorTimelineKind = {
  Call: 'Call',
  FollowUp: 'FollowUp',
  /** A Fundraiser's note on an Open Commitment — an annotation, never a state. */
  CheckIn: 'CheckIn',
  /** The person was put on a call book, with the Ask frozen then. */
  AddedToCallBook: 'AddedToCallBook',
} as const;
export type DonorTimelineKind = (typeof DonorTimelineKind)[keyof typeof DonorTimelineKind];
export declare namespace DonorTimelineKind {
  type Call = 'Call';
  type FollowUp = 'FollowUp';
  type CheckIn = 'CheckIn';
  type AddedToCallBook = 'AddedToCallBook';
}

/**
 * HooverCanvassingApi.Features.Finance.DuplicateFlagMatchReason
 *
 * Derived-at-read-time reason the waterfall flagged a pair (the flag stores none): the strongest signal the two contacts share.
 */
export const DuplicateFlagMatchReason = {
  Email: 'Email',
  NameAndAddress: 'NameAndAddress',
  Name: 'Name',
} as const;
export type DuplicateFlagMatchReason = (typeof DuplicateFlagMatchReason)[keyof typeof DuplicateFlagMatchReason];
export declare namespace DuplicateFlagMatchReason {
  type Email = 'Email';
  type NameAndAddress = 'NameAndAddress';
  type Name = 'Name';
}

/**
 * HooverCanvassingApi.Features.Finance.DuplicateFlagProvenance
 *
 * Where a `DuplicateContactFlag` came from. WriteTime = the pair was detected as it was being written (a new contact matched an existing one). Backfill = a later all-pairs sweep over existing contacts surfaced the pair; such a flag has no ledger row of its own. Stored as string.
 */
export const DuplicateFlagProvenance = {
  WriteTime: 'WriteTime',
  Backfill: 'Backfill',
} as const;
export type DuplicateFlagProvenance = (typeof DuplicateFlagProvenance)[keyof typeof DuplicateFlagProvenance];
export declare namespace DuplicateFlagProvenance {
  type WriteTime = 'WriteTime';
  type Backfill = 'Backfill';
}

/**
 * HooverCanvassingApi.Features.Finance.DuplicateFlagStatus
 *
 * Lifecycle of a `DuplicateContactFlag`. Open = the duplicate pair is surfaced and awaiting a treasurer decision. Dismissed = treasurer said "these are genuinely different contacts." Merged = the two were consolidated. Stored as string.
 */
export const DuplicateFlagStatus = {
  Open: 'Open',
  Dismissed: 'Dismissed',
  Merged: 'Merged',
} as const;
export type DuplicateFlagStatus = (typeof DuplicateFlagStatus)[keyof typeof DuplicateFlagStatus];
export declare namespace DuplicateFlagStatus {
  type Open = 'Open';
  type Dismissed = 'Dismissed';
  type Merged = 'Merged';
}

/**
 * HooverCanvassingApi.Features.Finance.ElectionAttributionBasis
 *
 * See `RulesetConfig.ElectionAttribution`.
 */
export const ElectionAttributionBasis = {
  /** The donor's designation decides. Undesignated money counts toward the next election on or after its date (`ElectionBucketResolver`, ADR-079), and folds into the primary for a per-election itemization aggregate. */
  DonorDesignation: 'DonorDesignation',
  /** The receipt date decides and a designation is ignored: the gift counts toward the first election, in date order, still open on that date, where an election stays open through `ElectionAttributionPolicy.ElectionClosesDaysAfter` days after election day. A gift received after every election closed counts toward the last one and is flagged for review. */
  ReceiptDate: 'ReceiptDate',
} as const;
export type ElectionAttributionBasis = (typeof ElectionAttributionBasis)[keyof typeof ElectionAttributionBasis];
export declare namespace ElectionAttributionBasis {
  type DonorDesignation = 'DonorDesignation';
  type ReceiptDate = 'ReceiptDate';
}

/**
 * HooverCanvassingApi.Features.Finance.ElectionDateRuleBasis
 *
 * See `RulesetConfig.ElectionDateRule`.
 */
export const ElectionDateRuleBasis = {
  /** The law leaves the dates to the committee's settings; nothing is suggested. */
  None: 'None',
  /** Each election falls on the Tuesday after the first Monday of its declared month. */
  TuesdayAfterFirstMonday: 'TuesdayAfterFirstMonday',
} as const;
export type ElectionDateRuleBasis = (typeof ElectionDateRuleBasis)[keyof typeof ElectionDateRuleBasis];
export declare namespace ElectionDateRuleBasis {
  type None = 'None';
  type TuesdayAfterFirstMonday = 'TuesdayAfterFirstMonday';
}

/** HooverCanvassingApi.Features.Finance.ElectionDesignation */
export const ElectionDesignation = {
  Primary: 'Primary',
  General: 'General',
  /** A gift designated to a runoff election. AL / GA / Federal can all set a primary runoff, a general runoff, or both (`ElectionCalendar`). The federal `.fec` Schedule A election code emits `R{year}`; AL does not split contributions by election, so its EDI is unaffected. Stored by string name (`HasConversion<string>`, varchar(50)), so its position here carries no ordinal meaning. */
  Runoff: 'Runoff',
  Undesignated: 'Undesignated',
} as const;
export type ElectionDesignation = (typeof ElectionDesignation)[keyof typeof ElectionDesignation];
export declare namespace ElectionDesignation {
  type Primary = 'Primary';
  type General = 'General';
  type Runoff = 'Runoff';
  type Undesignated = 'Undesignated';
}

/**
 * HooverCanvassingApi.Features.Finance.ElectionDueDateAdjustment
 *
 * How a jurisdiction treats a due date that falls on a non-business day. Applies to the election-offset due dates AND to the fixed calendar ones ("due February 15") — one declared rule, obeyed by both. Two generators deliberately do NOT consult it, and neither is a bug you should "fix" by threading this in without reading them first: a cadence entry (`ScheduleCadence.Monthly`/`ScheduleCadence.Weekly`) carries its own business-day handling in `ReportingCadenceExpander`, and `DeadlineService.GenerateElectionAnchoredPeriod` (North Carolina's period-end-relative quarterlies) hardcodes a FORWARD roll even though NC declares PriorBusinessDay — which is either a second rule NC genuinely has for that family, or a latent contradiction. It is untouched and unverified either way.
 */
export const ElectionDueDateAdjustment = {
  /** Walk the due date backward to the preceding business day: the receipt rule for a filing counter that is shut on the nominal date. NOT a default — an undeclared value is rejected at load (JsonReportingScheduleProvider.ValidateElectionDueDateAdjustmentDeclared). It is what every state ruleset except federal, Louisiana and New Jersey declares today, and for most of them that is an unverified carry-over rather than a researched finding, so treat it as a claim to check against the regulator's published calendar, not a safe starting point for a new jurisdiction. */
  PriorBusinessDay: 'PriorBusinessDay',
  /** Leave the due date on its actual calendar date. Correct wherever the filer submits electronically: 11 CFR 100.19(c) makes a federal report timely when the FEC receives and validates it by 11:59pm Eastern ON THE FILING DATE, and the FEC's published calendars state that "filing deadlines are not extended when they fall on nonworking days." The preceding-business-day walk is a RECEIPT rule for paper filers who hand-deliver or use first-class mail — the FEC counter is shut — and it never moves the deadline itself. Filers using certified or tracked overnight mail are judged on the postmark and are not shifted either. */
  ActualDate: 'ActualDate',
  /** Walk the due date forward to the following business day. Louisiana's rule: the Board of Ethics' published "SCHEDULE OF REPORTING AND FILING DATES" moves a deadline landing on a non-business day LATER, never earlier — for the October 9 2027 primary the 90-day pre-primary falls nominally on Sunday July 11 2027 and is published as Monday July 12. Across six published election calendars every deadline that moved at all moved forward; none moved backward. This shifts the DUE date only. The close of books is a fixed calendar offset and stays put even on a weekend — the same sheet closes the 10-P's books on Sunday September 19 and the 10-G's on Sunday October 31. */
  NextBusinessDay: 'NextBusinessDay',
} as const;
export type ElectionDueDateAdjustment = (typeof ElectionDueDateAdjustment)[keyof typeof ElectionDueDateAdjustment];
export declare namespace ElectionDueDateAdjustment {
  type PriorBusinessDay = 'PriorBusinessDay';
  type ActualDate = 'ActualDate';
  type NextBusinessDay = 'NextBusinessDay';
}

/**
 * HooverCanvassingApi.Features.Finance.ElectionOutcomeResult
 *
 * ADR-076: what happened at an `ElectionStage`. Recorded per stage by an admin; each recording persists the fact and writes a finance-audit row. A distinct axis from both `SubscriptionStatus` (the SaaS billing state) and `TenantCampaignStatus` (the account lifecycle): recording an outcome NEVER mutates either (cutoff consequences are a separate concern). Stored by string name (`HasConversion<string>`, varchar(50)), so ordering carries no meaning.
 */
export const ElectionOutcomeResult = {
  Advanced: 'Advanced',
  WentToRunoff: 'WentToRunoff',
  Won: 'Won',
  Lost: 'Lost',
  Withdrew: 'Withdrew',
} as const;
export type ElectionOutcomeResult = (typeof ElectionOutcomeResult)[keyof typeof ElectionOutcomeResult];
export declare namespace ElectionOutcomeResult {
  type Advanced = 'Advanced';
  type WentToRunoff = 'WentToRunoff';
  type Won = 'Won';
  type Lost = 'Lost';
  type Withdrew = 'Withdrew';
}

/**
 * HooverCanvassingApi.Features.Finance.ElectionStage
 *
 * ADR-076: the stage of an election cycle an `ElectionOutcome` is recorded against. A NEW axis, distinct from `ElectionDesignation` (which designates which election a GIFT is for): this names the STAGE a committee reached so a recorded `ElectionOutcomeResult` attaches to a specific point in the cycle. One `ElectionOutcome` row exists per (CampaignId, Stage). `ElectionStage.SingleElection` covers a jurisdiction or office with no primary→general split (a lone election). Stored by string name (`HasConversion<string>`, varchar(50)), so its position here carries no ordinal meaning.
 */
export const ElectionStage = {
  Primary: 'Primary',
  PrimaryRunoff: 'PrimaryRunoff',
  General: 'General',
  GeneralRunoff: 'GeneralRunoff',
  SingleElection: 'SingleElection',
} as const;
export type ElectionStage = (typeof ElectionStage)[keyof typeof ElectionStage];
export declare namespace ElectionStage {
  type Primary = 'Primary';
  type PrimaryRunoff = 'PrimaryRunoff';
  type General = 'General';
  type GeneralRunoff = 'GeneralRunoff';
  type SingleElection = 'SingleElection';
}

/**
 * HooverCanvassingApi.Common.Services.EmailLayout
 *
 * The frame an email body renders inside.
 */
export const EmailLayout = {
  /** `_layout.liquid`: the campaign's banner, contact lines, Paid-for-by and unsubscribe slot. */
  Campaign: 'Campaign',
  /** `_system-layout.liquid`: plain transactional mail from the platform, with no campaign branding, images or unsubscribe, so inboxes file it as Primary. Its values come from the model alone. */
  System: 'System',
} as const;
export type EmailLayout = (typeof EmailLayout)[keyof typeof EmailLayout];
export declare namespace EmailLayout {
  type Campaign = 'Campaign';
  type System = 'System';
}

/**
 * HooverCanvassingApi.Common.Services.EmailVoice
 *
 * Voice used for the from-address pair on an outbound email. Transactional is the default (noreply@...) for password resets, invitations, shift notifications, etc. Personal (adil@...) is used for lifecycle / conversion emails that benefit from a founder voice and encourage replies.
 */
export const EmailVoice = {
  Transactional: 'Transactional',
  Personal: 'Personal',
} as const;
export type EmailVoice = (typeof EmailVoice)[keyof typeof EmailVoice];
export declare namespace EmailVoice {
  type Transactional = 'Transactional';
  type Personal = 'Personal';
}

/**
 * HooverCanvassingApi.Features.Fundraising.Enrichment.EnrichmentRunScope
 *
 * Who a run matches: the `scope` on `POST /api/fundraising/enrichment/run`.
 */
export const EnrichmentRunScope = {
  /** Every Prospect in the campaign — to refresh everyone after a new Corpus Version. */
  Everyone: 'Everyone',
  /** Only Prospects no run has looked at — the book's "Not matched yet" (`Prospect.NotMatchedYet`). */
  Unmatched: 'Unmatched',
} as const;
export type EnrichmentRunScope = (typeof EnrichmentRunScope)[keyof typeof EnrichmentRunScope];
export declare namespace EnrichmentRunScope {
  type Everyone = 'Everyone';
  type Unmatched = 'Unmatched';
}

/**
 * HooverCanvassingApi.Features.Fundraising.Enrichment.EnrichmentRunState
 *
 * Where a campaign's latest "match our donors" run stands.
 */
export const EnrichmentRunState = {
  /** No run since the API started (run status is in memory and lost on restart). */
  NeverRun: 'NeverRun',
  /** Queued or running. */
  Running: 'Running',
  Completed: 'Completed',
  /** The run itself failed (not one person — those are counted in the summary). */
  Failed: 'Failed',
} as const;
export type EnrichmentRunState = (typeof EnrichmentRunState)[keyof typeof EnrichmentRunState];
export declare namespace EnrichmentRunState {
  type NeverRun = 'NeverRun';
  type Running = 'Running';
  type Completed = 'Completed';
  type Failed = 'Failed';
}

/**
 * HooverCanvassingApi.Features.Fundraising.Enrichment.EnrichmentStageStatus
 *
 * Where one matcher stage stands in the chunk being matched right now.
 */
export const EnrichmentStageStatus = {
  Pending: 'Pending',
  Active: 'Active',
  Done: 'Done',
} as const;
export type EnrichmentStageStatus = (typeof EnrichmentStageStatus)[keyof typeof EnrichmentStageStatus];
export declare namespace EnrichmentStageStatus {
  type Pending = 'Pending';
  type Active = 'Active';
  type Done = 'Done';
}

/** HooverCanvassingApi.Features.Finance.PortalEntry.EnteredRowKind */
export const EnteredRowKind = {
  Contribution: 'Contribution',
  Expenditure: 'Expenditure',
  Loan: 'Loan',
  Refund: 'Refund',
} as const;
export type EnteredRowKind = (typeof EnteredRowKind)[keyof typeof EnteredRowKind];
export declare namespace EnteredRowKind {
  type Contribution = 'Contribution';
  type Expenditure = 'Expenditure';
  type Loan = 'Loan';
  type Refund = 'Refund';
}

/**
 * HooverCanvassingApi.Features.Finance.ExpenditureCategory
 *
 * What an Expenditure was for. The ContributionRefund category is retired: a refund is now solely a ContributionRefund entity row (no companion Expenditure), so an Expenditure can never carry that category. Stored as an unconstrained string (`HasConversion<string>`, no DB CHECK), so the value retired cleanly once the residual rows were TRUNCATEd. See docs/design/2026-06-17-refund-dismiss-or-flag-redesign.md §9.6.
 */
export const ExpenditureCategory = {
  Operating: 'Operating',
  Fundraising: 'Fundraising',
  Travel: 'Travel',
  Personnel: 'Personnel',
  Advertising: 'Advertising',
  Other: 'Other',
  /** A repayment of a loan made to or guaranteed by the candidate. Maps to the FEC Schedule B line 19(a) record (SB19A) and to AL ExPurpose "07" (Loan Repayment); without this arm AL's `MapExpenditurePurpose` would silently fall through to Other (10). Distinct from the retired ContributionRefund category: a loan repayment IS an Expenditure (a real cash disbursement to the lender), whereas a refund is a ContributionRefund entity (no companion Expenditure). The candidate-vs-other-lender split (line 19a vs 19b) is NOT modeled in v1; only candidate personal-funds loans exist, so every LoanRepayment files on 19a/SB19A. Only the federal prior-filing import writes it; no category picker offers it. See docs/research/fec-f3-summary-line-map.md. */
  LoanRepayment: 'LoanRepayment',
  /** Reimbursing a person for out-of-pocket campaign outlays. Maps to FEC Schedule B line 17 (SB17, an operating disbursement). v6.0 AL FCPA has NO "reimbursement" purpose code (the pre-v6.0 08 the old mapping used is "Lodging" in v6.0), so AL files it as ExPurpose "10" (Other); Other(10) forces `exExplanationOther`, so the real purpose still rides out. */
  Reimbursement: 'Reimbursement',
  /**
   * A reportable transfer of funds to a DIFFERENT committee or entity. v6.0 AL FCPA has NO "transfer" purpose code (the pre-v6.0 05 the old mapping used is "Food" in v6.0), so AL files it as ExPurpose "10" (Other); Other(10) forces `exExplanationOther`, so the real purpose still rides out. FEC maps it to line 17 (SB17) as a v1 approximation: federal transfers to other authorized committees belong on line 18, which is not modeled, and are rare for our filers.
   *
   * A transfer between the committee's OWN accounts is NOT an expenditure: that is Exclude-from-report (ADR-014). This category is only for an outbound transfer that IS a reportable disbursement.
   */
  Transfer: 'Transfer',
  /** A disbursement that is NOT an operating expenditure, loan repayment, or contribution refund: FEC Form 3 line 21 "Other Disbursements", Schedule B SB21 (11 CFR 104.3(b)(4)(vi)). Real filers use it for non-federal donations, transfers to party/PAC committees, and contributions to OTHER candidates. Unlike `ExpenditureCategory.Other` (a misc OPERATING purpose, such as processing fees, that files on line 17), an OtherDisbursement is a distinct reportable transaction on its own summary line (line 21), so it is EXCLUDED from the line-17 operating aggregate and always itemizes: the disbursement-side twin of `ExpenditureCategory.LoanRepayment` (line 19). Added so the reverse importer can round-trip an SB21 instead of manifesting it (#65, follow-up to #57). Federal-only in practice: only the federal prior-filing import writes it and no category picker offers it; the jurisdiction-neutral category maps to AL exPurpose 10 (Other) honestly if one ever appears on an AL filing. */
  OtherDisbursement: 'OtherDisbursement',
  /** A "not yet categorized" state: the deliberate placeholder for an IMPORTED expense whose category the source didn't supply. Distinct from `ExpenditureCategory.Other` (a real, treasurer-chosen "misc operating" category): Uncategorized means the treasurer has NOT yet chosen, so the two are distinguishable. NOT the enum zero-value (Operating stays zero, so an unset entity never becomes Uncategorized by accident) and never a treasurer-selectable option (the category picker excludes it). An uncategorized expenditure HARD-BLOCKS filing (`UncategorizedExpenditureEnforceCheck`) until the treasurer picks a real category. Stored as an unconstrained string (`HasConversion<string>`, no DB CHECK), so adding it needed no migration. */
  Uncategorized: 'Uncategorized',
} as const;
export type ExpenditureCategory = (typeof ExpenditureCategory)[keyof typeof ExpenditureCategory];
export declare namespace ExpenditureCategory {
  type Operating = 'Operating';
  type Fundraising = 'Fundraising';
  type Travel = 'Travel';
  type Personnel = 'Personnel';
  type Advertising = 'Advertising';
  type Other = 'Other';
  type LoanRepayment = 'LoanRepayment';
  type Reimbursement = 'Reimbursement';
  type Transfer = 'Transfer';
  type OtherDisbursement = 'OtherDisbursement';
  type Uncategorized = 'Uncategorized';
}

/**
 * HooverCanvassingApi.Features.Finance.ExpenditureClassificationSubCategory
 *
 * BSW-009: which expenditure shape the treasurer picked in the panel's Expenditure sub-category dropdown. Drives the resolver branch in `BankTransactionClassifyAsExpenditureService`. Per the BSW-009 ticket Technical Notes, Personal/Dismiss is intentionally NOT a sub-category — Dismiss is a top-level TypeToggle action only, resolving the dual-entry-point confusion the verification reviewer flagged.
 */
export const ExpenditureClassificationSubCategory = {
  ProcessingFee: 'ProcessingFee',
  Refund: 'Refund',
  BankFee: 'BankFee',
  GeneralExpenditure: 'GeneralExpenditure',
} as const;
export type ExpenditureClassificationSubCategory = (typeof ExpenditureClassificationSubCategory)[keyof typeof ExpenditureClassificationSubCategory];
export declare namespace ExpenditureClassificationSubCategory {
  type ProcessingFee = 'ProcessingFee';
  type Refund = 'Refund';
  type BankFee = 'BankFee';
  type GeneralExpenditure = 'GeneralExpenditure';
}

/**
 * HooverCanvassingApi.Features.Finance.ExpenditureHintProvenance
 *
 * Whether an Expenditure came from a classification hint. Recorded when a bank line is classified: `ExpenditureHintProvenance.FromHint` when the treasurer kept every value the hint suggested, `ExpenditureHintProvenance.FromHintEdited` when they changed any of them, `ExpenditureHintProvenance.Manual` when no hint was involved — which every Expenditure entered any other way, or before hints existed, reads as.
 */
export const ExpenditureHintProvenance = {
  Manual: 'Manual',
  FromHint: 'FromHint',
  FromHintEdited: 'FromHintEdited',
} as const;
export type ExpenditureHintProvenance = (typeof ExpenditureHintProvenance)[keyof typeof ExpenditureHintProvenance];
export declare namespace ExpenditureHintProvenance {
  type Manual = 'Manual';
  type FromHint = 'FromHint';
  type FromHintEdited = 'FromHintEdited';
}

/**
 * HooverCanvassingApi.Common.Models.ExperienceLevel
 *
 * Manager's prior experience running canvassing — drives onboarding guidance volume.
 */
export const ExperienceLevel = {
  FirstTime: 'FirstTime',
  Some: 'Some',
  Experienced: 'Experienced',
} as const;
export type ExperienceLevel = (typeof ExperienceLevel)[keyof typeof ExperienceLevel];
export declare namespace ExperienceLevel {
  type FirstTime = 'FirstTime';
  type Some = 'Some';
  type Experienced = 'Experienced';
}

/**
 * HooverCanvassingApi.Features.Finance.ExportBatchMemberKind
 *
 * Discriminator for an `ExportBatchMember`'s `EntityId` — the table it references. Refunds never appear (AL emits nothing for them).
 */
export const ExportBatchMemberKind = {
  Contribution: 'Contribution',
  Expenditure: 'Expenditure',
  Loan: 'Loan',
} as const;
export type ExportBatchMemberKind = (typeof ExportBatchMemberKind)[keyof typeof ExportBatchMemberKind];
export declare namespace ExportBatchMemberKind {
  type Contribution = 'Contribution';
  type Expenditure = 'Expenditure';
  type Loan = 'Loan';
}

/**
 * HooverCanvassingApi.Features.Finance.ExportBatchStatus
 *
 * Lifecycle of an `ExportBatch` — the outbound upload-once ledger for AL's append-only FCPA portal (ADR-020). Distinct from `ImportBatchStatus`: an import is `Active` on creation, whereas an export has a throwaway `Generated` draft phase the upload-acceptance gate hinges on.
 *
 * - `Generated` — draft built, bytes + SHA pinned, awaiting the gate; the single live draft per report. Members live (non-tombstoned). Also the default/sentinel.
 * - `Uploaded` — gate answered "Yes, it imported"; member contributions/expenditures flip Confirmed → Reported, loans use membership as the marker. Members stay non-tombstoned forever — that permanent live membership is the upload-once slot.
 * - `Undone` — Undo upload reverted an Uploaded batch (members revert Reported → Confirmed). Declared for completeness; no v1 endpoint emits it. Members tombstoned.
 * - `Superseded` — regenerate replaced an un-uploaded draft. Members tombstoned.
 * - `Discarded` — the gate was answered "No" / the draft was rejected at the acceptance gate. A terminal status distinct from the others (never uploaded, never replaced). Members tombstoned.
 *
 * `Uploaded` is the only non-terminal-tombstoning end state — its members stay in the upload-once index forever and can never re-emit.
 */
export const ExportBatchStatus = {
  Generated: 'Generated',
  Uploaded: 'Uploaded',
  Undone: 'Undone',
  Superseded: 'Superseded',
  Discarded: 'Discarded',
} as const;
export type ExportBatchStatus = (typeof ExportBatchStatus)[keyof typeof ExportBatchStatus];
export declare namespace ExportBatchStatus {
  type Generated = 'Generated';
  type Uploaded = 'Uploaded';
  type Undone = 'Undone';
  type Superseded = 'Superseded';
  type Discarded = 'Discarded';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.Federal.FecFile.FecF3SummaryDegradedEntityType
 *
 * Issue #117 — the kind of row a Form 3 summary-build failure is attributed to, so the report-preview degradation banner can name the record and deep-link to the correct Close-ledger row (a `FecF3SummaryDegradedEntityType.Contribution` → the `contributionId` param, an `FecF3SummaryDegradedEntityType.Expenditure` → the `expenditureId` param — the same round-trip the existing "Fix Issues" links use). Serialized PascalCase on the wire by the global `JsonStringEnumConverter`, so the frontend discriminated union matches `'Contribution'` / `'Expenditure'` exactly.
 */
export const FecF3SummaryDegradedEntityType = {
  Contribution: 'Contribution',
  Expenditure: 'Expenditure',
} as const;
export type FecF3SummaryDegradedEntityType = (typeof FecF3SummaryDegradedEntityType)[keyof typeof FecF3SummaryDegradedEntityType];
export declare namespace FecF3SummaryDegradedEntityType {
  type Contribution = 'Contribution';
  type Expenditure = 'Expenditure';
}

/**
 * HooverCanvassingApi.Features.Finance.FederalTransferQualification
 *
 * #711 — WHY a federal-transfer receipt is exempt from the contribution limits. The exemption is defined by the RELATIONSHIP between the two committees, never by the giver merely being a committee (11 CFR § 110.3(c) "Permissible Transfers"); letting any committee-sourced receipt be labelled a transfer turns a correctly-exempt category into a way around every ceiling.
 *
 * Only the arms a candidate's authorized committee can actually stand in are modelled. The other § 110.3(c) categories are unreachable for a Form 3 filer: same-PARTY transfers run between party committees (a candidate committee is never one); a primary→general transfer (c)(3) is internal to one committee; and the previous→current (c)(4) and dual-office (c)(5) cases need a second-committee model this app does not have (tracked separately, and (c)(5) additionally requires attributing the transferred funds back to their original donors).
 */
export const FederalTransferQualification = {
  /** No qualifying relationship: an ordinary committee gift wearing a transfer label. It meters against the contribution limits and files on the contributor's own Schedule A line, inside total contributions. This is the answer that closes the bypass. */
  None: 'None',
  /** 11 CFR § 110.3(c)(1) via § 100.5(g)(1) — from another authorized committee of the SAME candidate. Those committees are affiliated, so the transfer is not a new gift between political actors but an internal movement of the same money. This is the relationship Schedule A line 12 names (52 U.S.C. § 30104(b)(2)(E)). */
  SameCandidateAuthorizedCommittee: 'SameCandidateAuthorizedCommittee',
  /** 11 CFR § 110.3(c)(2) — the committee's allocated share of joint-fundraising proceeds, transferred by the fundraising representative to a participant. A DISTINCT exemption from affiliation: the payer need not be an authorized committee of this candidate, so collapsing it into `FederalTransferQualification.SameCandidateAuthorizedCommittee` would wrongly meter a lawful, expressly-exempt receipt. */
  JointFundraisingProceeds: 'JointFundraisingProceeds',
} as const;
export type FederalTransferQualification = (typeof FederalTransferQualification)[keyof typeof FederalTransferQualification];
export declare namespace FederalTransferQualification {
  type None = 'None';
  type SameCandidateAuthorizedCommittee = 'SameCandidateAuthorizedCommittee';
  type JointFundraisingProceeds = 'JointFundraisingProceeds';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.FeeBasisScope
 *
 * Which contribution set a fee estimate is computed over (ADR-045). The two bases share ONE summation and differ ONLY by the ledger-status set, so rate/flat/refund handling can never drift between them.
 */
export const FeeBasisScope = {
  /** The [[Recordable fee]] basis — the fee on exactly the donations that will FILE (Confirmed / Reported, non-Voided). The ONLY actionable figure: it arms the pin, is what the book-a-fee Record action books, and is what the federal unbooked-fee gate checks. */
  Recordable: 'Recordable',
  /** The [[Fee forecast]] ("expected total") basis — the recordable set PLUS still-unreviewed Pending money. Display-only: it NEVER arms, books, or gates; it only shows the treasurer where the recordable fee is heading (visible on a fresh import before anything is confirmed). */
  Forecast: 'Forecast',
} as const;
export type FeeBasisScope = (typeof FeeBasisScope)[keyof typeof FeeBasisScope];
export declare namespace FeeBasisScope {
  type Recordable = 'Recordable';
  type Forecast = 'Forecast';
}

/**
 * HooverCanvassingApi.Features.Finance.FeeRowState
 *
 * Lifecycle state of the estimated processor-fee row, server-derived so the client never re-computes arm/difference logic (C4). Passive: period open OR sub-tolerance gap — shown to the cent, no primary action. Active: period closing and armed (estimated − recorded > tolerance) — the primary "Record" prompt. A late-gift fee discovered after filing is not a distinct row-state: it is booked as an ordinary expenditure dated into the corrected period, which flips that report to NeedsAmendment via the general amendment check (ADR-019/020).
 */
export const FeeRowState = {
  Passive: 'Passive',
  Active: 'Active',
} as const;
export type FeeRowState = (typeof FeeRowState)[keyof typeof FeeRowState];
export declare namespace FeeRowState {
  type Passive = 'Passive';
  type Active = 'Active';
}

/** HooverCanvassingApi.Features.Assistant.FeedbackRating */
export const FeedbackRating = {
  Up: 'Up',
  Down: 'Down',
} as const;
export type FeedbackRating = (typeof FeedbackRating)[keyof typeof FeedbackRating];
export declare namespace FeedbackRating {
  type Up = 'Up';
  type Down = 'Down';
}

/**
 * HooverCanvassingApi.Features.Fundraising.Enrichment.FieldAgreement
 *
 * How one of a candidate's fields compares with the Prospect's own.
 */
export const FieldAgreement = {
  /** The same, once spelling, case and company words are set aside. */
  Same: 'Same',
  /** Not the same, but compatible: a nickname or a one-sided middle initial or suffix, the same house in other words, one job title inside the other. */
  Close: 'Close',
  /** A different value. */
  Differs: 'Differs',
} as const;
export type FieldAgreement = (typeof FieldAgreement)[keyof typeof FieldAgreement];
export declare namespace FieldAgreement {
  type Same = 'Same';
  type Close = 'Close';
  type Differs = 'Differs';
}

/**
 * HooverCanvassingApi.Features.Finance.FiledLoanBalanceSource
 *
 * Where a jurisdiction's filed loan balance COMES FROM — the question the as-filed freeze has to answer, because the frozen evidence is the authority for what a filing reported and stamping the wrong column records a number the file never printed.
 *
 * Serialized as a string in ruleset JSON. Declared, never defaulted: a jurisdiction that derives its balance and inherits the treasurer-maintained freeze would fail silently — the filing would be right and its own evidence wrong.
 */
export const FiledLoanBalanceSource = {
  /** The treasurer-maintained `Loan.OutstandingBalance` scalar, which is what the filing emitted. Federal (`SC/10`'s carry-forward columns are cumulative treasurer figures by FEC definition), Alabama, Virginia, North Carolina. */
  TreasurerMaintained: 'TreasurerMaintained',
  /** Derived by date-windowing the loan's linked repayment expenditures. Louisiana: its Schedule B closing balance and the payment sub-records beneath it come from one window, so the balance and the payments explaining it cannot drift. */
  DerivedFromLinkedPayments: 'DerivedFromLinkedPayments',
} as const;
export type FiledLoanBalanceSource = (typeof FiledLoanBalanceSource)[keyof typeof FiledLoanBalanceSource];
export declare namespace FiledLoanBalanceSource {
  type TreasurerMaintained = 'TreasurerMaintained';
  type DerivedFromLinkedPayments = 'DerivedFromLinkedPayments';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.Summary.FiledLoanBasisPolicy
 *
 * ADR-051 — a per-jurisdiction report-summary projector: the self-contained module that turns an assembled report-row set into the state-specific summary view the report-detail page (and the Close-hub preview) renders. Dispatched through `ReportSummaryProjectorResolver` by ruleset code, mirroring the `(StateCode, FormatKind)` dispatch of `SubmissionBuilderResolver`. Onboarding a state's summary view is one projector + one registration + one frontend switch case — never an edit to a shared `RulesetCode == …` switch (the exact scattering ADR-051 retires).
 *
 * The two shipping projections are entangled at DIFFERENT points of the detail build, with different side effects, so the projector is deliberately two-phase:
 *
 * - `ProjectScheduleMembership` — runs EARLY, right after assembly, because its output feeds the flat Schedule A/B/D row-filtering (Virginia drops its Schedule-C received receipts and Schedule-E loan-repayments from the A/B/D tables so each row shows exactly once). Fail-loud on the detail path (the preview wraps the whole projection).
 * - `ProjectSummary` — runs LATE, after the displayed item lists are built, because the federal projection stamps each displayed item's resolved FEC line. It owns its own degradation semantics (federal degrades the whole summary to null on broken data, an all-or-nothing flat fallback that never blocks the page).
 *
 * A projector implements only the phase it needs; the neutral default (`FlatReportSummaryProjector`) implements neither and yields the by-type / by- category breakdown.
 */
export const FiledLoanBasisPolicy = {
  /** This view reports no loan balances, so no basis is ever resolved for it (Alabama, the neutral default). */
  NotNeeded: 'NotNeeded',
  /** Resolve the basis; when it cannot be resolved, the PROJECTOR answers. Federal returns a `ReportSummaryProjectionResult.Degraded` result — the same flat view the resolver would reach, but carrying the treasurer-facing banner that says why the summary is missing. */
  ProjectorDegrades: 'ProjectorDegrades',
  /** Resolve the basis; when it cannot be resolved, the RESOLVER declines this projector and the report keeps the neutral flat view. For a projector with no degradation path of its own, where a null-shaped result would read as a legitimate empty view rather than a refusal (Virginia, North Carolina — ADR-034 records why neither should grow one). */
  ResolverDeclines: 'ResolverDeclines',
} as const;
export type FiledLoanBasisPolicy = (typeof FiledLoanBasisPolicy)[keyof typeof FiledLoanBasisPolicy];
export declare namespace FiledLoanBasisPolicy {
  type NotNeeded = 'NotNeeded';
  type ProjectorDegrades = 'ProjectorDegrades';
  type ResolverDeclines = 'ResolverDeclines';
}

/**
 * HooverCanvassingApi.Features.Finance.Filing.FilingCompletenessCategory
 *
 * The completeness buckets a federal preflight signal can fall into. Order is by urgency for display: a hard fix (`FilingCompletenessCategory.CannotFileFix`) over a portal hand-off.
 */
export const FilingCompletenessCategory = {
  /** "We file what we can; complete this in the FEC portal" — deferred regimes the system can't emit (earmarked conduit pass-through, an amendment receipt id the FEC assigns, a re-materialized struck loan, a committee-transfer / in-kind-org / mis-lined refund the portal must finish). */
  PortalHandoff: 'PortalHandoff',
  /** The report's money doesn't conserve — the filed totals don't account for all the confirmed dollars in the period, beginning cash doesn't continue from the last report, or cash on hand goes negative. */
  ReconciliationGap: 'ReconciliationGap',
  /** A curable field is missing or inconsistent — add it for a complete report (a disbursement purpose, a lender name, a refund payee address, a report code, a loan ledger that doesn't reconcile). */
  DataToFix: 'DataToFix',
  /** The FEC's best-efforts path applies — filing is allowed once a documented good-faith request for the missing donor information is on file. */
  BestEffort: 'BestEffort',
  /** A hard failure — the file literally cannot be built until it is fixed. */
  CannotFileFix: 'CannotFileFix',
} as const;
export type FilingCompletenessCategory = (typeof FilingCompletenessCategory)[keyof typeof FilingCompletenessCategory];
export declare namespace FilingCompletenessCategory {
  type PortalHandoff = 'PortalHandoff';
  type ReconciliationGap = 'ReconciliationGap';
  type DataToFix = 'DataToFix';
  type BestEffort = 'BestEffort';
  type CannotFileFix = 'CannotFileFix';
}

/**
 * HooverCanvassingApi.Features.Finance.FilingGranularity
 *
 * ADR-021 — the export-path SHAPE axis. `Incremental` (AL) drives the per-period ExportBatch delta + upload gate, exporting only the not-yet-uploaded rows; `WholeReport` (federal) keeps the per-report SubmissionPackage and generates the complete report each period. This axis, not `AmendmentModel`, decides the filing surface: New Jersey amends `PortalOnly` like Alabama but files whole reports. Plain enum (a parse target, not JSON-bound).
 */
export const FilingGranularity = {
  Incremental: 'Incremental',
  WholeReport: 'WholeReport',
} as const;
export type FilingGranularity = (typeof FilingGranularity)[keyof typeof FilingGranularity];
export declare namespace FilingGranularity {
  type Incremental = 'Incremental';
  type WholeReport = 'WholeReport';
}

/** HooverCanvassingApi.Features.Finance.FilingObligationStatus */
export const FilingObligationStatus = {
  Pending: 'Pending',
  Filed: 'Filed',
  Overdue: 'Overdue',
  Cancelled: 'Cancelled',
} as const;
export type FilingObligationStatus = (typeof FilingObligationStatus)[keyof typeof FilingObligationStatus];
export declare namespace FilingObligationStatus {
  type Pending = 'Pending';
  type Filed = 'Filed';
  type Overdue = 'Overdue';
  type Cancelled = 'Cancelled';
}

/**
 * HooverCanvassingApi.Features.Finance.FilingOrigin
 *
 * How a `ComplianceReport` came to be filed (ADR-018).
 *
 * - `InSystem` — generated by our builder and filed via the in-system pipeline (the default; every existing and forward report).
 * - `ExternalPortal` — filed by the customer directly through the state portal and imported at onboarding (the mid-cycle backfill). It has NO `SubmissionPackage`; its totals/cash are read FROZEN from the stored columns (never live-recomputed from rows, which are itemized-only and would be short the non-itemized remainder), it cannot be re-filed or in-product amended, and it keys the calendar lock on the report itself rather than a deadline.
 * - `PortalAttested` — a forward AL report the treasurer finalized by attesting "I filed it in the AL FCPA portal" (ADR-021 upload-≠-filing). AL produces no in-app filing artifact (we only upload transactions, never a report), so like `ExternalPortal` it has NO `SubmissionPackage` and cannot be re-filed / in-product amended. Deliberately DISTINCT from `ExternalPortal`: the onboarding-backfill undo deletes every `ExternalPortal` report, and the frozen-total reads are `ExternalPortal`-only — a live forward AL report must NOT be swept by either, so it carries its OWN origin and reports cash LIVE (compute-over-locked, ADR-017), not frozen.
 * - `EmailAttested` — a forward report the treasurer finalized by attesting they filed it BY EMAIL (ADR-019, upload-≠-filing) for a jurisdiction that files by emailing the artifact rather than uploading it to a portal (NC emails the `.cfd` to the NCSBE inbox; there is no portal). Behaves exactly like `PortalAttested` — no `SubmissionPackage`, reports cash LIVE, and is reopenable — but carries its own origin because `PortalAttested` is semantically wrong for an emailed filing. Shared with any other email-filed jurisdiction (e.g. MS). Delivery of the send NEVER produces this origin; only the treasurer's confirm-filed attestation does.
 */
export const FilingOrigin = {
  InSystem: 'InSystem',
  ExternalPortal: 'ExternalPortal',
  PortalAttested: 'PortalAttested',
  EmailAttested: 'EmailAttested',
} as const;
export type FilingOrigin = (typeof FilingOrigin)[keyof typeof FilingOrigin];
export declare namespace FilingOrigin {
  type InSystem = 'InSystem';
  type ExternalPortal = 'ExternalPortal';
  type PortalAttested = 'PortalAttested';
  type EmailAttested = 'EmailAttested';
}

/** HooverCanvassingApi.Features.Finance.Filing.EmailSend.FilingSendError */
export const FilingSendError = {
  NotAuthorized: 'NotAuthorized',
  ReportNotFound: 'ReportNotFound',
  NoPackageToSend: 'NoPackageToSend',
  NoDestinationConfigured: 'NoDestinationConfigured',
  MissingTreasurerEmail: 'MissingTreasurerEmail',
  AlreadySent: 'AlreadySent',
  SendFailed: 'SendFailed',
  /** Issue #861 — a county destination (no jurisdiction default) has no recorded confirmation yet. */
  DestinationNotConfirmed: 'DestinationNotConfirmed',
  /** Issue #861 — the confirmed destination doesn't match the currently-resolved one (stale/forged). */
  DestinationConfirmationMismatch: 'DestinationConfirmationMismatch',
} as const;
export type FilingSendError = (typeof FilingSendError)[keyof typeof FilingSendError];
export declare namespace FilingSendError {
  type NotAuthorized = 'NotAuthorized';
  type ReportNotFound = 'ReportNotFound';
  type NoPackageToSend = 'NoPackageToSend';
  type NoDestinationConfigured = 'NoDestinationConfigured';
  type MissingTreasurerEmail = 'MissingTreasurerEmail';
  type AlreadySent = 'AlreadySent';
  type SendFailed = 'SendFailed';
  type DestinationNotConfirmed = 'DestinationNotConfirmed';
  type DestinationConfirmationMismatch = 'DestinationConfirmationMismatch';
}

/**
 * HooverCanvassingApi.Features.Finance.FilingSendStatus
 *
 * Delivery lifecycle of an app-sent filing email (issue #295). "Delivered" is never "accepted" — it only means the destination MTA received the message; NCSBE acceptance is a separate, out-of-band fact. A bounce/complaint or a failed send routes the treasurer to download-and-self-send.
 */
export const FilingSendStatus = {
  /** Handed to Resend for delivery; awaiting a delivery/bounce webhook. */
  Sent: 'Sent',
  /** Resend confirmed delivery to the destination MTA (still not "accepted"). */
  Delivered: 'Delivered',
  /** Hard/soft bounce or spam complaint — surfaces the download-and-self-send fallback. */
  Bounced: 'Bounced',
  /** The provider send call itself failed (no message id) — also a self-send fallback. */
  Failed: 'Failed',
} as const;
export type FilingSendStatus = (typeof FilingSendStatus)[keyof typeof FilingSendStatus];
export declare namespace FilingSendStatus {
  type Sent = 'Sent';
  type Delivered = 'Delivered';
  type Bounced = 'Bounced';
  type Failed = 'Failed';
}

/**
 * HooverCanvassingApi.Features.Finance.FilingTransportChannel
 *
 * How a generated filing leaves the app (the transport channel on `CampaignFinanceSettings`). The v1 filing path is generate → download → the treasurer self-sends, so `FilingTransportChannel.Download` is the default; `FilingTransportChannel.Email` is the app-sends-on-the-campaign's-behalf fast-follow (NC emails the `.cfd` to the NCSBE inbox). A bounce on the email channel routes the treasurer back to download-and-self-send.
 */
export const FilingTransportChannel = {
  /** Generate → download → the treasurer transmits it themselves (the v1 default). */
  Download: 'Download',
  /** The app emails the filing to the state destination on the campaign's behalf. */
  Email: 'Email',
} as const;
export type FilingTransportChannel = (typeof FilingTransportChannel)[keyof typeof FilingTransportChannel];
export declare namespace FilingTransportChannel {
  type Download = 'Download';
  type Email = 'Email';
}

/**
 * HooverCanvassingApi.Features.Finance.Filing.EmailSend.Webhooks.FilingWebhookResult
 *
 * How the processor resolved one Resend event — the controller maps this to an HTTP status.
 */
export const FilingWebhookResult = {
  /** The send transitioned (delivered/bounced/failed) and the change committed. → 2xx. */
  Applied: 'Applied',
  /** No send matched this provider message id (unknown/foreign email). → 2xx, nothing to do. */
  Unmatched: 'Unmatched',
  /** Nothing to apply — duplicate delivery, untracked type, or a would-be regression. → 2xx. */
  NoOp: 'NoOp',
} as const;
export type FilingWebhookResult = (typeof FilingWebhookResult)[keyof typeof FilingWebhookResult];
export declare namespace FilingWebhookResult {
  type Applied = 'Applied';
  type Unmatched = 'Unmatched';
  type NoOp = 'NoOp';
}

/**
 * HooverCanvassingApi.Features.Finance.FinalReportReason
 *
 * Why a report is the committee's FINAL report — the shared "this committee is done filing" signal (GitHub #778). Its presence (a non-null value) IS the flag: a null reason is an ordinary periodic report, exactly as before. The three values are Louisiana Form 102's three mutually-exclusive final-report reasons (CVR cols 22/23/24), and modelling them as one nullable enum rather than a `bool` + a separate reason makes their mutual exclusivity structurally unrepresentable — a report cannot be two of them at once, so no guard is needed.
 *
 * Deliberately a single shared column on `ComplianceReport`, not an LA-only field: no shared final-report concept existed when this landed (VA hard-codes `IsFinalReport=false`; NC/AL have no termination path), and a jurisdiction that only needs the boolean reads `FinalReportReason is not null` without caring which reason it is. The reason→CVR-column mapping is LA-specific and lives in the LA builder. The three reasons happen to be LA's vocabulary today; a jurisdiction with a different one adds its own values here (the mapping stays in its own builder).
 */
export const FinalReportReason = {
  /** The candidate withdrew from the race (LA CVR col 22, `Final_With`). */
  Withdrawn: 'Withdrawn',
  /** The candidate was unopposed (LA CVR col 23, `Final_Unop`). */
  Unopposed: 'Unopposed',
  /** Filed after the election, all loans and debts paid, no surplus funds remaining (LA CVR col 24, `Final_Afte`). */
  FiledAfterElection: 'FiledAfterElection',
} as const;
export type FinalReportReason = (typeof FinalReportReason)[keyof typeof FinalReportReason];
export declare namespace FinalReportReason {
  type Withdrawn = 'Withdrawn';
  type Unopposed = 'Unopposed';
  type FiledAfterElection = 'FiledAfterElection';
}

/**
 * HooverCanvassingApi.Features.Finance.FinanceExtractionStatus
 *
 * CAP-001 — extraction outcome. A FAILED read is a VISIBLE state the treasurer acts on (re-key the fields manually), never a silent empty success. CAP-002 surfaces it as the extract endpoint's `extractionStatus`. Serializes PascalCase on the wire.
 */
export const FinanceExtractionStatus = {
  Succeeded: 'Succeeded',
  Failed: 'Failed',
} as const;
export type FinanceExtractionStatus = (typeof FinanceExtractionStatus)[keyof typeof FinanceExtractionStatus];
export declare namespace FinanceExtractionStatus {
  type Succeeded = 'Succeeded';
  type Failed = 'Failed';
}

/** HooverCanvassingApi.Features.Finance.FlagSeverity */
export const FinanceFlagSeverity = {
  Red: 'Red',
  Yellow: 'Yellow',
  Green: 'Green',
  Blue: 'Blue',
  Neutral: 'Neutral',
} as const;
export type FinanceFlagSeverity = (typeof FinanceFlagSeverity)[keyof typeof FinanceFlagSeverity];
export declare namespace FinanceFlagSeverity {
  type Red = 'Red';
  type Yellow = 'Yellow';
  type Green = 'Green';
  type Blue = 'Blue';
  type Neutral = 'Neutral';
}

/**
 * HooverCanvassingApi.Features.Fundraising.CallTime.FollowUpChannel
 *
 * Which of the phone's composers the draft went to.
 */
export const FollowUpChannel = {
  Text: 'Text',
  Email: 'Email',
} as const;
export type FollowUpChannel = (typeof FollowUpChannel)[keyof typeof FollowUpChannel];
export declare namespace FollowUpChannel {
  type Text = 'Text';
  type Email = 'Email';
}

/**
 * HooverCanvassingApi.Features.Fundraising.CallTime.FollowUpRegister
 *
 * The tone of a follow-up. After an outcome it's chosen by the outcome: committed → thank you; voicemail or no answer → missed you; call back requested or declined → thanks for your time; wrong number and removal requested get none. `FollowUpRegister.DonationLink` is what's offered during the call, before any outcome is logged. The mapping itself lives with the filler in the shared package.
 */
export const FollowUpRegister = {
  /** After a commitment: the amount and the donation link. */
  ThankYou: 'ThankYou',
  /** After a voicemail or no answer: the donation link. */
  MissedYou: 'MissedYou',
  /** After a call-back request or a decline: no link, so it never reads as pushy. */
  ThanksForYourTime: 'ThanksForYourTime',
  /** "Here's the link I mentioned", sent while still on the call. No `{amount}`: nothing has been committed yet, so the donor gives whatever they like through the link. */
  DonationLink: 'DonationLink',
} as const;
export type FollowUpRegister = (typeof FollowUpRegister)[keyof typeof FollowUpRegister];
export declare namespace FollowUpRegister {
  type ThankYou = 'ThankYou';
  type MissedYou = 'MissedYou';
  type ThanksForYourTime = 'ThanksForYourTime';
  type DonationLink = 'DonationLink';
}

/**
 * HooverCanvassingApi.Features.Fundraising.CallTime.FollowUpResult
 *
 * What the phone reported when its composer closed. Only iOS reports sent or cancelled; Android, the `mailto:` fallback and a saved email draft record `FollowUpResult.Unknown`.
 */
export const FollowUpResult = {
  Sent: 'Sent',
  Cancelled: 'Cancelled',
  Unknown: 'Unknown',
} as const;
export type FollowUpResult = (typeof FollowUpResult)[keyof typeof FollowUpResult];
export declare namespace FollowUpResult {
  type Sent = 'Sent';
  type Cancelled = 'Cancelled';
  type Unknown = 'Unknown';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.FormatKind
 *
 * Submission file format produced by an `ISubmissionBuilder`. Concept-named — never state-named. Adding LA's first `Edi` builder must touch zero exhaustive switches anywhere. See the design plan § Submission layer and apps/api/Features/Finance/CLAUDE.md.
 */
export const FormatKind = {
  Edi: 'Edi',
  Xml: 'Xml',
  Xlsx: 'Xlsx',
  Pdf: 'Pdf',
  DosFile: 'DosFile',
  CometXml: 'CometXml',
  PortalPdf: 'PortalPdf',
  Csv: 'Csv',
  /** The FEC's proprietary ASCII-28-delimited `.fec` flat file (federal Form 3). System-named for a single-system proprietary format, following the `FormatKind.CometXml` / `FormatKind.DosFile` precedent. Dispatched by the tuple `("federal", FecFile)` (ADR-003); see `Reporting/Federal/FecFile/`. */
  FecFile: 'FecFile',
  /** Louisiana's LEADERS `.PDP` (PDSERF/Plus) delimited, line-oriented flat file — the first non-XML/EDI/`.fec` output format. Concept-named for a single-system format, following the `FormatKind.CometXml` / `FormatKind.FecFile` precedent (ADR-003 forbids a state-named member). Dispatched by the tuple `("LA", LeadersPdp)`; see `Reporting/Louisiana/Pdp/`. Mirrored in `packages/shared`'s `FormatKind`. */
  LeadersPdp: 'LeadersPdp',
  /** North Carolina's CFRS `.cfd` import file — a relational, Row-ID-keyed, section-delimited flat file (each `[SECTION][N]` block carries tilde-prefixed rows). Concept-named for a single-system format, following the `FormatKind.CometXml` / `FormatKind.FecFile` / `FormatKind.LeadersPdp` precedent (ADR-003 forbids a state-named member). Dispatched by the tuple `("NC", CfrsCfd)`; see `Reporting/NorthCarolina/CfrsCfd/`. Mirrored in `packages/shared`'s `FormatKind`. */
  CfrsCfd: 'CfrsCfd',
} as const;
export type FormatKind = (typeof FormatKind)[keyof typeof FormatKind];
export declare namespace FormatKind {
  type Edi = 'Edi';
  type Xml = 'Xml';
  type Xlsx = 'Xlsx';
  type Pdf = 'Pdf';
  type DosFile = 'DosFile';
  type CometXml = 'CometXml';
  type PortalPdf = 'PortalPdf';
  type Csv = 'Csv';
  type FecFile = 'FecFile';
  type LeadersPdp = 'LeadersPdp';
  type CfrsCfd = 'CfrsCfd';
}

/**
 * HooverCanvassingApi.Features.Finance.FreezeReason
 *
 * Why a money row cannot be edited, in the order a refusal names them.
 */
export const FreezeReason = {
  /** The row's period is calendar-locked (ADR-019), and no audited unlock reopened it. */
  CalendarLocked: 'CalendarLocked',
  Voided: 'Voided',
  /** The row is already on the append-only state portal (ADR-020) and its period was not reopened by an audited unlock. */
  UploadedToPortal: 'UploadedToPortal',
} as const;
export type FreezeReason = (typeof FreezeReason)[keyof typeof FreezeReason];
export declare namespace FreezeReason {
  type CalendarLocked = 'CalendarLocked';
  type Voided = 'Voided';
  type UploadedToPortal = 'UploadedToPortal';
}

/**
 * HooverCanvassingApi.Features.Auth.GetStartedNextStep
 *
 * Where the web takes the person after sign-up.
 */
export const GetStartedNextStep = {
  Onboarding: 'Onboarding',
  ExpressTextSend: 'ExpressTextSend',
} as const;
export type GetStartedNextStep = (typeof GetStartedNextStep)[keyof typeof GetStartedNextStep];
export declare namespace GetStartedNextStep {
  type Onboarding = 'Onboarding';
  type ExpressTextSend = 'ExpressTextSend';
}

/** HooverCanvassingApi.Features.RacePicture.CampaignLink.GoalOutside */
export const GoalOutside = {
  High: 'High',
  Low: 'Low',
} as const;
export type GoalOutside = (typeof GoalOutside)[keyof typeof GoalOutside];
export declare namespace GoalOutside {
  type High = 'High';
  type Low = 'Low';
}

/** HooverCanvassingApi.Features.Walkbooks.HouseStatus */
export const HouseStatus = {
  NotVisited: 'NotVisited',
  Visited: 'Visited',
  NotHome: 'NotHome',
  NeedsFollowUp: 'NeedsFollowUp',
} as const;
export type HouseStatus = (typeof HouseStatus)[keyof typeof HouseStatus];
export declare namespace HouseStatus {
  type NotVisited = 'NotVisited';
  type Visited = 'Visited';
  type NotHome = 'NotHome';
  type NeedsFollowUp = 'NeedsFollowUp';
}

/**
 * HooverCanvassingApi.Features.VoterData.HouseholdMethod
 *
 * How the builder predicts a segment's households from its base voters V, households H and the filtered voter share p, before L2 answers.
 */
export const HouseholdMethod = {
  /** H × p. Fits traits a household shares, such as home ownership. */
  Scaling: 'Scaling',
  /** H × (1 − (1 − p)^(V/H)). Fits traits of a person, such as age or party. */
  HouseholdSize: 'HouseholdSize',
} as const;
export type HouseholdMethod = (typeof HouseholdMethod)[keyof typeof HouseholdMethod];
export declare namespace HouseholdMethod {
  type Scaling = 'Scaling';
  type HouseholdSize = 'HouseholdSize';
}

/**
 * HooverCanvassingApi.Features.Finance.ImportBatchStatus
 *
 * Reversibility state of an ImportBatch. Superseded is reserved for the v1.5 re-parse flow — no v1 code path produces it, but the enum value exists now so v1.5 does not need a schema migration.
 */
export const ImportBatchStatus = {
  Active: 'Active',
  Undone: 'Undone',
  Superseded: 'Superseded',
} as const;
export type ImportBatchStatus = (typeof ImportBatchStatus)[keyof typeof ImportBatchStatus];
export declare namespace ImportBatchStatus {
  type Active = 'Active';
  type Undone = 'Undone';
  type Superseded = 'Superseded';
}

/** HooverCanvassingApi.Features.Voters.ImportJobStatus */
export const ImportJobStatus = {
  Pending: 'Pending',
  Running: 'Running',
  Completed: 'Completed',
  Failed: 'Failed',
  Cancelled: 'Cancelled',
} as const;
export type ImportJobStatus = (typeof ImportJobStatus)[keyof typeof ImportJobStatus];
export declare namespace ImportJobStatus {
  type Pending = 'Pending';
  type Running = 'Running';
  type Completed = 'Completed';
  type Failed = 'Failed';
  type Cancelled = 'Cancelled';
}

/** HooverCanvassingApi.Features.Voters.ImportRowStatus */
export const ImportRowStatus = {
  Imported: 'Imported',
  Skipped: 'Skipped',
  Error: 'Error',
  Linked: 'Linked',
} as const;
export type ImportRowStatus = (typeof ImportRowStatus)[keyof typeof ImportRowStatus];
export declare namespace ImportRowStatus {
  type Imported = 'Imported';
  type Skipped = 'Skipped';
  type Error = 'Error';
  type Linked = 'Linked';
}

/**
 * HooverCanvassingApi.Features.Finance.InKindDonorBasis
 *
 * Which donor type an in-kind gift meters as for its contribution LIMIT, declared per ruleset (ADR-078 policy shape). Source bans always judge goods by the giver.
 */
export const InKindDonorBasis = {
  /** Every in-kind gift prices on the individual limit, whoever gave it. The mapping every ruleset had before the policy existed. */
  IndividualBucket: 'IndividualBucket',
  /** An in-kind gift prices on the contributor's own limit, as money from them would. */
  ContributorSubtype: 'ContributorSubtype',
} as const;
export type InKindDonorBasis = (typeof InKindDonorBasis)[keyof typeof InKindDonorBasis];
export declare namespace InKindDonorBasis {
  type IndividualBucket = 'IndividualBucket';
  type ContributorSubtype = 'ContributorSubtype';
}

/**
 * HooverCanvassingApi.Features.Finance.InKindNature
 *
 * Structured classifier for in-kind contributions. Required by AL EDI (cbInKindNature); other states ignore it and use InKindDescription free text.
 */
export const InKindNature = {
  Administrative: 'Administrative',
  Advertising: 'Advertising',
  Consulting: 'Consulting',
  Equipment: 'Equipment',
  Food: 'Food',
  Rent: 'Rent',
  Transportation: 'Transportation',
  Other: 'Other',
} as const;
export type InKindNature = (typeof InKindNature)[keyof typeof InKindNature];
export declare namespace InKindNature {
  type Administrative = 'Administrative';
  type Advertising = 'Advertising';
  type Consulting = 'Consulting';
  type Equipment = 'Equipment';
  type Food = 'Food';
  type Rent = 'Rent';
  type Transportation = 'Transportation';
  type Other = 'Other';
}

/**
 * HooverCanvassingApi.Features.Finance.ItemizationEntryBasis
 *
 * What the classify-time itemization verdict compares against the threshold.
 */
export const ItemizationEntryBasis = {
  /** The single gift. */
  Gift: 'Gift',
  /** The donor's aggregate over the itemization window. */
  Aggregate: 'Aggregate',
  /** The donor's aggregate over the itemization window, within the gift's election. */
  PerElectionAggregate: 'PerElectionAggregate',
} as const;
export type ItemizationEntryBasis = (typeof ItemizationEntryBasis)[keyof typeof ItemizationEntryBasis];
export declare namespace ItemizationEntryBasis {
  type Gift = 'Gift';
  type Aggregate = 'Aggregate';
  type PerElectionAggregate = 'PerElectionAggregate';
}

/**
 * HooverCanvassingApi.Features.Finance.ItemizationFloorScope
 *
 * Which receipts the itemization threshold binds.
 */
export const ItemizationFloorScope = {
  /** Every contributor itemizes only over the threshold. */
  EveryContributor: 'EveryContributor',
  /** Only an FEC line 11a receipt (individuals and other organizations) waits for the threshold; a party committee, a PAC and an authorized committee's transfer itemize at any amount. */
  FecLine11aOnly: 'FecLine11aOnly',
} as const;
export type ItemizationFloorScope = (typeof ItemizationFloorScope)[keyof typeof ItemizationFloorScope];
export declare namespace ItemizationFloorScope {
  type EveryContributor = 'EveryContributor';
  type FecLine11aOnly = 'FecLine11aOnly';
}

/**
 * HooverCanvassingApi.Features.Finance.ItemizationThresholdBoundary
 *
 * Whether a basis exactly at the itemization threshold is itemized.
 */
export const ItemizationThresholdBoundary = {
  /** A basis at or over the threshold is itemized. */
  Inclusive: 'Inclusive',
  /** Only a basis over the threshold is itemized. */
  Exclusive: 'Exclusive',
} as const;
export type ItemizationThresholdBoundary = (typeof ItemizationThresholdBoundary)[keyof typeof ItemizationThresholdBoundary];
export declare namespace ItemizationThresholdBoundary {
  type Inclusive = 'Inclusive';
  type Exclusive = 'Exclusive';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.ItemizationWindow
 *
 * FEC-003 (D1/D9) / #424 — the itemization aggregation window. Federal authorized committees aggregate per election cycle (11 CFR 104.3(a)); Alabama (and the calendar-year states) aggregate per calendar year. Only the window varies per jurisdiction — the basis (per-donor for receipts, per-payee for disbursements) does not — so there is intentionally NO `ItemizationBasis` enum, just this 2-value window. Promoted from a code-level string-compare lookup to a declared `RulesetConfig.ItemizationWindow` policy resolved through `ItemizationWindowResolver` (#424), so the early-warning ledger and the filing path read the SAME window and can never disagree on who is itemized. Serialized as a string in ruleset JSON.
 */
export const ItemizationWindow = {
  CalendarYear: 'CalendarYear',
  ElectionCycle: 'ElectionCycle',
} as const;
export type ItemizationWindow = (typeof ItemizationWindow)[keyof typeof ItemizationWindow];
export declare namespace ItemizationWindow {
  type CalendarYear = 'CalendarYear';
  type ElectionCycle = 'ElectionCycle';
}

/**
 * HooverCanvassingApi.Features.Finance.JointCandidatesCommitteePolicy
 *
 * See `RulesetConfig.JointCandidatesCommittees`.
 */
export const JointCandidatesCommitteePolicy = {
  /** The jurisdiction has no joint candidates committee; setup asks for no committee type. */
  NotOffered: 'NotOffered',
  /** Setup requires the committee type. A joint candidates committee may accept from each source the single-candidate cap times its number of candidates. */
  LimitPerCandidate: 'LimitPerCandidate',
} as const;
export type JointCandidatesCommitteePolicy = (typeof JointCandidatesCommitteePolicy)[keyof typeof JointCandidatesCommitteePolicy];
export declare namespace JointCandidatesCommitteePolicy {
  type NotOffered = 'NotOffered';
  type LimitPerCandidate = 'LimitPerCandidate';
}

/** HooverCanvassingApi.Features.VoterData.L2FailureKind */
export const L2FailureKind = {
  /** The platform account or the subsidiary's app isn't configured. No call was made. */
  NotConfigured: 'NotConfigured',
  /** L2 answered `result: "fail"` — a bad column or value. Usually inside an HTTP 200. */
  Rejected: 'Rejected',
  /** L2 couldn't be reached, answered an HTTP error, or sent a body we can't read. */
  Unavailable: 'Unavailable',
  /** An export file larger than the caller's limit; it was refused or cut off, never kept whole. */
  TooLarge: 'TooLarge',
} as const;
export type L2FailureKind = (typeof L2FailureKind)[keyof typeof L2FailureKind];
export declare namespace L2FailureKind {
  type NotConfigured = 'NotConfigured';
  type Rejected = 'Rejected';
  type Unavailable = 'Unavailable';
  type TooLarge = 'TooLarge';
}

/**
 * HooverCanvassingApi.Features.VoterData.L2Priority
 *
 * Who gets the next free L2 call. Lower goes first.
 */
export const L2Priority = {
  /** A customer buying: the exact count and price behind an order. */
  OrderQuote: 'OrderQuote',
  /** The builder's count for the segment on screen. */
  LiveCount: 'LiveCount',
  /** Detail loaded on demand: a rule's other options, an election's vote counts. */
  Detail: 'Detail',
  /** The export driver buying a paid order's records (ADR-096): it waits behind every count, so the builder stays responsive. Must stay last: the call queue's lanes are indexed by ordinal. */
  Export: 'Export',
} as const;
export type L2Priority = (typeof L2Priority)[keyof typeof L2Priority];
export declare namespace L2Priority {
  type OrderQuote = 'OrderQuote';
  type LiveCount = 'LiveCount';
  type Detail = 'Detail';
  type Export = 'Export';
}

/**
 * HooverCanvassingApi.Features.Finance.LedgerStatus
 *
 * Reversibility-aware status on Contribution and Expenditure. Pending = imported but awaiting treasurer match-and-split. Confirmed = eligible for inclusion in any state/federal filing. Reported = on a filed SubmissionPackage and locked from further mutation (BSW-002 — the contribution-side equivalent of the legacy `TransactionStatus.Reported` guard). Voided = the money row was cancelled (a treasurer Void — it happened but is called off); the row is retained for audit, excluded from every sum, and never surfaces in itemized queries. Reversible via un-void (ADR-016).
 */
export const LedgerStatus = {
  Pending: 'Pending',
  /**
   * Eligible for inclusion in any state/federal filing.
   *
   * Semantic shifted with ADR-011 / USI-006. Pre-USI-006 it meant "matched to a payout via the deleted reconciliation-match service"; post-USI-006 it means "treasurer reviewed and approved for filing." The state machine is unchanged at the schema level — only the meaning of Confirmed changed. The standalone `IContributionConfirmService` behind `POST /api/finance/contributions/{id}/confirm` is the ONLY Pending → Confirmed flip path post-ADR-011. Filings still flip Confirmed → Reported in `SubmissionPackageService.FileReportAsync`; Void cascades still apply. The next reader grepping for `Confirmed,` needs this comment on the literal — moving it to the type-level xmldoc hides the load-bearing meaning shift from a cold reader.
   */
  Confirmed: 'Confirmed',
  Reported: 'Reported',
  Voided: 'Voided',
} as const;
export type LedgerStatus = (typeof LedgerStatus)[keyof typeof LedgerStatus];
export declare namespace LedgerStatus {
  type Pending = 'Pending';
  type Confirmed = 'Confirmed';
  type Reported = 'Reported';
  type Voided = 'Voided';
}

/**
 * HooverCanvassingApi.Features.Legal.LegalAcceptanceSource
 *
 * Where a `UserLegalAcceptance` row came from. `Backfill` is reserved for the Sequence-0 grandfather rollout; `AdminOverride` for platform-admin inserts (which also fill `ActingAdmin*` columns).
 */
export const LegalAcceptanceSource = {
  Mobile: 'Mobile',
  Web: 'Web',
  Backfill: 'Backfill',
  AdminOverride: 'AdminOverride',
} as const;
export type LegalAcceptanceSource = (typeof LegalAcceptanceSource)[keyof typeof LegalAcceptanceSource];
export declare namespace LegalAcceptanceSource {
  type Mobile = 'Mobile';
  type Web = 'Web';
  type Backfill = 'Backfill';
  type AdminOverride = 'AdminOverride';
}

/**
 * HooverCanvassingApi.Features.Legal.LegalDocumentType
 *
 * A versioned legal document. A withdrawal is not one of these — it is a different event kind on `UserLegalAcceptance`, tracked via `LegalEventKind`.
 */
export const LegalDocumentType = {
  Eula: 'Eula',
  PrivacyPolicy: 'PrivacyPolicy',
  TermsOfService: 'TermsOfService',
} as const;
export type LegalDocumentType = (typeof LegalDocumentType)[keyof typeof LegalDocumentType];
export declare namespace LegalDocumentType {
  type Eula = 'Eula';
  type PrivacyPolicy = 'PrivacyPolicy';
  type TermsOfService = 'TermsOfService';
}

/**
 * HooverCanvassingApi.Features.Legal.LegalEventKind
 *
 * What a `UserLegalAcceptance` row records. Splits withdrawal from acceptance so a withdrawal doesn't have to masquerade as a "ConsentWithdrawal document type" — the design doc's §2.2.2 wording that model deliberately breaks up.
 *
 * Invariants (enforced at the write layer, not the DB): - `Acceptance` rows: `DocumentType` and `VersionSequence` are both set. - `Withdrawal` rows: both are null. The gate ignores them; reporting joins on them.
 */
export const LegalEventKind = {
  Acceptance: 'Acceptance',
  Withdrawal: 'Withdrawal',
} as const;
export type LegalEventKind = (typeof LegalEventKind)[keyof typeof LegalEventKind];
export declare namespace LegalEventKind {
  type Acceptance = 'Acceptance';
  type Withdrawal = 'Withdrawal';
}

/**
 * HooverCanvassingApi.Features.Legal.LegalPublishNotificationStatus
 *
 * Lifecycle of a per-publish notification job. Never resurrected once terminal.
 */
export const LegalPublishNotificationStatus = {
  Pending: 'Pending',
  Sending: 'Sending',
  Sent: 'Sent',
  Failed: 'Failed',
} as const;
export type LegalPublishNotificationStatus = (typeof LegalPublishNotificationStatus)[keyof typeof LegalPublishNotificationStatus];
export declare namespace LegalPublishNotificationStatus {
  type Pending = 'Pending';
  type Sending = 'Sending';
  type Sent = 'Sent';
  type Failed = 'Failed';
}

/**
 * HooverCanvassingApi.Features.Legal.LegalPublishReason
 *
 * Why a `LegalDocumentVersion` was published. Decides `RequiresReconsent` at publish time: `Material` and `Rollback` wall users on earlier versions; `NonMaterial` advances the visible text without walling anyone (§3.4.1).
 */
export const LegalPublishReason = {
  Material: 'Material',
  NonMaterial: 'NonMaterial',
  Rollback: 'Rollback',
} as const;
export type LegalPublishReason = (typeof LegalPublishReason)[keyof typeof LegalPublishReason];
export declare namespace LegalPublishReason {
  type Material = 'Material';
  type NonMaterial = 'NonMaterial';
  type Rollback = 'Rollback';
}

/**
 * HooverCanvassingApi.Features.Finance.LimitVersionBasis
 *
 * Which date picks the ruleset version a contribution's limit is priced on.
 */
export const LimitVersionBasis = {
  /** The version in force on the campaign's today. */
  EvaluationDate: 'EvaluationDate',
  /** The version in force on the contribution's own date. */
  GiftDate: 'GiftDate',
} as const;
export type LimitVersionBasis = (typeof LimitVersionBasis)[keyof typeof LimitVersionBasis];
export declare namespace LimitVersionBasis {
  type EvaluationDate = 'EvaluationDate';
  type GiftDate = 'GiftDate';
}

/**
 * HooverCanvassingApi.Features.VoterData.ListPurpose
 *
 * What a Voter data list is for. It will set defaults and the columns delivered.
 */
export const ListPurpose = {
  Doors: 'Doors',
  Phones: 'Phones',
  Mail: 'Mail',
  Turnout: 'Turnout',
} as const;
export type ListPurpose = (typeof ListPurpose)[keyof typeof ListPurpose];
export declare namespace ListPurpose {
  type Doors = 'Doors';
  type Phones = 'Phones';
  type Mail = 'Mail';
  type Turnout = 'Turnout';
}

/**
 * HooverCanvassingApi.Features.Finance.ManualDeadlineError
 *
 * Why a manual reporting period was rejected. Maps to a 400 error code at the edge.
 */
export const ManualDeadlineError = {
  /** A required field (name) is blank. */
  MissingName: 'MissingName',
  /** PeriodStart is after PeriodEnd, or the due date falls before the period closes. */
  InvalidRange: 'InvalidRange',
  /** The window overlaps an existing deadline's coverage period. */
  Overlap: 'Overlap',
  /** The window leaves a gap after the immediately-prior period (not contiguous). */
  NonContiguous: 'NonContiguous',
} as const;
export type ManualDeadlineError = (typeof ManualDeadlineError)[keyof typeof ManualDeadlineError];
export declare namespace ManualDeadlineError {
  type MissingName = 'MissingName';
  type InvalidRange = 'InvalidRange';
  type Overlap = 'Overlap';
  type NonContiguous = 'NonContiguous';
}

/**
 * HooverCanvassingApi.Features.Finance.ManualDeadlineWarning
 *
 * A non-blocking advisory on an otherwise-successful manual reporting-period create. The period IS created; the treasurer is merely informed. (Warn-don't-block: the valid report-code universe is jurisdiction-published and evolves, so a code we don't recognize may still be legitimate — we never trap a filing.)
 */
export const ManualDeadlineWarning = {
  /** The entered report code isn't in the jurisdiction's recognized set. Still accepted — the treasurer should double-check it against their filing calendar before filing. */
  UnrecognizedReportCode: 'UnrecognizedReportCode',
} as const;
export type ManualDeadlineWarning = (typeof ManualDeadlineWarning)[keyof typeof ManualDeadlineWarning];
export declare namespace ManualDeadlineWarning {
  type UnrecognizedReportCode = 'UnrecognizedReportCode';
}

/** HooverCanvassingApi.Features.Finance.MappingConfidence */
export const MappingConfidence = {
  None: 'None',
  Medium: 'Medium',
  High: 'High',
} as const;
export type MappingConfidence = (typeof MappingConfidence)[keyof typeof MappingConfidence];
export declare namespace MappingConfidence {
  type None = 'None';
  type Medium = 'Medium';
  type High = 'High';
}

/**
 * HooverCanvassingApi.Features.Fundraising.Matching.MatchDecidedBy
 *
 * Which stage settled a candidate's disposition. For logging and the run summary — never shown as a score.
 */
export const MatchDecidedBy = {
  /** The in-process rule pass on the person's `(zip5, last_core)` bucket. */
  Rule: 'Rule',
  /** The judge's probability, banded. */
  Judge: 'Judge',
  /** The surname + state fallback's house/city corroboration. */
  Fallback: 'Fallback',
  /** Suppressed with no judge call (a different given name, or nothing worth judging). */
  NotJudged: 'NotJudged',
} as const;
export type MatchDecidedBy = (typeof MatchDecidedBy)[keyof typeof MatchDecidedBy];
export declare namespace MatchDecidedBy {
  type Rule = 'Rule';
  type Judge = 'Judge';
  type Fallback = 'Fallback';
  type NotJudged = 'NotJudged';
}

/**
 * HooverCanvassingApi.Features.Fundraising.Enrichment.MatchDisposition
 *
 * Which surface a `Match` meets — the disposition band the matcher (Slice 2) produced. It is not a score: it selects where a match is shown, exactly as the file-type detector's `High | Low | None` does. The whole dispositioned set is persisted, `MatchDisposition.Suppress` included, because a stored accept verdict can later promote a now-suppressed candidate back into the accepted union, and inheritance needs the whole blocked set to compare against.
 */
export const MatchDisposition = {
  /** Score decisive — accepted automatically (still held to the household boundary). */
  AutoAccept: 'AutoAccept',
  /** Ambiguous — queued for a staffer to adjudicate in Match Review. */
  Review: 'Review',
  /** Below review — not shown as fact and not queued; persisted only so a stored accept verdict can promote it back into the union at read time. */
  Suppress: 'Suppress',
} as const;
export type MatchDisposition = (typeof MatchDisposition)[keyof typeof MatchDisposition];
export declare namespace MatchDisposition {
  type AutoAccept = 'AutoAccept';
  type Review = 'Review';
  type Suppress = 'Suppress';
}

/**
 * HooverCanvassingApi.Features.Fundraising.Matching.MatchStage
 *
 * The matcher's four stages, in the order it runs them.
 */
export const MatchStage = {
  /** The corpus read on every person's `(zip5, last_core)`. */
  Searching: 'Searching',
  /** The in-process rule pass. */
  ExactMatch: 'ExactMatch',
  /** The surname + state fallback, for people the rule pass left unmatched, such as people who moved or have no ZIP. */
  Recovering: 'Recovering',
  /** The judge, on the unclear pairs only. */
  Judging: 'Judging',
} as const;
export type MatchStage = (typeof MatchStage)[keyof typeof MatchStage];
export declare namespace MatchStage {
  type Searching = 'Searching';
  type ExactMatch = 'ExactMatch';
  type Recovering = 'Recovering';
  type Judging = 'Judging';
}

/**
 * HooverCanvassingApi.Features.Fundraising.Enrichment.MatchVerdictDecision
 *
 * A Fundraiser's answer to "Is this them?".
 */
export const MatchVerdictDecision = {
  /** "Yes, that's them" — their giving counts toward the record and the Ask. */
  Accept: 'Accept',
  /** "Not them" — dropped for good; their giving never sizes an Ask. */
  Reject: 'Reject',
} as const;
export type MatchVerdictDecision = (typeof MatchVerdictDecision)[keyof typeof MatchVerdictDecision];
export declare namespace MatchVerdictDecision {
  type Accept = 'Accept';
  type Reject = 'Reject';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.Maryland.Changeset.MdChangesetAction
 *
 * The per-row changeset action MDCRIS acts on (ADR-073). Add/Amend/Delete only — the file format also defines `Edit`, but v1 never emits it (an Amend supersedes the prior values idempotently by Transaction ID, so a distinct Edit adds no capability). A removed filed row uses `Delete` (the interim default per the #581 SBE-probe finding). Rendered to the workbook's `Actions` column via `MdChangesetActionLabels`.
 */
export const MdChangesetAction = {
  Add: 'Add',
  Amend: 'Amend',
  Delete: 'Delete',
} as const;
export type MdChangesetAction = (typeof MdChangesetAction)[keyof typeof MdChangesetAction];
export declare namespace MdChangesetAction {
  type Add = 'Add';
  type Amend = 'Amend';
  type Delete = 'Delete';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.Maryland.Changeset.MdEmittedRowKind
 *
 * Discriminator for a Maryland MDCRIS emitted row's transaction — the template/sub-sheet it files on (ADR-073). One entity's GUID is its Transaction ID across every kind, so this routes a row (and, crucially, a `Delete`) to the correct sheet. `Forgiven` is deliberately absent — the Debt forgiveness sub-sheet is deferred in v1. The domain concept exists (`Debt.SettlementKind`, ADR-031 amendment 2026-09-08); the Maryland sheet that would carry it does not.
 */
export const MdEmittedRowKind = {
  Contribution: 'Contribution',
  Expenditure: 'Expenditure',
  OutstandingObligation: 'OutstandingObligation',
  Return: 'Return',
  Payment: 'Payment',
  ExpenditureReturn: 'ExpenditureReturn',
} as const;
export type MdEmittedRowKind = (typeof MdEmittedRowKind)[keyof typeof MdEmittedRowKind];
export declare namespace MdEmittedRowKind {
  type Contribution = 'Contribution';
  type Expenditure = 'Expenditure';
  type OutstandingObligation = 'OutstandingObligation';
  type Return = 'Return';
  type Payment = 'Payment';
  type ExpenditureReturn = 'ExpenditureReturn';
}

/**
 * HooverCanvassingApi.Features.Messaging.MessagingProvisioningStatus
 *
 * The status spine of the disposable Messaging Provisioning half of a `CampaignMessagingProfile` — the record an operator drives by hand through an outside 10DLC/TCR vendor (there is no vendor/TCR API). Stored as a PascalCase string column (mirrors the Finance / Work Orders enum convention) so the values stay stable across the API and the shared TS mirror. Happy path: `NotStarted → InfoCollected → SubmittedToVendor → Provisioned`. Terminal off-ramps: `Rejected` (the vendor declined) / `Failed` (the provisioning attempt broke down and will not be retried on this record). This is deliberately NOT the portable Campaign Verify pass — that pass lives on the same profile as plain scalar fields and has no spine (it is a fact plus an expiry, not a workflow).
 */
export const MessagingProvisioningStatus = {
  /** The profile exists but no provisioning work has begun. The create state. */
  NotStarted: 'NotStarted',
  /** The minimal use-case fields the vendor needs have been captured. */
  InfoCollected: 'InfoCollected',
  /** The operator handed the use-case package to the vendor by hand. */
  SubmittedToVendor: 'SubmittedToVendor',
  /** The vendor returned the 10DLC/TCR brand + campaign IDs; provisioning is live. */
  Provisioned: 'Provisioned',
  /** Terminal — the vendor declined the registration. */
  Rejected: 'Rejected',
  /** Terminal — the provisioning attempt broke down and was abandoned. */
  Failed: 'Failed',
} as const;
export type MessagingProvisioningStatus = (typeof MessagingProvisioningStatus)[keyof typeof MessagingProvisioningStatus];
export declare namespace MessagingProvisioningStatus {
  type NotStarted = 'NotStarted';
  type InfoCollected = 'InfoCollected';
  type SubmittedToVendor = 'SubmittedToVendor';
  type Provisioned = 'Provisioned';
  type Rejected = 'Rejected';
  type Failed = 'Failed';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.MissingDonorField
 *
 * The itemization fields a donor can be missing for filing. Lives here (the shared compliance-primitive home) rather than in any one feature so both the sort inbox and the Contacts surface reference the same wire enum. PascalCase on the wire (`project_wire_enum_pascalcase_discriminators`) — the FE matches these tokens directly. Below the itemization threshold a donor is aggregated (not itemized), so a placeholder name / blank address is fine. A row carries a set of these, so it can say several fields are missing at once.
 */
export const MissingDonorField = {
  Occupation: 'Occupation',
  Employer: 'Employer',
  Address: 'Address',
  /** The donor's name is blank, or still the import placeholder ("Unknown donor", "Unknown payee"). */
  Name: 'Name',
  EmployerAddress: 'EmployerAddress',
  /** An occupation on file that the ruleset rejects as too vague to describe a livelihood (`ContributorDisclosurePolicy.InsufficientOccupations`). */
  VagueOccupation: 'VagueOccupation',
} as const;
export type MissingDonorField = (typeof MissingDonorField)[keyof typeof MissingDonorField];
export declare namespace MissingDonorField {
  type Occupation = 'Occupation';
  type Employer = 'Employer';
  type Address = 'Address';
  type Name = 'Name';
  type EmployerAddress = 'EmployerAddress';
  type VagueOccupation = 'VagueOccupation';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.Mississippi.CoverPdf.MississippiCoverReportTypeBox
 *
 * One "TYPE OF REPORT" box on the Mississippi county-tier cover. The county forms carry a dated Pre-Election, Pre-Runoff and Annual row (each with an explicit covered date range) plus a Termination row; the filer marks exactly one. (Statewide/legislative periodic reports and the 48-hour notice have no box on the county form — those resolve to `null` and the builder leaves the group unmarked rather than guessing, as the North Carolina cover does.)
 */
export const MississippiCoverReportTypeBox = {
  PreElection: 'PreElection',
  PreRunoff: 'PreRunoff',
  Annual: 'Annual',
  Termination: 'Termination',
} as const;
export type MississippiCoverReportTypeBox = (typeof MississippiCoverReportTypeBox)[keyof typeof MississippiCoverReportTypeBox];
export declare namespace MississippiCoverReportTypeBox {
  type PreElection = 'PreElection';
  type PreRunoff = 'PreRunoff';
  type Annual = 'Annual';
  type Termination = 'Termination';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.Mississippi.CoverReview.MississippiCoverReviewFailure
 *
 * What went wrong, so the controller can pick the HTTP status.
 */
export const MississippiCoverReviewFailure = {
  NotFound: 'NotFound',
  Invalid: 'Invalid',
  Conflict: 'Conflict',
} as const;
export type MississippiCoverReviewFailure = (typeof MississippiCoverReviewFailure)[keyof typeof MississippiCoverReviewFailure];
export declare namespace MississippiCoverReviewFailure {
  type NotFound = 'NotFound';
  type Invalid = 'Invalid';
  type Conflict = 'Conflict';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.Mississippi.CoverPdf.MississippiCoverRole
 *
 * The Mississippi filer role — which of the three v1 official cover forms a report files. Mississippi publishes a separate form per role with identical data grids and different headers/signers, so the role selects the vendored template (its printed title) and the certification signer (the candidate on individual forms, the director or treasurer on committee forms — never both).
 */
export const MississippiCoverRole = {
  /** An individual candidate filing under their own name ("Signature of Candidate"). */
  IndividualCandidate: 'IndividualCandidate',
  /** A candidate's authorized committee ("Signature of Director or Treasurer"). */
  CandidateCommittee: 'CandidateCommittee',
  /** A political committee / PAC, not tied to a candidate ("Signature of Director or Treasurer"). */
  PoliticalCommittee: 'PoliticalCommittee',
} as const;
export type MississippiCoverRole = (typeof MississippiCoverRole)[keyof typeof MississippiCoverRole];
export declare namespace MississippiCoverRole {
  type IndividualCandidate = 'IndividualCandidate';
  type CandidateCommittee = 'CandidateCommittee';
  type PoliticalCommittee = 'PoliticalCommittee';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.Mississippi.CoverPdf.MississippiCoverVariant
 *
 * Which FAMILY of Mississippi cover form a report files. Mississippi publishes a separate cover per filer `MississippiCoverRole` AND a separate set of those forms for judicial seats (a two-page layout with a split Work/Home telephone, an Office Sought field, and a judicial reporting schedule). A judicial candidate and a county candidate resolve to the SAME role, so the judicial-vs- standard difference is a distinct axis on the template table — never an overload of the role enum (the CLAUDE.md field-difference note: a judicial role declares its own field set, it does not silently reuse the county one).
 */
export const MississippiCoverVariant = {
  /** The county-tier forms (SOS 04-2022) — one page, single Telephone, no Office Sought. */
  Standard: 'Standard',
  /** The judicial forms — two pages, split Work/Home telephone, Office Sought, judicial schedule. */
  Judicial: 'Judicial',
} as const;
export type MississippiCoverVariant = (typeof MississippiCoverVariant)[keyof typeof MississippiCoverVariant];
export declare namespace MississippiCoverVariant {
  type Standard = 'Standard';
  type Judicial = 'Judicial';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.Mississippi.CoverPdf.MississippiJudicialReportTypeBox
 *
 * One "TYPE OF REPORT" box on the Mississippi JUDICIAL cover. The judicial reporting schedule (Miss. Code Ann. § 23-15-807) is a delta schedule — five fixed-calendar periodic windows plus the election-relative pre-election and pre-runoff reports — so, unlike the county form's four boxes, the judicial form carries eight, and the five periodics share the report-type code `PER` and are distinguished only by their coverage PERIOD.
 */
export const MississippiJudicialReportTypeBox = {
  /** May 8 periodic — covers Jan 1 through Apr 30. */
  May8Periodic: 'May8Periodic',
  /** June 10 periodic — covers May 1 through May 31. */
  June10Periodic: 'June10Periodic',
  /** July 10 periodic — covers Jun 1 through Jun 30. */
  July10Periodic: 'July10Periodic',
  /** October 9 periodic — covers Jul 1 through Sep 30. */
  October9Periodic: 'October9Periodic',
  /** October 27 pre-election — covers Oct 1 through Oct 22. */
  October27PreElection: 'October27PreElection',
  /** November 24 pre-runoff — covers Oct 23 through Nov 19. */
  November24PreRunoff: 'November24PreRunoff',
  /** January 9 periodic — covers Oct 1 through Dec 31 (the year-end periodic). */
  January9Periodic: 'January9Periodic',
  /** Termination report. */
  Termination: 'Termination',
} as const;
export type MississippiJudicialReportTypeBox = (typeof MississippiJudicialReportTypeBox)[keyof typeof MississippiJudicialReportTypeBox];
export declare namespace MississippiJudicialReportTypeBox {
  type May8Periodic = 'May8Periodic';
  type June10Periodic = 'June10Periodic';
  type July10Periodic = 'July10Periodic';
  type October9Periodic = 'October9Periodic';
  type October27PreElection = 'October27PreElection';
  type November24PreRunoff = 'November24PreRunoff';
  type January9Periodic = 'January9Periodic';
  type Termination = 'Termination';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.Mississippi.Schedules.MississippiReceiptDestination
 *
 * Which itemized schedule (if any) a receipt of a given `ReceiptNature` rides.
 */
export const MississippiReceiptDestination = {
  /** The Itemized Receipts schedule (cash / other monetary receipts + loans). */
  CashReceipts: 'CashReceipts',
  /** The Itemized Receipts – In-Kind schedule (its own schedule, excluded from cover totals). */
  InKind: 'InKind',
  /** Not emitted on any Mississippi schedule — a receipt regime outside Mississippi's vocabulary. */
  NotEmitted: 'NotEmitted',
} as const;
export type MississippiReceiptDestination = (typeof MississippiReceiptDestination)[keyof typeof MississippiReceiptDestination];
export declare namespace MississippiReceiptDestination {
  type CashReceipts = 'CashReceipts';
  type InKind = 'InKind';
  type NotEmitted = 'NotEmitted';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.Mississippi.Schedules.MississippiScheduleKind
 *
 * The three Mississippi attachment schedules that ride with the cover.
 */
export const MississippiScheduleKind = {
  /** Itemized Receipts (cash / other monetary receipts + loans). 4 rows per page. */
  Receipts: 'Receipts',
  /** Itemized Receipts – In-Kind Contributions. Its own schedule, no year-to-date column. */
  InKind: 'InKind',
  /** Itemized Disbursements. 6 rows per page. */
  Disbursements: 'Disbursements',
} as const;
export type MississippiScheduleKind = (typeof MississippiScheduleKind)[keyof typeof MississippiScheduleKind];
export declare namespace MississippiScheduleKind {
  type Receipts = 'Receipts';
  type InKind = 'InKind';
  type Disbursements = 'Disbursements';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.Mississippi.Schedules.MississippiScheduleSourceKind
 *
 * The "Source" a Mississippi itemized-receipts / in-kind row is marked as — the checkboxes printed on the schedule (Corporation / PAC / Individual / Loan / Other). Derived from the contributor's identity (`FinanceContact.ContactKind` + `FinanceContact.OrganizationType`), except a received loan, which is always `MississippiScheduleSourceKind.Loan` (§ 23-15-807 — a loan is disclosed as a receipt with source "Loan" on the same schedule, issue #858).
 */
export const MississippiScheduleSourceKind = {
  Corporation: 'Corporation',
  Pac: 'Pac',
  Individual: 'Individual',
  Loan: 'Loan',
  Other: 'Other',
} as const;
export type MississippiScheduleSourceKind = (typeof MississippiScheduleSourceKind)[keyof typeof MississippiScheduleSourceKind];
export declare namespace MississippiScheduleSourceKind {
  type Corporation = 'Corporation';
  type Pac = 'Pac';
  type Individual = 'Individual';
  type Loan = 'Loan';
  type Other = 'Other';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.Mississippi.CoverPdf.MississippiSignerLine
 *
 * The certification signer line printed on a role's form (one signer, never both).
 */
export const MississippiSignerLine = {
  /** "Signature of Candidate" — the individual-candidate form. */
  Candidate: 'Candidate',
  /** "Signature of Director or Treasurer" — the committee forms. */
  DirectorOrTreasurer: 'DirectorOrTreasurer',
} as const;
export type MississippiSignerLine = (typeof MississippiSignerLine)[keyof typeof MississippiSignerLine];
export declare namespace MississippiSignerLine {
  type Candidate = 'Candidate';
  type DirectorOrTreasurer = 'DirectorOrTreasurer';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.Mississippi.CoverPdf.MississippiTemplateFill
 *
 * How a vendored Mississippi template is filled.
 */
export const MississippiTemplateFill = {
  /** The periodic cover forms — real AcroForms filled by stable field name. */
  FieldName: 'FieldName',
  /** The 48-hour notice — a flat, fieldless form stamped at measured page coordinates. */
  Coordinate: 'Coordinate',
} as const;
export type MississippiTemplateFill = (typeof MississippiTemplateFill)[keyof typeof MississippiTemplateFill];
export declare namespace MississippiTemplateFill {
  type FieldName = 'FieldName';
  type Coordinate = 'Coordinate';
}

/**
 * HooverCanvassingApi.Features.Finance.MoneyMovement
 *
 * ADR-015 D8 — the one canonical money-movement vocabulary both import tiers produce (precise adapters AND the generic content scanner), so downstream triage cannot tell which tier read a row. The generic content scanner SUGGESTS one of these per row from a deterministic token vocabulary + value-shape; the suggestion is preview-advisory ONLY (the treasurer confirms, ADR-004). It never redirects the commit, which reads only (direction, IsRefundShaped) and always writes a Pending row — a `Fee`/`Pending`/`Void` suggestion synthesizes NO entity. The future LLM upgrade replaces the deterministic matcher behind this same enum. PascalCase values: this surfaces to the preview UI as a wire enum.
 */
export const MoneyMovement = {
  Received: 'Received',
  Spent: 'Spent',
  Refund: 'Refund',
  Void: 'Void',
  Pending: 'Pending',
  Fee: 'Fee',
} as const;
export type MoneyMovement = (typeof MoneyMovement)[keyof typeof MoneyMovement];
export declare namespace MoneyMovement {
  type Received = 'Received';
  type Spent = 'Spent';
  type Refund = 'Refund';
  type Void = 'Void';
  type Pending = 'Pending';
  type Fee = 'Fee';
}

/**
 * HooverCanvassingApi.Features.Finance.StripeConnect.Money.MoneyStreamResolutionOutcome
 *
 * How a money-stream (refund / dispute) event resolved to a campaign.
 */
export const MoneyStreamResolutionOutcome = {
  /** A parent Contribution for the PaymentIntent was found; its campaign is authoritative. */
  ResolvedByParent: 'ResolvedByParent',
  /** No parent, but the destination connected account resolved a campaign (before-parent case). */
  ResolvedByAccount: 'ResolvedByAccount',
  /** The destination account is unknown/absent — poison (quarantine). */
  UnknownAccount: 'UnknownAccount',
} as const;
export type MoneyStreamResolutionOutcome = (typeof MoneyStreamResolutionOutcome)[keyof typeof MoneyStreamResolutionOutcome];
export declare namespace MoneyStreamResolutionOutcome {
  type ResolvedByParent = 'ResolvedByParent';
  type ResolvedByAccount = 'ResolvedByAccount';
  type UnknownAccount = 'UnknownAccount';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.NewJersey.NewJerseyElecBlockReason
 *
 * Why a row can't be sent until the treasurer fixes one of its contacts.
 */
export const NewJerseyElecBlockReason = {
  /** The importer reads a row with no first name as an organization. */
  FirstNameRequired: 'FirstNameRequired',
  OccupationNotOnElecList: 'OccupationNotOnElecList',
  EmployerNameRequired: 'EmployerNameRequired',
  /** ELEC requires a benefit type on spending for another candidate, which is the office category recorded on the committee it supports. */
  RecipientOfficeCategoryRequired: 'RecipientOfficeCategoryRequired',
} as const;
export type NewJerseyElecBlockReason = (typeof NewJerseyElecBlockReason)[keyof typeof NewJerseyElecBlockReason];
export declare namespace NewJerseyElecBlockReason {
  type FirstNameRequired = 'FirstNameRequired';
  type OccupationNotOnElecList = 'OccupationNotOnElecList';
  type EmployerNameRequired = 'EmployerNameRequired';
  type RecipientOfficeCategoryRequired = 'RecipientOfficeCategoryRequired';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.NewJersey.NewJerseyElecHandEntryReason
 *
 * Why an itemized contribution is kept out of the files for the treasurer to enter in the portal.
 */
export const NewJerseyElecHandEntryReason = {
  /** The contributor's type has no importable ELEC value; the importer would read it as INDIVIDUAL. */
  ContributorTypeNotImportable: 'ContributorTypeNotImportable',
  /** A returning contributor shares a name with another contributor already in the portal or in the same upload. IsNew=No matches by name alone, so the gift could attach to the wrong person. */
  SameNameContributor: 'SameNameContributor',
} as const;
export type NewJerseyElecHandEntryReason = (typeof NewJerseyElecHandEntryReason)[keyof typeof NewJerseyElecHandEntryReason];
export declare namespace NewJerseyElecHandEntryReason {
  type ContributorTypeNotImportable = 'ContributorTypeNotImportable';
  type SameNameContributor = 'SameNameContributor';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.NewJersey.NewJerseyElecItemType
 *
 * The ELEC eFile upload item types the generated files are uploaded as.
 */
export const NewJerseyElecItemType = {
  Monetary: 'Monetary',
  InKind: 'InKind',
  Expenses: 'Expenses',
  OtherExp: 'OtherExp',
  ContMadeToOthers: 'ContMadeToOthers',
  ExpMadeOnBehalf: 'ExpMadeOnBehalf',
} as const;
export type NewJerseyElecItemType = (typeof NewJerseyElecItemType)[keyof typeof NewJerseyElecItemType];
export declare namespace NewJerseyElecItemType {
  type Monetary = 'Monetary';
  type InKind = 'InKind';
  type Expenses = 'Expenses';
  type OtherExp = 'OtherExp';
  type ContMadeToOthers = 'ContMadeToOthers';
  type ExpMadeOnBehalf = 'ExpMadeOnBehalf';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.NewJersey.NewJerseyR1ReceiptType
 *
 * The R-1 receipt types (ELEC's R-1 instructions, Schedules A and B).
 */
export const NewJerseyR1ReceiptType = {
  Currency: 'Currency',
  OtherMonetary: 'OtherMonetary',
  InKind: 'InKind',
  RefundsReceived: 'RefundsReceived',
  DividendsInterest: 'DividendsInterest',
} as const;
export type NewJerseyR1ReceiptType = (typeof NewJerseyR1ReceiptType)[keyof typeof NewJerseyR1ReceiptType];
export declare namespace NewJerseyR1ReceiptType {
  type Currency = 'Currency';
  type OtherMonetary = 'OtherMonetary';
  type InKind = 'InKind';
  type RefundsReceived = 'RefundsReceived';
  type DividendsInterest = 'DividendsInterest';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.NewJersey.NewJerseyR1Schedule
 *
 * Where an expenditure is listed, by ELEC's R-1 schedule numbers. Every expenditure is listed, whatever its amount.
 */
export const NewJerseyR1Schedule = {
  /** Schedule 8, campaign and operating expenditures. */
  OperatingDisbursements: 'OperatingDisbursements',
  /** Schedule 9, other expenses. */
  OtherDisbursements: 'OtherDisbursements',
  /** Schedule 10 for a contribution made to a candidate or committee, Schedule 11 for spending on its behalf. */
  ContributionsMade: 'ContributionsMade',
  /** Schedule 8 too: ELEC prints a loan payment with purpose "Loan Payment" and the balance left. */
  LoanRepayments: 'LoanRepayments',
} as const;
export type NewJerseyR1Schedule = (typeof NewJerseyR1Schedule)[keyof typeof NewJerseyR1Schedule];
export declare namespace NewJerseyR1Schedule {
  type OperatingDisbursements = 'OperatingDisbursements';
  type OtherDisbursements = 'OtherDisbursements';
  type ContributionsMade = 'ContributionsMade';
  type LoanRepayments = 'LoanRepayments';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.NewJersey.NewJerseyR1UnitemizedLine
 *
 * The two lump sums for sources at or under the itemization threshold.
 */
export const NewJerseyR1UnitemizedLine = {
  MonetaryAndLoans: 'MonetaryAndLoans',
  InKind: 'InKind',
} as const;
export type NewJerseyR1UnitemizedLine = (typeof NewJerseyR1UnitemizedLine)[keyof typeof NewJerseyR1UnitemizedLine];
export declare namespace NewJerseyR1UnitemizedLine {
  type MonetaryAndLoans = 'MonetaryAndLoans';
  type InKind = 'InKind';
}

/** HooverCanvassingApi.Features.Finance.NotificationChannel */
export const NotificationChannel = {
  Email: 'Email',
  Sms: 'Sms',
} as const;
export type NotificationChannel = (typeof NotificationChannel)[keyof typeof NotificationChannel];
export declare namespace NotificationChannel {
  type Email = 'Email';
  type Sms = 'Sms';
}

/** HooverCanvassingApi.Features.Finance.NotificationStatus */
export const NotificationStatus = {
  Pending: 'Pending',
  Sent: 'Sent',
  Delivered: 'Delivered',
  Failed: 'Failed',
  Cancelled: 'Cancelled',
} as const;
export type NotificationStatus = (typeof NotificationStatus)[keyof typeof NotificationStatus];
export declare namespace NotificationStatus {
  type Pending = 'Pending';
  type Sent = 'Sent';
  type Delivered = 'Delivered';
  type Failed = 'Failed';
  type Cancelled = 'Cancelled';
}

/**
 * HooverCanvassingApi.Features.Finance.ObligationKind
 *
 * Discriminates the `FilingObligation` regimes. `ObligationKind.RapidContribution` (AL §17-5-8.1(c) $20k Major Contribution / VA §24.2-947.9 pre-election rapid) is per-contribution: `ContributionId`-bound, generable, and (AL only) contribution-flipping. `ObligationKind.DailyAggregate` (AL §17-5-8(b)(3), the $5k/day pre-election report) is per-day: `ContributionId`-null, `ObligationDate`-keyed, portal-filed only (no file, no flip). `ObligationKind.PreSessionAggregate` (VA §24.2-947.11, the Jan-15 pre-session large-contribution report) is per-window: `ContributionId`-null, `ObligationDate`-keyed to the window end, generable as a multi-donor COMET `LargeContributionReport`, and supplemental (marks the obligation Filed WITHOUT flipping the gifts — they stay Confirmed for the periodic report, ADR-039). `ObligationKind.RapidNotice` (NC §163-278.9(a)(2), the 48-hour pre-election notice) is per-contribution like `ObligationKind.RapidContribution` but WINDOW-GATED at creation (a single ≥$1,000 gift whose receipt date falls inside an election's pre-election window) with a deadline 48 hours from the gift's receipt (campaign-local → UTC), and supplemental (marks the obligation Filed WITHOUT flipping the gift — it stays Confirmed for the periodic report that must ALSO disclose it, ADR-038). It is a DISTINCT kind so it never rides the `ObligationKind.RapidContribution` read-layer window filters (VA §24.2-947.9 / LA R.S. 18:1495.4), which are gated on that kind alone. Default is `ObligationKind.RapidContribution` so pre-existing rows backfill to it.
 */
export const ObligationKind = {
  RapidContribution: 'RapidContribution',
  DailyAggregate: 'DailyAggregate',
  PreSessionAggregate: 'PreSessionAggregate',
  RapidNotice: 'RapidNotice',
  RapidLoan: 'RapidLoan',
} as const;
export type ObligationKind = (typeof ObligationKind)[keyof typeof ObligationKind];
export declare namespace ObligationKind {
  type RapidContribution = 'RapidContribution';
  type DailyAggregate = 'DailyAggregate';
  type PreSessionAggregate = 'PreSessionAggregate';
  type RapidNotice = 'RapidNotice';
  type RapidLoan = 'RapidLoan';
}

/** HooverCanvassingApi.Features.Onboarding.OnboardingAssistanceStatus */
export const OnboardingAssistanceStatus = {
  New: 'New',
} as const;
export type OnboardingAssistanceStatus = (typeof OnboardingAssistanceStatus)[keyof typeof OnboardingAssistanceStatus];
export declare namespace OnboardingAssistanceStatus {
  type New = 'New';
}

/**
 * HooverCanvassingApi.Features.Onboarding.OnboardingAssistanceType
 *
 * The kind of hands-on onboarding help a manager requested. Extensible as we add concierge onboarding asks.
 */
export const OnboardingAssistanceType = {
  /** "We'll get your voter file for you": a concierge stand-in while voter-file pricing and self-serve are being decided. */
  VoterFile: 'VoterFile',
} as const;
export type OnboardingAssistanceType = (typeof OnboardingAssistanceType)[keyof typeof OnboardingAssistanceType];
export declare namespace OnboardingAssistanceType {
  type VoterFile = 'VoterFile';
}

/** HooverCanvassingApi.Features.Assistant.OnboardingOutcome */
export const OnboardingOutcome = {
  Finished: 'Finished',
  Skipped: 'Skipped',
} as const;
export type OnboardingOutcome = (typeof OnboardingOutcome)[keyof typeof OnboardingOutcome];
export declare namespace OnboardingOutcome {
  type Finished = 'Finished';
  type Skipped = 'Skipped';
}

/** HooverCanvassingApi.Features.Onboarding.OnboardingPlayCategory */
export const OnboardingPlayCategory = {
  Foundational: 'Foundational',
  Operational: 'Operational',
} as const;
export type OnboardingPlayCategory = (typeof OnboardingPlayCategory)[keyof typeof OnboardingPlayCategory];
export declare namespace OnboardingPlayCategory {
  type Foundational = 'Foundational';
  type Operational = 'Operational';
}

/**
 * HooverCanvassingApi.Features.Onboarding.OnboardingPlayKey
 *
 * The V1 onboarding play catalog. Each play has two beats: a short learning step where the manager provides input, then a software-generated win (an artifact they couldn't have built that fast by hand). Surfaced 2-3 at a time by `OnboardingMenuPlanner` based on what the manager already has and how close the election is.
 */
export const OnboardingPlayKey = {
  GetVoterFileIn: 'GetVoterFileIn',
  RecruitVolunteers: 'RecruitVolunteers',
  BuildCampaignPlan: 'BuildCampaignPlan',
  LockInScript: 'LockInScript',
  PlanFirstCanvassDay: 'PlanFirstCanvassDay',
  PlanNextCanvassWeekend: 'PlanNextCanvassWeekend',
} as const;
export type OnboardingPlayKey = (typeof OnboardingPlayKey)[keyof typeof OnboardingPlayKey];
export declare namespace OnboardingPlayKey {
  type GetVoterFileIn = 'GetVoterFileIn';
  type RecruitVolunteers = 'RecruitVolunteers';
  type BuildCampaignPlan = 'BuildCampaignPlan';
  type LockInScript = 'LockInScript';
  type PlanFirstCanvassDay = 'PlanFirstCanvassDay';
  type PlanNextCanvassWeekend = 'PlanNextCanvassWeekend';
}

/**
 * HooverCanvassingApi.Common.Models.OnboardingStatus
 *
 * Onboarding progress for a campaign manager. Transitions forward only; users who skip can be resumed via the "Resume tutorial" badge until they explicitly complete.
 */
export const OnboardingStatus = {
  NotStarted: 'NotStarted',
  SelfIdCompleted: 'SelfIdCompleted',
  InDemo: 'InDemo',
  Skipped: 'Skipped',
  Completed: 'Completed',
} as const;
export type OnboardingStatus = (typeof OnboardingStatus)[keyof typeof OnboardingStatus];
export declare namespace OnboardingStatus {
  type NotStarted = 'NotStarted';
  type SelfIdCompleted = 'SelfIdCompleted';
  type InDemo = 'InDemo';
  type Skipped = 'Skipped';
  type Completed = 'Completed';
}

/**
 * HooverCanvassingApi.Features.Assistant.OnboardingStep
 *
 * What a member sees on opening the assistant: nothing, the whole first-open onboarding, or only the purchases step. `OnboardingStep.PurchasesOnly` is for a member who finished onboarding and has since been given Finance Manage.
 */
export const OnboardingStep = {
  None: 'None',
  Full: 'Full',
  PurchasesOnly: 'PurchasesOnly',
} as const;
export type OnboardingStep = (typeof OnboardingStep)[keyof typeof OnboardingStep];
export declare namespace OnboardingStep {
  type None = 'None';
  type Full = 'Full';
  type PurchasesOnly = 'PurchasesOnly';
}

/**
 * HooverCanvassingApi.Features.Finance.OrganizationType
 *
 * ADR-057 — the SUBTYPE of an organization `FinanceContact`: corporation, PAC, party, candidate committee, other committee, or other organization. Orthogonal to `ContactKind` and nullable (null = an org whose subtype is not yet classified; never set on an individual). The single source of truth for the organization-subtype axis across every role a contact plays (Donor, Payee, Beneficiary Committee, lender). "Is a committee?" is DERIVED from it (the four committee values — see `OrganizationTypeExtensions.IsCommittee`). This REPLACED the retired ADR-030 `CommitteeKind` enum (#344) as the POSITIVE "is a committee" signal a federal committee-to-committee disbursement keys on (a blank `FinanceContact.CommitteeFecId` is ambiguous, so the id is deliberately NOT the signal); it drives the precise Schedule B/A ENTITY TYPE (CCM/PAC/PTY/COM) via `PayeeEntityType.ForPayee` and the file-time missing-id block. Stored by enum name (HasConversion<string>, varchar(50)) — ordinal position is cosmetic.
 *
 * #478 — carries `JsonStringEnumConverter` so the ruleset provider (whose local `JsonSerializerOptions` has no global converter) can deserialize the `supportedOrganizationTypes` string array from a ruleset JSON, mirroring `ItemizationWindow` / `RefundNettingBasis`. The API wire is already PascalCase-string via the global converter, so the attribute changes no existing output.
 */
export const OrganizationType = {
  /** A for-profit / non-profit corporation — NOT a committee (a federally prohibited source). */
  Corporation: 'Corporation',
  /** A separate-segregated-fund / non-connected PAC — a committee (Schedule entity `PAC`). */
  Pac: 'Pac',
  /** A party committee (national / state / local) — a committee (Schedule entity `PTY`). */
  Party: 'Party',
  /** A candidate's own authorized committee — a committee (Schedule entity `CCM`). */
  CandidateCommittee: 'CandidateCommittee',
  /** Any other registered political committee — a committee (Schedule entity `COM`). */
  OtherCommittee: 'OtherCommittee',
  /** Any other (non-corporate, non-committee) organization — NOT a committee. */
  OtherOrganization: 'OtherOrganization',
  /** A labor union / labor organization — NOT a committee, and a federally prohibited source (52 U.S.C. § 30118, alongside corporations) and an NC-prohibited source (N.C.G.S. § 163-278.15). Added by #346 to activate those dormant union prohibitions (its ruleset `"union"` donor type had no producing subtype until now). */
  Union: 'Union',
} as const;
export type OrganizationType = (typeof OrganizationType)[keyof typeof OrganizationType];
export declare namespace OrganizationType {
  type Corporation = 'Corporation';
  type Pac = 'Pac';
  type Party = 'Party';
  type CandidateCommittee = 'CandidateCommittee';
  type OtherCommittee = 'OtherCommittee';
  type OtherOrganization = 'OtherOrganization';
  type Union = 'Union';
}

/**
 * HooverCanvassingApi.Features.Finance.OrganizationTypeConflictActionResult
 *
 * The outcome of a resolve/dismiss on a contact-merge OrganizationType conflict. A concurrency mismatch is signalled out-of-band via `ConcurrencyConflictException` (mapped to 409 by the controller), mirroring the merge.
 */
export const OrganizationTypeConflictActionResult = {
  /** No such contact, or the contact carries no merge-conflict history at all → 404. */
  NotFound: 'NotFound',
  /** The chosen subtype isn't one of the two recorded candidate values → 400. */
  InvalidChoice: 'InvalidChoice',
  /** Applied (or an idempotent no-op success for an already-closed dismissal). */
  Ok: 'Ok',
} as const;
export type OrganizationTypeConflictActionResult = (typeof OrganizationTypeConflictActionResult)[keyof typeof OrganizationTypeConflictActionResult];
export declare namespace OrganizationTypeConflictActionResult {
  type NotFound = 'NotFound';
  type InvalidChoice = 'InvalidChoice';
  type Ok = 'Ok';
}

/**
 * HooverCanvassingApi.Features.Finance.OutcomeType
 *
 * Outcome type for compliance evaluation results. Flag = advisory (UI display), Gate = blocking (must resolve/override), Notification = reporting obligation (informational, never blocking).
 */
export const OutcomeType = {
  Flag: 'Flag',
  Gate: 'Gate',
  Notification: 'Notification',
} as const;
export type OutcomeType = (typeof OutcomeType)[keyof typeof OutcomeType];
export declare namespace OutcomeType {
  type Flag = 'Flag';
  type Gate = 'Gate';
  type Notification = 'Notification';
}

/**
 * HooverCanvassingApi.Features.Finance.StripeConnect.Recurring.OverLimitRecurringCancelOutcome
 *
 * The outcome of an over-limit one-click recurring cancel (ADR-069, #618).
 */
export const OverLimitRecurringCancelOutcome = {
  /** The donor's active series was canceled in Stripe and its mirror moved to Canceled. */
  Canceled: 'Canceled',
  /** The contribution is a one-time gift (null series-link) — nothing to cancel, a clean no-op. */
  NoRecurringLink: 'NoRecurringLink',
  /** Nothing live remained to cancel — the mirror was already terminal (or absent). Idempotent. */
  AlreadyInactive: 'AlreadyInactive',
} as const;
export type OverLimitRecurringCancelOutcome = (typeof OverLimitRecurringCancelOutcome)[keyof typeof OverLimitRecurringCancelOutcome];
export declare namespace OverLimitRecurringCancelOutcome {
  type Canceled = 'Canceled';
  type NoRecurringLink = 'NoRecurringLink';
  type AlreadyInactive = 'AlreadyInactive';
}

/**
 * HooverCanvassingApi.Features.Finance.P2PPlatform
 *
 * PPR-015 — which P2P platform a hand-entered Venmo / Zelle / Cash App contribution came from. Stored on the `Contribution` row so downstream reporting can distinguish the platforms even though they all share one `PaymentProcessorKind.ManualP2P` processor. `Other` is the catch-all when a campaign accepts P2P from an off-list app (PayPal personal, Apple Cash, etc.) — the treasurer still has to fill out the same donor metadata.
 */
export const P2PPlatform = {
  Venmo: 'Venmo',
  Zelle: 'Zelle',
  CashApp: 'CashApp',
  Other: 'Other',
} as const;
export type P2PPlatform = (typeof P2PPlatform)[keyof typeof P2PPlatform];
export declare namespace P2PPlatform {
  type Venmo = 'Venmo';
  type Zelle = 'Zelle';
  type CashApp = 'CashApp';
  type Other = 'Other';
}

/**
 * HooverCanvassingApi.Features.RacePicture.CampaignLink.PartyPrimary
 *
 * The candidate's answer to "which primary do you run in?", asked only when the Race has both party primaries.
 */
export const PartyPrimary = {
  Republican: 'Republican',
  Democratic: 'Democratic',
} as const;
export type PartyPrimary = (typeof PartyPrimary)[keyof typeof PartyPrimary];
export declare namespace PartyPrimary {
  type Republican = 'Republican';
  type Democratic = 'Democratic';
}

/**
 * HooverCanvassingApi.Features.Finance.PartyPrimaryApplicability
 *
 * How a reporting-schedule entry relates to Louisiana's closed-party-primary latch (`CampaignFinanceSettings.RunsClosedPartyPrimary`) — the fifth applicability gate DeadlineService applies, sibling to office-level / general-election-month / election-method / threshold-committee. Louisiana's PSC / Supreme Court / BESE candidates run a closed party primary and file a DIFFERENT report calendar from open-primary candidates: they skip the 180-P and 90-P entirely and instead file a 10-SP anchored to the second party primary (runoff).
 *
 * - `Any` — the entry generates regardless of the latch (the shared 30P/10P/10G/40G, and every entry in every other jurisdiction). The default, so every entry that leaves the field unset is inert w.r.t. the latch.
 * - `OpenPrimaryOnly` — the 180-P/90-P pre-primary reports an open-primary candidate files; suppressed only for a DECLARED closed-party-primary candidate. Fires for open OR undeclared, which matches the backfill default (`RunsClosedPartyPrimary` defaults to false/null on onboarding).
 * - `PartyPrimaryOnly` — the 10-SP a closed-party-primary candidate files; generated ONLY when the latch is set.
 *
 * General-purpose, but Louisiana is the only consumer. `Any = 0` so an absent JSON value defaults to it (no silent behavior change for any other jurisdiction or existing entry).
 */
export const PartyPrimaryApplicability = {
  Any: 'Any',
  OpenPrimaryOnly: 'OpenPrimaryOnly',
  PartyPrimaryOnly: 'PartyPrimaryOnly',
} as const;
export type PartyPrimaryApplicability = (typeof PartyPrimaryApplicability)[keyof typeof PartyPrimaryApplicability];
export declare namespace PartyPrimaryApplicability {
  type Any = 'Any';
  type OpenPrimaryOnly = 'OpenPrimaryOnly';
  type PartyPrimaryOnly = 'PartyPrimaryOnly';
}

/**
 * HooverCanvassingApi.Features.Finance.PayloadScope
 *
 * ADR-021 — what the upload payload carries. `TransactionsOnly` (AL) emits no summary/COH lines; `FilerAssertsSummary` (federal and VA) emits the full summary (VA's COMET XSD requires the filer to assert Schedules G/H); `PortalComputesSummary` (e.g. IL, when onboarded) emits neither. Plain enum (a parse target, not JSON-bound).
 */
export const PayloadScope = {
  TransactionsOnly: 'TransactionsOnly',
  PortalComputesSummary: 'PortalComputesSummary',
  FilerAssertsSummary: 'FilerAssertsSummary',
} as const;
export type PayloadScope = (typeof PayloadScope)[keyof typeof PayloadScope];
export declare namespace PayloadScope {
  type TransactionsOnly = 'TransactionsOnly';
  type PortalComputesSummary = 'PortalComputesSummary';
  type FilerAssertsSummary = 'FilerAssertsSummary';
}

/** HooverCanvassingApi.Features.Finance.PaymentMethod */
export const PaymentMethod = {
  Cash: 'Cash',
  Check: 'Check',
  CreditCard: 'CreditCard',
  Wire: 'Wire',
  InKind: 'InKind',
  Other: 'Other',
} as const;
export type PaymentMethod = (typeof PaymentMethod)[keyof typeof PaymentMethod];
export declare namespace PaymentMethod {
  type Cash = 'Cash';
  type Check = 'Check';
  type CreditCard = 'CreditCard';
  type Wire = 'Wire';
  type InKind = 'InKind';
  type Other = 'Other';
}

/**
 * HooverCanvassingApi.Features.Finance.PaymentProcessorKind
 *
 * Which processor a PaymentProcessor row represents. ManualP2P is the catch-all for Venmo / Zelle / Cash App contributions a treasurer enters by hand (PPR-015). Other (PPR-002) is the "name your own processor" escape hatch — no fee defaults, no adapter, treasurer-named display.
 */
export const PaymentProcessorKind = {
  Numero: 'Numero',
  WinRed: 'WinRed',
  ActBlue: 'ActBlue',
  Anedot: 'Anedot',
  Stripe: 'Stripe',
  StripeConnect: 'StripeConnect',
  ManualP2P: 'ManualP2P',
  Other: 'Other',
} as const;
export type PaymentProcessorKind = (typeof PaymentProcessorKind)[keyof typeof PaymentProcessorKind];
export declare namespace PaymentProcessorKind {
  type Numero = 'Numero';
  type WinRed = 'WinRed';
  type ActBlue = 'ActBlue';
  type Anedot = 'Anedot';
  type Stripe = 'Stripe';
  type StripeConnect = 'StripeConnect';
  type ManualP2P = 'ManualP2P';
  type Other = 'Other';
}

/** HooverCanvassingApi.Features.Auth.PendingVolunteerStatus */
export const PendingVolunteerStatus = {
  Pending: 'Pending',
  Approved: 'Approved',
  Rejected: 'Rejected',
} as const;
export type PendingVolunteerStatus = (typeof PendingVolunteerStatus)[keyof typeof PendingVolunteerStatus];
export declare namespace PendingVolunteerStatus {
  type Pending = 'Pending';
  type Approved = 'Approved';
  type Rejected = 'Rejected';
}

/**
 * HooverCanvassingApi.Features.Finance.Plaid.PlaidSyncOutcome
 *
 * How a `/transactions/sync` call resolved — callers switch on this, never on an exception.
 */
export const PlaidSyncOutcome = {
  /** The page returned normally. */
  Ok: 'Ok',
  /** Plaid returned `ITEM_LOGIN_REQUIRED`; the Item must be reconnected (#560). */
  LoginRequired: 'LoginRequired',
  /** A transient Plaid/network error; retry later (#561 poll). */
  TransientError: 'TransientError',
} as const;
export type PlaidSyncOutcome = (typeof PlaidSyncOutcome)[keyof typeof PlaidSyncOutcome];
export declare namespace PlaidSyncOutcome {
  type Ok = 'Ok';
  type LoginRequired = 'LoginRequired';
  type TransientError = 'TransientError';
}

/**
 * HooverCanvassingApi.Features.Finance.Plaid.PollSyncStatus
 *
 * How a poll-initiated sync (#561) resolved.
 */
export const PollSyncStatus = {
  Ran: 'Ran',
  SkippedInProgress: 'SkippedInProgress',
} as const;
export type PollSyncStatus = (typeof PollSyncStatus)[keyof typeof PollSyncStatus];
export declare namespace PollSyncStatus {
  type Ran = 'Ran';
  type SkippedInProgress = 'SkippedInProgress';
}

/** HooverCanvassingApi.Features.Finance.PortalEntry.PortalEntryRunStatus */
export const PortalEntryRunStatus = {
  Starting: 'Starting',
  WaitingForLogin: 'WaitingForLogin',
  Running: 'Running',
  NeedsHuman: 'NeedsHuman',
  Completed: 'Completed',
  Failed: 'Failed',
  Cancelled: 'Cancelled',
} as const;
export type PortalEntryRunStatus = (typeof PortalEntryRunStatus)[keyof typeof PortalEntryRunStatus];
export declare namespace PortalEntryRunStatus {
  type Starting = 'Starting';
  type WaitingForLogin = 'WaitingForLogin';
  type Running = 'Running';
  type NeedsHuman = 'NeedsHuman';
  type Completed = 'Completed';
  type Failed = 'Failed';
  type Cancelled = 'Cancelled';
}

/**
 * HooverCanvassingApi.Features.Finance.PortalFollowUpStatus
 *
 * Whether a `PortalFollowUp` is still owed (`Open`) or the treasurer has handled it in the portal (`Done`). PascalCase to match the .NET enum convention.
 */
export const PortalFollowUpStatus = {
  Open: 'Open',
  Done: 'Done',
} as const;
export type PortalFollowUpStatus = (typeof PortalFollowUpStatus)[keyof typeof PortalFollowUpStatus];
export declare namespace PortalFollowUpStatus {
  type Open = 'Open';
  type Done = 'Done';
}

/**
 * HooverCanvassingApi.Features.Finance.PreChargeLimitDecision
 *
 * The limit-only decision the public pre-charge donation block returns (Resolved-decision #12). `Block` when the would-be gift pushes the per-election/period aggregate over the cap, `Allow` otherwise — including exactly at the cap, and for an unlimited / no-matching rule. Serializes PascalCase on the wire.
 *
 * There is no third "approaching" value: an 80%-of-limit band once sat between these two and neither the treasurer nor the donor could act on it, so it was removed rather than kept as noise.
 */
export const PreChargeLimitDecision = {
  Allow: 'Allow',
  Block: 'Block',
  /** The gift exceeds the limit AND the jurisdiction can cure it — charge anyway (GitHub #744). Distinct from `PreChargeLimitDecision.Allow` on purpose: the money is lawful to accept but creates a statutory cure obligation, so the donation page must still offer the donor the election with headroom (an express designation needs no cure at all) before letting them proceed. Folding it into `PreChargeLimitDecision.Allow` would charge the card without ever showing that offer, turning every over-limit gift into treasurer work that a single donor click could have avoided. */
  AcceptOverLimit: 'AcceptOverLimit',
} as const;
export type PreChargeLimitDecision = (typeof PreChargeLimitDecision)[keyof typeof PreChargeLimitDecision];
export declare namespace PreChargeLimitDecision {
  type Allow = 'Allow';
  type Block = 'Block';
  type AcceptOverLimit = 'AcceptOverLimit';
}

/**
 * HooverCanvassingApi.Features.Finance.Filing.Preflight.PreflightMode
 *
 * The registry runs the SAME checks at GET preflight and (Enforce-only) at file time. A check branches on `PreflightMode` only where GET vs file output differs (the AL InKind message differs between the two).
 */
export const PreflightMode = {
  /** GET preflight — Enforce checks return a hard-failure, Warn checks a warning. */
  Preflight: 'Preflight',
  /** File time — Enforce checks THROW on violation; only Enforce checks run. */
  File: 'File',
} as const;
export type PreflightMode = (typeof PreflightMode)[keyof typeof PreflightMode];
export declare namespace PreflightMode {
  type Preflight = 'Preflight';
  type File = 'File';
}

/**
 * HooverCanvassingApi.Features.Finance.Filing.Preflight.PreflightSeverity
 *
 * Registry extraction of the filing-preflight checks (severity-is-a-property, not an enum). Each check is a discrete `IPreflightCheck` the `PreflightCheckRegistry` iterates in a declared order, replacing the former inline jurisdiction branches in `FilingPreflightService.PreflightAsync`. See `docs/compliance-architecture.md` §5 for the target shape and `Filing/CLAUDE.md` for the wire contract.
 *
 * `PreflightSeverity` is a check-REGISTRATION property, internal to the registry — NEVER a wire `WarningSeverity` enum. `Filing/CLAUDE.md` bans one: on the wire, severity is encoded by which `WarningAction`s a warning carries. A `PreflightSeverity.Warn` check surfaces at GET preflight; a `PreflightSeverity.Enforce` check ALSO re-runs at file time and throws.
 */
export const PreflightSeverity = {
  /** Surfaces at GET preflight only (alert-don't-block, ADR-007). */
  Warn: 'Warn',
  /** Surfaces at GET preflight AND re-runs at file time, throwing a `SubmissionPackageException` on violation (the AL-pack file-time hard guards). */
  Enforce: 'Enforce',
} as const;
export type PreflightSeverity = (typeof PreflightSeverity)[keyof typeof PreflightSeverity];
export declare namespace PreflightSeverity {
  type Warn = 'Warn';
  type Enforce = 'Enforce';
}

/**
 * HooverCanvassingApi.Features.Finance.PreparationMode
 *
 * Whether the app produces the filing artifact for a scheduled report, or only reminds the treasurer to file it themselves in the state portal. See `Features/Finance/CONTEXT.md` "Preparation mode".
 *
 * - `Prepared` — the app materializes the report + its transaction delta for the treasurer to export/upload (AL monthly + weekly pre-election); it offers Export/File and its transactions cross via `ExportBatch`.
 * - `RemindOnly` — a portal-native report the app cannot build (the AL annual); it surfaces only the deadline reminder + an optional "I filed it in the portal" attestation, and never an Export/File action or a transaction delta; `export-delta` and `file` refuse it with a 400 `RemindOnlyReport`.
 *
 * Absent in the reporting-schedule JSON ⇒ `Prepared` (see `ReportingScheduleEntry.PreparationMode`). `Prepared = 0` so the enum default and the string store default line up.
 */
export const PreparationMode = {
  Prepared: 'Prepared',
  RemindOnly: 'RemindOnly',
} as const;
export type PreparationMode = (typeof PreparationMode)[keyof typeof PreparationMode];
export declare namespace PreparationMode {
  type Prepared = 'Prepared';
  type RemindOnly = 'RemindOnly';
}

/**
 * HooverCanvassingApi.Features.Finance.ProcessorContributionStatus
 *
 * What the processor told us about a contribution's status. Persisted on Contribution.ProcessorStatus alongside the canonical LedgerStatus. Distinct from LedgerStatus because the processor's view (e.g. Refunded) can diverge from the ledger's view (Confirmed, with a separate refund expenditure for the same period).
 */
export const ProcessorContributionStatus = {
  /** The payment transaction completed successfully at the processor level. This is NOT "payout settled" — the payout (ACH disbursement to the campaign's bank account) is a separate, later event. A card charge that succeeds immediately is `Disbursed`; the ACH deposit may arrive days later. Filing eligibility turns on whether the transaction itself completed (ADR-007), not on whether the payout settled. */
  Disbursed: 'Disbursed',
  /** The payment transaction has not completed yet (e.g., pending ACH/echeck that may still bounce). See `SubmissionPackageExceptionType.ProcessorPreSettlement` for the ledger-classifier counterpart. `Pending` gifts count toward the D3 detection predicate — an imported row is a real fact even before the transaction clears. */
  Pending: 'Pending',
  /** The payment was reversed at the processor level (e.g., Stripe refund, WinRed/Anedot status flip). A `Refunded` gift keeps `LedgerStatus = Confirmed` with a separate `ContributionRefund` row. The fee-eligibility predicate INCLUDES `Refunded` gifts at the full gift amount — the processor kept its cut on the refund; an all-refunded period arms the fee nudge (fee-on-refund research). */
  Refunded: 'Refunded',
  /** The payment was voided before settlement (processor-side cancellation). Distinguished from `Refunded` because the transaction never completed; excluded from the fee-eligibility predicate — no money moved. */
  Voided: 'Voided',
} as const;
export type ProcessorContributionStatus = (typeof ProcessorContributionStatus)[keyof typeof ProcessorContributionStatus];
export declare namespace ProcessorContributionStatus {
  type Disbursed = 'Disbursed';
  type Pending = 'Pending';
  type Refunded = 'Refunded';
  type Voided = 'Voided';
}

/**
 * HooverCanvassingApi.Features.Finance.ProcessorFeeStepAsideReason
 *
 * Why a treasurer consciously stepped aside on the calculated processor fee for a period + processor (spec #103 Impl Decision 3, issue #109) instead of recording it. Carried on the `ProcessorFeeStepAside` record and on its `FilingExceptionAttested(ProcessorFeeOmitted)` forensic audit row. The reason is the treasurer's why (distinct from the fee-omission classifier itself); the copy shown for `ProcessorFeeStepAsideReason.AddLater` is jurisdiction-aware at the render boundary (federal "add it later by amending" vs. Alabama "add it on a later upload") — the enum value is jurisdiction-neutral.
 */
export const ProcessorFeeStepAsideReason = {
  /** The treasurer recorded (or will record) the fee by hand outside the helper. */
  EnteredMyself: 'EnteredMyself',
  /** No processor fee applies to this period (e.g. a comped or zero-fee arrangement). */
  NoFeeApplies: 'NoFeeApplies',
  /** The treasurer will add the fee later (federal amendment / Alabama later upload). */
  AddLater: 'AddLater',
} as const;
export type ProcessorFeeStepAsideReason = (typeof ProcessorFeeStepAsideReason)[keyof typeof ProcessorFeeStepAsideReason];
export declare namespace ProcessorFeeStepAsideReason {
  type EnteredMyself = 'EnteredMyself';
  type NoFeeApplies = 'NoFeeApplies';
  type AddLater = 'AddLater';
}

/**
 * HooverCanvassingApi.Features.Finance.ProcessorSourceShape
 *
 * The money-in / payout shape of a processor import source — it selects which adapter parse method and which commit arm handles the source.
 */
export const ProcessorSourceShape = {
  /** Money-in contributions file (Numero / WinRed / Anedot / ActBlue). */
  Contributions: 'Contributions',
  /** Payout-only file — parsed for its refund rows (ADR-011: no payout aggregate). */
  Payouts: 'Payouts',
  /** Single combined report carrying contributions + refunds (Stripe). */
  Combined: 'Combined',
} as const;
export type ProcessorSourceShape = (typeof ProcessorSourceShape)[keyof typeof ProcessorSourceShape];
export declare namespace ProcessorSourceShape {
  type Contributions = 'Contributions';
  type Payouts = 'Payouts';
  type Combined = 'Combined';
}

/**
 * HooverCanvassingApi.Features.Assistant.ProposalState
 *
 * A write the model wants a human to run. `ProposalState.Pending` moves once, by a conditional update on its version, to `ProposalState.Approved`, `ProposalState.Rejected` or `ProposalState.Failed`; an `ApiAction` moves to `ProposalState.Allowed` first and the approver's app reports it on to Approved or Failed.
 */
export const ProposalState = {
  Pending: 'Pending',
  Allowed: 'Allowed',
  Approved: 'Approved',
  Rejected: 'Rejected',
  Failed: 'Failed',
} as const;
export type ProposalState = (typeof ProposalState)[keyof typeof ProposalState];
export declare namespace ProposalState {
  type Pending = 'Pending';
  type Allowed = 'Allowed';
  type Approved = 'Approved';
  type Rejected = 'Rejected';
  type Failed = 'Failed';
}

/**
 * HooverCanvassingApi.Features.Fundraising.ProspectBookFilter
 *
 * A filter chip on the Prospects book. `ProspectBookFilter.NeverCalled`, `ProspectBookFilter.CallBackOwed` and `ProspectBookFilter.OpenCommitment` go in `?activity=`; the rest go in `?match=`. Both combine with a call book.
 */
export const ProspectBookFilter = {
  /** People whose match status is `ProspectMatchStatus.FederalGiving`. */
  FederalGiving: 'FederalGiving',
  /** People whose match status is `ProspectMatchStatus.PossibleMatch`. */
  PossibleMatch: 'PossibleMatch',
  /** People with no phone number, who the Call Queue can't ring. */
  NoPhone: 'NoPhone',
  /** People with no Call Attempt at all. */
  NeverCalled: 'NeverCalled',
  /** People whose last call asked for a call back. */
  CallBackOwed: 'CallBackOwed',
  /** People with an Open Commitment — money promised, not yet in. */
  OpenCommitment: 'OpenCommitment',
  /** People whose match status is `ProspectMatchStatus.NotMatchedYet`: no Enrichment has looked at them. */
  NotMatchedYet: 'NotMatchedYet',
  /** People whose match status is `ProspectMatchStatus.NoneFound`: a run looked and found no federal giving. */
  NoneFound: 'NoneFound',
} as const;
export type ProspectBookFilter = (typeof ProspectBookFilter)[keyof typeof ProspectBookFilter];
export declare namespace ProspectBookFilter {
  type FederalGiving = 'FederalGiving';
  type PossibleMatch = 'PossibleMatch';
  type NoPhone = 'NoPhone';
  type NeverCalled = 'NeverCalled';
  type CallBackOwed = 'CallBackOwed';
  type OpenCommitment = 'OpenCommitment';
  type NotMatchedYet = 'NotMatchedYet';
  type NoneFound = 'NoneFound';
}

/**
 * HooverCanvassingApi.Features.Fundraising.ProspectBookSort
 *
 * A column the Prospects book sorts by (`?sort=`, with `?dir=`). Without one the book is largest Ask first, then name.
 */
export const ProspectBookSort = {
  /** The suggested Ask; no Ask goes last either way. */
  Ask: 'Ask',
  /** The person's name, ignoring case. */
  Name: 'Name',
  /** The phone's digits; no phone goes last either way. */
  Phone: 'Phone',
  /** Matched, a match to review, no match, not matched yet — the bigger gift first within each. */
  Match: 'Match',
  /** How many call books they're on. */
  CallBooks: 'CallBooks',
  /** Their last call; never called counts as the longest ago. */
  LastActivity: 'LastActivity',
} as const;
export type ProspectBookSort = (typeof ProspectBookSort)[keyof typeof ProspectBookSort];
export declare namespace ProspectBookSort {
  type Ask = 'Ask';
  type Name = 'Name';
  type Phone = 'Phone';
  type Match = 'Match';
  type CallBooks = 'CallBooks';
  type LastActivity = 'LastActivity';
}

/**
 * HooverCanvassingApi.Features.Fundraising.ProspectLists.ProspectListBindingConfidence
 *
 * How a binding came about: an exact header name, a human's pick, or nothing bound.
 */
export const ProspectListBindingConfidence = {
  High: 'High',
  Manual: 'Manual',
  None: 'None',
} as const;
export type ProspectListBindingConfidence = (typeof ProspectListBindingConfidence)[keyof typeof ProspectListBindingConfidence];
export declare namespace ProspectListBindingConfidence {
  type High = 'High';
  type Manual = 'Manual';
  type None = 'None';
}

/**
 * HooverCanvassingApi.Features.Fundraising.ProspectLists.ProspectListColumnTarget
 *
 * What an uploaded column can be bound to. Every uploaded row is an Individual.
 */
export const ProspectListColumnTarget = {
  Ignore: 'Ignore',
  /** A single name column, split into first and last by `ProspectListNameSplitter`. */
  FullName: 'FullName',
  FirstName: 'FirstName',
  LastName: 'LastName',
  Phone: 'Phone',
  Email: 'Email',
  Street: 'Street',
  City: 'City',
  State: 'State',
  Zip: 'Zip',
  /** Optional: apartment, suite or unit. */
  AddressLine2: 'AddressLine2',
  /** Optional: shown to the Caller, and read when matching federal giving. */
  Employer: 'Employer',
  /** Optional: shown to the Caller, and read when matching federal giving. */
  Occupation: 'Occupation',
  /** Optional: the person-wide note (`Prospect.Notes`) the Caller reads before dialling. */
  Notes: 'Notes',
} as const;
export type ProspectListColumnTarget = (typeof ProspectListColumnTarget)[keyof typeof ProspectListColumnTarget];
export declare namespace ProspectListColumnTarget {
  type Ignore = 'Ignore';
  type FullName = 'FullName';
  type FirstName = 'FirstName';
  type LastName = 'LastName';
  type Phone = 'Phone';
  type Email = 'Email';
  type Street = 'Street';
  type City = 'City';
  type State = 'State';
  type Zip = 'Zip';
  type AddressLine2 = 'AddressLine2';
  type Employer = 'Employer';
  type Occupation = 'Occupation';
  type Notes = 'Notes';
}

/**
 * HooverCanvassingApi.Features.Fundraising.ProspectLists.ProspectListEnrichmentState
 *
 * Where matching an upload's people against federal giving stands.
 */
export const ProspectListEnrichmentState = {
  /** The upload is still running; matching starts when it finishes. */
  Waiting: 'Waiting',
  Running: 'Running',
  Completed: 'Completed',
  /** Matching threw. The upload itself is still in — import and matching are two truths. */
  Failed: 'Failed',
  /** The upload failed, so no one was matched. */
  NotRun: 'NotRun',
} as const;
export type ProspectListEnrichmentState = (typeof ProspectListEnrichmentState)[keyof typeof ProspectListEnrichmentState];
export declare namespace ProspectListEnrichmentState {
  type Waiting = 'Waiting';
  type Running = 'Running';
  type Completed = 'Completed';
  type Failed = 'Failed';
  type NotRun = 'NotRun';
}

/** HooverCanvassingApi.Features.Fundraising.ProspectLists.ProspectListImportOutcome */
export const ProspectListImportOutcome = {
  /** The list was not waiting to be imported (already finished, or gone). Nothing was done. */
  Skipped: 'Skipped',
  Imported: 'Imported',
  Failed: 'Failed',
} as const;
export type ProspectListImportOutcome = (typeof ProspectListImportOutcome)[keyof typeof ProspectListImportOutcome];
export declare namespace ProspectListImportOutcome {
  type Skipped = 'Skipped';
  type Imported = 'Imported';
  type Failed = 'Failed';
}

/**
 * HooverCanvassingApi.Features.Fundraising.ProspectLists.ProspectListMembershipOrigin
 *
 * Whether the upload brought the Prospect into existence or found them already present.
 */
export const ProspectListMembershipOrigin = {
  Created: 'Created',
  Adopted: 'Adopted',
} as const;
export type ProspectListMembershipOrigin = (typeof ProspectListMembershipOrigin)[keyof typeof ProspectListMembershipOrigin];
export declare namespace ProspectListMembershipOrigin {
  type Created = 'Created';
  type Adopted = 'Adopted';
}

/**
 * HooverCanvassingApi.Features.Fundraising.ProspectLists.ProspectListNameOrder
 *
 * How a single `ProspectListColumnTarget.FullName` column orders its names.
 */
export const ProspectListNameOrder = {
  /** `John Q Smith`. */
  FirstLast: 'FirstLast',
  /** `Smith, John Q` — the FEC `contbr_nm` shape. */
  LastFirst: 'LastFirst',
} as const;
export type ProspectListNameOrder = (typeof ProspectListNameOrder)[keyof typeof ProspectListNameOrder];
export declare namespace ProspectListNameOrder {
  type FirstLast = 'FirstLast';
  type LastFirst = 'LastFirst';
}

/**
 * HooverCanvassingApi.Features.Fundraising.ProspectLists.ProspectListStatus
 *
 * Where a `ProspectList` is in its life. Persisted as the enum name — the idempotency index filter names `ProspectListStatus.Failed` and `ProspectListStatus.Undone` literally, so renaming either means changing that filter too.
 */
export const ProspectListStatus = {
  Pending: 'Pending',
  Importing: 'Importing',
  Imported: 'Imported',
  Failed: 'Failed',
  Undone: 'Undone',
} as const;
export type ProspectListStatus = (typeof ProspectListStatus)[keyof typeof ProspectListStatus];
export declare namespace ProspectListStatus {
  type Pending = 'Pending';
  type Importing = 'Importing';
  type Imported = 'Imported';
  type Failed = 'Failed';
  type Undone = 'Undone';
}

/**
 * HooverCanvassingApi.Features.Fundraising.ProspectMatchStatus
 *
 * What Enrichment found for a Prospect, as one status.
 */
export const ProspectMatchStatus = {
  /** Accepted federal giving, and no possible match waiting on a yes or no. */
  FederalGiving: 'FederalGiving',
  /** At least one possible match waiting on a yes or no — whatever else was accepted. */
  PossibleMatch: 'PossibleMatch',
  /** Enrichment looked and found no federal giving to show. */
  NoneFound: 'NoneFound',
  /** No Enrichment run has looked at this person yet. */
  NotMatchedYet: 'NotMatchedYet',
} as const;
export type ProspectMatchStatus = (typeof ProspectMatchStatus)[keyof typeof ProspectMatchStatus];
export declare namespace ProspectMatchStatus {
  type FederalGiving = 'FederalGiving';
  type PossibleMatch = 'PossibleMatch';
  type NoneFound = 'NoneFound';
  type NotMatchedYet = 'NotMatchedYet';
}

/**
 * HooverCanvassingApi.Features.Messaging.ProvisioningAction
 *
 * The hand-driven edge an operator asks the advance endpoint to take on the provisioning spine. Each value maps to one guarded method on `CampaignMessagingProfile`. Stored only on the wire (request body) — never persisted — so the operator advances the spine one deliberate step at a time.
 */
export const ProvisioningAction = {
  /** NotStarted → InfoCollected (requires the use-case fields). */
  CollectInfo: 'CollectInfo',
  /** InfoCollected → SubmittedToVendor (carries the vendor name). */
  SubmitToVendor: 'SubmitToVendor',
  /** SubmittedToVendor → Provisioned (carries the returned TCR ids). */
  MarkProvisioned: 'MarkProvisioned',
  /** Any non-terminal state → Rejected (terminal). */
  Reject: 'Reject',
  /** Any non-terminal state → Failed (terminal). */
  Fail: 'Fail',
} as const;
export type ProvisioningAction = (typeof ProvisioningAction)[keyof typeof ProvisioningAction];
export declare namespace ProvisioningAction {
  type CollectInfo = 'CollectInfo';
  type SubmitToVendor = 'SubmitToVendor';
  type MarkProvisioned = 'MarkProvisioned';
  type Reject = 'Reject';
  type Fail = 'Fail';
}

/** HooverCanvassingApi.Features.Assistant.PushAudience */
export const PushAudience = {
  Member: 'Member',
  Planner: 'Planner',
} as const;
export type PushAudience = (typeof PushAudience)[keyof typeof PushAudience];
export declare namespace PushAudience {
  type Member = 'Member';
  type Planner = 'Planner';
}

/** HooverCanvassingApi.Features.Assistant.PushPlatform */
export const PushPlatform = {
  Ios: 'Ios',
  Android: 'Android',
} as const;
export type PushPlatform = (typeof PushPlatform)[keyof typeof PushPlatform];
export declare namespace PushPlatform {
  type Ios = 'Ios';
  type Android = 'Android';
}

/**
 * HooverCanvassingApi.Features.Assistant.PushQueueOutcome
 *
 * `PushQueueOutcome.Deferred`: quiet hours, so it goes out at `dueAt`. `PushQueueOutcome.Capped`: the user's daily cap is spent; nothing was queued.
 */
export const PushQueueOutcome = {
  Queued: 'Queued',
  Deferred: 'Deferred',
  Capped: 'Capped',
} as const;
export type PushQueueOutcome = (typeof PushQueueOutcome)[keyof typeof PushQueueOutcome];
export declare namespace PushQueueOutcome {
  type Queued = 'Queued';
  type Deferred = 'Deferred';
  type Capped = 'Capped';
}

/**
 * HooverCanvassingApi.Features.Assistant.PushState
 *
 * Where an outbox push stands. Transitions live in `AssistantPush.MoveTo`; the sender's claim is the one write that bypasses it (Pending to Sending).
 */
export const PushState = {
  Pending: 'Pending',
  Sending: 'Sending',
  Sent: 'Sent',
  Failed: 'Failed',
  Suppressed: 'Suppressed',
} as const;
export type PushState = (typeof PushState)[keyof typeof PushState];
export declare namespace PushState {
  type Pending = 'Pending';
  type Sending = 'Sending';
  type Sent = 'Sent';
  type Failed = 'Failed';
  type Suppressed = 'Suppressed';
}

/** HooverCanvassingApi.Features.Assistant.PushTarget */
export const PushTarget = {
  AssistantThread: 'AssistantThread',
  CallSession: 'CallSession',
} as const;
export type PushTarget = (typeof PushTarget)[keyof typeof PushTarget];
export declare namespace PushTarget {
  type AssistantThread = 'AssistantThread';
  type CallSession = 'CallSession';
}

/**
 * HooverCanvassingApi.Features.Assistant.PushTemplate
 *
 * `PushTemplate.SessionStartsSoon` is the candidate's pre-session reminder the sender writes; `PushTemplate.PurchaseQuestion` and `PushTemplate.ExplanationsWaiting` are the finance sweeps', the second telling a finance manager that purchasers' explanations wait for approval. The agent queues none of them (`PushTemplates.WrittenByTheApi`).
 */
export const PushTemplate = {
  SessionStartsSoon: 'SessionStartsSoon',
  SessionMoved: 'SessionMoved',
  SessionCutShort: 'SessionCutShort',
  SessionMissed: 'SessionMissed',
  PledgeOverdue: 'PledgeOverdue',
  PurchaseQuestion: 'PurchaseQuestion',
  ThreadUpdate: 'ThreadUpdate',
  ExplanationsWaiting: 'ExplanationsWaiting',
} as const;
export type PushTemplate = (typeof PushTemplate)[keyof typeof PushTemplate];
export declare namespace PushTemplate {
  type SessionStartsSoon = 'SessionStartsSoon';
  type SessionMoved = 'SessionMoved';
  type SessionCutShort = 'SessionCutShort';
  type SessionMissed = 'SessionMissed';
  type PledgeOverdue = 'PledgeOverdue';
  type PurchaseQuestion = 'PurchaseQuestion';
  type ThreadUpdate = 'ThreadUpdate';
  type ExplanationsWaiting = 'ExplanationsWaiting';
}

/** HooverCanvassingApi.Features.Contacts.QuestionErrorType */
export const QuestionErrorType = {
  None: 'None',
  NotFound: 'NotFound',
  Locked: 'Locked',
  LimitReached: 'LimitReached',
} as const;
export type QuestionErrorType = (typeof QuestionErrorType)[keyof typeof QuestionErrorType];
export declare namespace QuestionErrorType {
  type None = 'None';
  type NotFound = 'NotFound';
  type Locked = 'Locked';
  type LimitReached = 'LimitReached';
}

/** HooverCanvassingApi.Features.RacePicture.Catalog.RaceCatalogSwitchKind */
export const RaceCatalogSwitchKind = {
  Publish: 'Publish',
  Rollback: 'Rollback',
} as const;
export type RaceCatalogSwitchKind = (typeof RaceCatalogSwitchKind)[keyof typeof RaceCatalogSwitchKind];
export declare namespace RaceCatalogSwitchKind {
  type Publish = 'Publish';
  type Rollback = 'Rollback';
}

/** HooverCanvassingApi.Features.RacePicture.Catalog.RaceChangeParts */
export const RaceChangeParts = {
  None: 'None',
  Identity: 'Identity',
  Stages: 'Stages',
  Dates: 'Dates',
  Status: 'Status',
  Rule: 'Rule',
  Numbers: 'Numbers',
  Notes: 'Notes',
  Sources: 'Sources',
  /** Where the Race's voters are in the voter file. */
  Area: 'Area',
} as const;
export type RaceChangeParts = (typeof RaceChangeParts)[keyof typeof RaceChangeParts];
export declare namespace RaceChangeParts {
  type None = 'None';
  type Identity = 'Identity';
  type Stages = 'Stages';
  type Dates = 'Dates';
  type Status = 'Status';
  type Rule = 'Rule';
  type Numbers = 'Numbers';
  type Notes = 'Notes';
  type Sources = 'Sources';
  type Area = 'Area';
}

/** HooverCanvassingApi.Features.RacePicture.CampaignLink.RaceLinkChange */
export const RaceLinkChange = {
  Linked: 'Linked',
  Relinked: 'Relinked',
  /** The candidate changed the Race they wrote in. It keeps its id, so its goals stay current. */
  Edited: 'Edited',
  PrimaryAnswered: 'PrimaryAnswered',
  Unchanged: 'Unchanged',
} as const;
export type RaceLinkChange = (typeof RaceLinkChange)[keyof typeof RaceLinkChange];
export declare namespace RaceLinkChange {
  type Linked = 'Linked';
  type Relinked = 'Relinked';
  type Edited = 'Edited';
  type PrimaryAnswered = 'PrimaryAnswered';
  type Unchanged = 'Unchanged';
}

/** HooverCanvassingApi.Features.RacePicture.CampaignLink.RacePrimaryStatus */
export const RacePrimaryStatus = {
  NoPrimary: 'NoPrimary',
  Only: 'Only',
  Chosen: 'Chosen',
  NeedsAnswer: 'NeedsAnswer',
  AnswerGone: 'AnswerGone',
} as const;
export type RacePrimaryStatus = (typeof RacePrimaryStatus)[keyof typeof RacePrimaryStatus];
export declare namespace RacePrimaryStatus {
  type NoPrimary = 'NoPrimary';
  type Only = 'Only';
  type Chosen = 'Chosen';
  type NeedsAnswer = 'NeedsAnswer';
  type AnswerGone = 'AnswerGone';
}

/**
 * HooverCanvassingApi.Features.RacePicture.Catalog.RaceStageKind
 *
 * Which election of the Race's cycle a stage is. At most one stage of each kind per Race.
 */
export const RaceStageKind = {
  RepublicanPrimary: 'RepublicanPrimary',
  DemocraticPrimary: 'DemocraticPrimary',
  /** A first round every voter takes part in: California and Washington's top two, Louisiana's open primary. */
  OpenPrimary: 'OpenPrimary',
  /** A nonpartisan round that narrows the field (Wisconsin's February primary, many cities). */
  NonpartisanPrimary: 'NonpartisanPrimary',
  General: 'General',
} as const;
export type RaceStageKind = (typeof RaceStageKind)[keyof typeof RaceStageKind];
export declare namespace RaceStageKind {
  type RepublicanPrimary = 'RepublicanPrimary';
  type DemocraticPrimary = 'DemocraticPrimary';
  type OpenPrimary = 'OpenPrimary';
  type NonpartisanPrimary = 'NonpartisanPrimary';
  type General = 'General';
}

/** HooverCanvassingApi.Features.RacePicture.Catalog.RaceStanding */
export const RaceStanding = {
  Live: 'Live',
  Retired: 'Retired',
} as const;
export type RaceStanding = (typeof RaceStanding)[keyof typeof RaceStanding];
export declare namespace RaceStanding {
  type Live = 'Live';
  type Retired = 'Retired';
}

/**
 * HooverCanvassingApi.Features.Assistant.RaiseState
 *
 * How an open item stands against the last brief the member read: new to them, worse since, or already told.
 */
export const RaiseState = {
  New: 'New',
  Escalated: 'Escalated',
  Raised: 'Raised',
} as const;
export type RaiseState = (typeof RaiseState)[keyof typeof RaiseState];
export declare namespace RaiseState {
  type New = 'New';
  type Escalated = 'Escalated';
  type Raised = 'Raised';
}

/** HooverCanvassingApi.Features.Voice.RateLimitRejection */
export const RateLimitRejection = {
  UserConcurrentLimit: 'UserConcurrentLimit',
  CampaignConcurrentLimit: 'CampaignConcurrentLimit',
  CooldownActive: 'CooldownActive',
  DailyMinutesExceeded: 'DailyMinutesExceeded',
} as const;
export type RateLimitRejection = (typeof RateLimitRejection)[keyof typeof RateLimitRejection];
export declare namespace RateLimitRejection {
  type UserConcurrentLimit = 'UserConcurrentLimit';
  type CampaignConcurrentLimit = 'CampaignConcurrentLimit';
  type CooldownActive = 'CooldownActive';
  type DailyMinutesExceeded = 'DailyMinutesExceeded';
}

/**
 * HooverCanvassingApi.Features.Finance.ReceiptConfirmOutcome
 *
 * Which fork the receipt confirm took. PascalCase on the wire.
 */
export const ReceiptConfirmOutcome = {
  /** Yes → the receipt landed in the Unattached pocket; no Expenditure. */
  Pocketed: 'Pocketed',
  /** No → an Expenditure + ExpenditureAttachment were created directly. */
  ExpenditureCreated: 'ExpenditureCreated',
} as const;
export type ReceiptConfirmOutcome = (typeof ReceiptConfirmOutcome)[keyof typeof ReceiptConfirmOutcome];
export declare namespace ReceiptConfirmOutcome {
  type Pocketed = 'Pocketed';
  type ExpenditureCreated = 'ExpenditureCreated';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.ExpenditureItemizationAggregate.ReceiptItemizationLine
 *
 * GitHub #130 — the itemization "line" a receipt files on. The per-donor itemization aggregate is scoped per line (mirroring the disbursement-side K4 fork): the contribution line (cash + in-kind: FEC line 11 / VA Schedule A/B / AL) has its own per-donor cash+in-kind aggregate, while each NON-contribution receipt files on — and aggregates within — its own line (FEC SA14 offsets / SA15 other-receipts; VA Schedule C). Co-mingling them let an `ReceiptNature.OffsetToOperating` / `ReceiptNature.OtherReceipt` under a donor contact push a sub-threshold contribution over the itemization line and over-state its printed TotalToDate.
 */
export const ReceiptItemizationLine = {
  /**
   * Cash + in-kind contributions (FEC line 11 / VA Sched A/B / AL). Also the catch-all for every other type (Party/Pac/Corporate/FederalTransfer/Other): only the two explicit non-contribution receipts below are carved out.
   *
   * Why `ReceiptNature.FederalTransfer` (and Party/Pac) stay here even though a transfer is not a line-11 contribution (it files on FEC SA12, ADR-048): the federal SA12/SA11B/SA11C emit decision does NOT depend on this fork. Those lines itemize per record regardless of amount (11 CFR 104.3(a)(4)(ii)/(iii)) — the builder's `EmitsScheduleARecord` gate covers them (GitHub #142), so a sub-threshold committee receipt still emits its record with no help from a per-line aggregate. This fork's ONLY job is the seq-22 running total, and co-mingling there cannot involve a transfer/committee: SA12/SA11B/SA11C come from a committee contact, never the individual-donor contact that carries a small gift. Carving them onto their own line would be worse than useless — this enum feeds `ExpenditureItemizationAggregate.NonContributionReceiptTypes`, which VA COMET consumes as its Schedule-C routing set, so a carved-out committee receipt would misroute onto VA Schedule C and drop off VA's contribution schedules A/B (ADR-048).
   */
  Contribution: 'Contribution',
  OffsetToOperating: 'OffsetToOperating',
  OtherReceipt: 'OtherReceipt',
} as const;
export type ReceiptItemizationLine = (typeof ReceiptItemizationLine)[keyof typeof ReceiptItemizationLine];
export declare namespace ReceiptItemizationLine {
  type Contribution = 'Contribution';
  type OffsetToOperating = 'OffsetToOperating';
  type OtherReceipt = 'OtherReceipt';
}

/**
 * HooverCanvassingApi.Features.Finance.ReceiptNature
 *
 * ADR-057's contract step (#344) removed the four person/organization gift members (Individual, Pac, Party, Corporate): a contributor's person-vs-organization identity lives solely on the contact (`ContactKind` + `OrganizationType`), and a plain monetary receipt is the identity-less `ReceiptNature.Cash`. Stored gift-type strings were migrated Individual/Pac/Party/Corporate → Cash by the accompanying EF migration; historical audit strings keep rendering via the retained label-map keys.
 */
export const ReceiptNature = {
  InKind: 'InKind',
  FederalTransfer: 'FederalTransfer',
  /** An offset to operating expenditures: a vendor refund, rebate or returned check received. Files on Schedule A line 14 (SA14) and ALSO feeds the line-7b Total Offsets mirror (net operating 7c = 7a − 7b). */
  OffsetToOperating: 'OffsetToOperating',
  /** Interest, dividends, recount funds, redesignations. Files on Schedule A line 15 (SA15). Not an operating offset (line 7b/14 unaffected). */
  OtherReceipt: 'OtherReceipt',
  /** A catch-all CONTRIBUTOR SOURCE with no cleaner federal-flavored value, added for jurisdictions whose portal offers an explicit "Other" source that none of the other members map to. The Alabama FCPA portal has one (EDI `cbContributionSource` 05); `MapContributionSource` routes it there. Federal has no such user-selectable source, so a federal picker never offers it and the FEC `.fec` emit switch hard-throws on it (it is NOT a modeled federal receipt regime: mislabeling it as an individual would overstate contributions with no signal). AL contributions carry it; federal filings never do. */
  Other: 'Other',
  /** Monetary as opposed to in-kind. It does not mean currency, and a check gift carries it too. Bills and coins are `PaymentMethod.Cash`. ADR-057: plain cash, a receipt NATURE (how the money arrived) carrying no person-vs-organization contributor identity. That identity lives on the contact (`ContactKind` + `OrganizationType`), so a Cash gift's donor-type bucket is resolved by `MapDonorType`'s second axis (the contact subtype). The processor and compiled import write paths land this value at intake (identity taken from the contact's ContactKind); the enum RENAME to ReceiptNature and the remaining write paths (manual entry, StripeConnect) are the later contract step of ADR-057's expand-then-contract sequence. Stored by name (`HasConversion<string>`), so its position after Other is cosmetic. */
  Cash: 'Cash',
} as const;
export type ReceiptNature = (typeof ReceiptNature)[keyof typeof ReceiptNature];
export declare namespace ReceiptNature {
  type InKind = 'InKind';
  type FederalTransfer = 'FederalTransfer';
  type OffsetToOperating = 'OffsetToOperating';
  type OtherReceipt = 'OtherReceipt';
  type Other = 'Other';
  type Cash = 'Cash';
}

/**
 * HooverCanvassingApi.Features.Finance.RecipientOfficeCategory
 *
 * The office a beneficiary committee's candidate seeks, as a state's filing format classifies spending for another candidate: New Jersey's ELEC asks for a benefit type of gubernatorial, legislative or all other candidates. Recorded once on the beneficiary committee's contact. Which categories a jurisdiction offers is declared by `RulesetConfig.RecipientOfficeCategories`. Stored by name.
 */
export const RecipientOfficeCategory = {
  Gubernatorial: 'Gubernatorial',
  Legislative: 'Legislative',
  Other: 'Other',
} as const;
export type RecipientOfficeCategory = (typeof RecipientOfficeCategory)[keyof typeof RecipientOfficeCategory];
export declare namespace RecipientOfficeCategory {
  type Gubernatorial = 'Gubernatorial';
  type Legislative = 'Legislative';
  type Other = 'Other';
}

/**
 * HooverCanvassingApi.Features.WorkOrders.RecipientSource
 *
 * Where a Text send's Recipient list comes from.
 */
export const RecipientSource = {
  CampaignCellPhones: 'CampaignCellPhones',
  UploadedCsv: 'UploadedCsv',
  BoughtList: 'BoughtList',
} as const;
export type RecipientSource = (typeof RecipientSource)[keyof typeof RecipientSource];
export declare namespace RecipientSource {
  type CampaignCellPhones = 'CampaignCellPhones';
  type UploadedCsv = 'UploadedCsv';
  type BoughtList = 'BoughtList';
}

/**
 * HooverCanvassingApi.Features.Finance.Plaid.ReconnectStatus
 *
 * How `IBankConnectionStatusService.ReconnectAsync` resolved.
 */
export const ReconnectStatus = {
  Reconnected: 'Reconnected',
  ConnectionNotFound: 'ConnectionNotFound',
  ConnectionDisconnected: 'ConnectionDisconnected',
} as const;
export type ReconnectStatus = (typeof ReconnectStatus)[keyof typeof ReconnectStatus];
export declare namespace ReconnectStatus {
  type Reconnected = 'Reconnected';
  type ConnectionNotFound = 'ConnectionNotFound';
  type ConnectionDisconnected = 'ConnectionDisconnected';
}

/**
 * HooverCanvassingApi.Features.Finance.Plaid.ReconnectTokenStatus
 *
 * How `IBankConnectionStatusService.CreateReconnectTokenAsync` resolved.
 */
export const ReconnectTokenStatus = {
  Issued: 'Issued',
  ConnectionNotFound: 'ConnectionNotFound',
  ConnectionDisconnected: 'ConnectionDisconnected',
} as const;
export type ReconnectTokenStatus = (typeof ReconnectTokenStatus)[keyof typeof ReconnectTokenStatus];
export declare namespace ReconnectTokenStatus {
  type Issued = 'Issued';
  type ConnectionNotFound = 'ConnectionNotFound';
  type ConnectionDisconnected = 'ConnectionDisconnected';
}

/**
 * HooverCanvassingApi.Features.Fundraising.RecordAcceptance
 *
 * How a federal record came to be a Prospect's.
 */
export const RecordAcceptance = {
  /** The matcher was sure enough on its own; no one has answered for it. */
  Automatic: 'Automatic',
  /** A Fundraiser said "Yes, that's them" — a Match Verdict. */
  Confirmed: 'Confirmed',
} as const;
export type RecordAcceptance = (typeof RecordAcceptance)[keyof typeof RecordAcceptance];
export declare namespace RecordAcceptance {
  type Automatic = 'Automatic';
  type Confirmed = 'Confirmed';
}

/**
 * HooverCanvassingApi.Features.Finance.RecurringDonationCadence
 *
 * The recurring cadence of a `RecurringDonation`. Monthly-only in v1 (the political default; other cadences multiply limit/velocity exposure for no demand — ADR-069). Modeled as an enum so a future cadence is an additive change, not a schema reshape.
 */
export const RecurringDonationCadence = {
  Monthly: 'Monthly',
} as const;
export type RecurringDonationCadence = (typeof RecurringDonationCadence)[keyof typeof RecurringDonationCadence];
export declare namespace RecurringDonationCadence {
  type Monthly = 'Monthly';
}

/**
 * HooverCanvassingApi.Features.Finance.RecurringDonationStatus
 *
 * Lifecycle of a `RecurringDonation` mirror. Created `RecurringDonationStatus.Active` on `customer.subscription.created`; a later slice moves it to `RecurringDonationStatus.Ended` (reached its `cancel_at` cutoff) or `RecurringDonationStatus.Canceled` (treasurer/admin/dunning cancel) via the subscription-lifecycle webhooks (ADR-069).
 */
export const RecurringDonationStatus = {
  Active: 'Active',
  Ended: 'Ended',
  Canceled: 'Canceled',
} as const;
export type RecurringDonationStatus = (typeof RecurringDonationStatus)[keyof typeof RecurringDonationStatus];
export declare namespace RecurringDonationStatus {
  type Active = 'Active';
  type Ended = 'Ended';
  type Canceled = 'Canceled';
}

/**
 * HooverCanvassingApi.Features.Finance.StripeConnect.Recurring.RecurringDonorCancelOutcome
 *
 * The outcome of a per-donor recurring cancel.
 */
export const RecurringDonorCancelOutcome = {
  /** The donor's active series was canceled in Stripe and its mirror moved to Canceled. */
  Canceled: 'Canceled',
  /** Nothing live remained — the mirror was already terminal. Idempotent no-op. */
  AlreadyInactive: 'AlreadyInactive',
} as const;
export type RecurringDonorCancelOutcome = (typeof RecurringDonorCancelOutcome)[keyof typeof RecurringDonorCancelOutcome];
export declare namespace RecurringDonorCancelOutcome {
  type Canceled = 'Canceled';
  type AlreadyInactive = 'AlreadyInactive';
}

/**
 * HooverCanvassingApi.Features.Finance.RefundNettingBasis
 *
 * ADR-028 — the per-jurisdiction cash-on-hand refund-netting policy. Serialized as a string in ruleset JSON.
 */
export const RefundNettingBasis = {
  /** Alabama — files gross; refunds are NOT deducted from COH (a return is a portal amendment to the original contribution, not an in-period cash event). */
  Gross: 'Gross',
  /** Federal — nets refunds out; in-period refunds reduce COH. */
  NetRefunds: 'NetRefunds',
  /** Virginia — the third way (Finance CLAUDE.md "may need a third way, not federal's or AL's"): contributions file GROSS (never netted onto the receipt, like AL) yet an issued contribution refund is a real Schedule-D DISBURSEMENT that DOES reduce COH (like federal, unlike AL). Resolves to deduct=true, so `FinanceCashMath.ComputeEndingCash` agrees with the VA COMET builder's `ComputeScheduleGh`, which folds the refund into the Schedule-D disbursement total unconditionally. */
  GrossWithRefundDisbursement: 'GrossWithRefundDisbursement',
} as const;
export type RefundNettingBasis = (typeof RefundNettingBasis)[keyof typeof RefundNettingBasis];
export declare namespace RefundNettingBasis {
  type Gross = 'Gross';
  type NetRefunds = 'NetRefunds';
  type GrossWithRefundDisbursement = 'GrossWithRefundDisbursement';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.Alabama.Replay.ReplayChangeClassification
 *
 * The verdict axis: was this change the reader-switch's intended effect, or a red flag?
 */
export const ReplayChangeClassification = {
  /** A `cbContributionSource` flip, or a contributor name-block field that moved alongside one on the same contribution (the person↔org shape change the source code drives). This is exactly what ADR-057's reader-switch is meant to change. */
  IntendedContributorSource: 'IntendedContributorSource',
  /** Anything else — a red flag the harness surfaces for a human to inspect. */
  Unintended: 'Unintended',
} as const;
export type ReplayChangeClassification = (typeof ReplayChangeClassification)[keyof typeof ReplayChangeClassification];
export declare namespace ReplayChangeClassification {
  type IntendedContributorSource = 'IntendedContributorSource';
  type Unintended = 'Unintended';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.Alabama.Replay.ReplayChangeKind
 *
 * The mechanical shape of a single diff between the pinned and replayed bytes.
 */
export const ReplayChangeKind = {
  /** A field present on both sides with different text. */
  CellValue: 'CellValue',
  /** A field the replay emits that the original did not. */
  FieldAdded: 'FieldAdded',
  /** A field the original emitted that the replay does not. */
  FieldRemoved: 'FieldRemoved',
  /** A whole record the replay emits that the original did not. */
  RecordAdded: 'RecordAdded',
  /** A whole record the original emitted that the replay does not. */
  RecordRemoved: 'RecordRemoved',
} as const;
export type ReplayChangeKind = (typeof ReplayChangeKind)[keyof typeof ReplayChangeKind];
export declare namespace ReplayChangeKind {
  type CellValue = 'CellValue';
  type FieldAdded = 'FieldAdded';
  type FieldRemoved = 'FieldRemoved';
  type RecordAdded = 'RecordAdded';
  type RecordRemoved = 'RecordRemoved';
}

/**
 * HooverCanvassingApi.Features.Assistant.ReplayGuard
 *
 * How an allowlisted action stops a crashed app from running the same request twice. The app sends the stored request, so a retry after a lost report must be harmless.
 */
export const ReplayGuard = {
  /** The request carries a concurrency token the endpoint enforces; a second run is a 409. */
  Token: 'Token',
  /** Running the request twice ends in the same state. */
  Idempotent: 'Idempotent',
} as const;
export type ReplayGuard = (typeof ReplayGuard)[keyof typeof ReplayGuard];
export declare namespace ReplayGuard {
  type Token = 'Token';
  type Idempotent = 'Idempotent';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.Alabama.Replay.ReplayRecordKind
 *
 * Which AL EDI record shape a changed cell belongs to.
 */
export const ReplayRecordKind = {
  /** The `<control>` file header (committeeId / committeeName / …). */
  Control: 'Control',
  Contribution: 'Contribution',
  Expenditure: 'Expenditure',
  /** A loan `<receipt reReceiptType=07>`. */
  Receipt: 'Receipt',
} as const;
export type ReplayRecordKind = (typeof ReplayRecordKind)[keyof typeof ReplayRecordKind];
export declare namespace ReplayRecordKind {
  type Control = 'Control';
  type Contribution = 'Contribution';
  type Expenditure = 'Expenditure';
  type Receipt = 'Receipt';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.ReportRowInclusion
 *
 * Per-row inclusion discriminator (decision #10). Drives FLW-002's excluded-rows subsection; for Filed/Amended reports the value reflects file-time facts, never live status.
 */
export const ReportRowInclusion = {
  /** On the filing. */
  Included: 'Included',
  /** In period, excluded because Pending and not attested. */
  ExcludedPending: 'ExcludedPending',
  /** In period, excluded because the money row was Voided (ADR-016). */
  ExcludedVoided: 'ExcludedVoided',
  /** Attested-Pending row whose live `ProcessorStatus` moved to a non-filable value before filing (FPD-020 `excludedDueToStatusDrift`). */
  ExcludedStatusDrift: 'ExcludedStatusDrift',
  /** Already reported to the state (its per-row `LedgerStatus == Reported` stamp) but the period is not yet filed/closed — folded back into the DISPLAY so an open report that was uploaded still shows its itemized rows + totals, matching the cash summary which already folds these rows. On the report, NOT on the next upload/filing (so it is deliberately not `ReportRowInclusion.Included` — the wire/export set reads `Included`-only and must never re-emit an already-reported row). See `Features/Finance/CLAUDE.md` → "the reported stamp vs the period lock". */
  Reported: 'Reported',
} as const;
export type ReportRowInclusion = (typeof ReportRowInclusion)[keyof typeof ReportRowInclusion];
export declare namespace ReportRowInclusion {
  type Included = 'Included';
  type ExcludedPending = 'ExcludedPending';
  type ExcludedVoided = 'ExcludedVoided';
  type ExcludedStatusDrift = 'ExcludedStatusDrift';
  type Reported = 'Reported';
}

/** HooverCanvassingApi.Features.Finance.ReportStatus */
export const ReportStatus = {
  NotStarted: 'NotStarted',
  Ready: 'Ready',
  Filed: 'Filed',
  NeedsAmendment: 'NeedsAmendment',
  Amended: 'Amended',
} as const;
export type ReportStatus = (typeof ReportStatus)[keyof typeof ReportStatus];
export declare namespace ReportStatus {
  type NotStarted = 'NotStarted';
  type Ready = 'Ready';
  type Filed = 'Filed';
  type NeedsAmendment = 'NeedsAmendment';
  type Amended = 'Amended';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.ReportSummaryKind
 *
 * ADR-051 — which report-summary view a `ComplianceReport` renders on the report-detail page (and, once the preview is wired onto the same projection, the Close-hub preview). The single wire discriminator the frontend view switch keys on, replacing the retired `federalSummary != null` / `vaSchedules != null` / `stateCode == 'AL'` chain. A jurisdiction with no bespoke view resolves to `ReportSummaryKind.Flat` (the neutral by-type / by-category breakdown), so onboarding a state is one new value + one switch case, nowhere else.
 */
export const ReportSummaryKind = {
  /** Federal Form 3 Detailed Summary Page. */
  Federal: 'Federal',
  /** Virginia COMET Statement-of-funds + Schedules A–F. */
  Virginia: 'Virginia',
  /** Alabama FCPA Statement-of-funds + Schedules A/B/D. */
  Alabama: 'Alabama',
  /** North Carolina CFRS Detailed Summary (CRO-1100): reporting-period + election-cycle cover totals. */
  NorthCarolina: 'NorthCarolina',
  /** The neutral flat/by-type breakdown — the default for any other jurisdiction. */
  Flat: 'Flat',
} as const;
export type ReportSummaryKind = (typeof ReportSummaryKind)[keyof typeof ReportSummaryKind];
export declare namespace ReportSummaryKind {
  type Federal = 'Federal';
  type Virginia = 'Virginia';
  type Alabama = 'Alabama';
  type NorthCarolina = 'NorthCarolina';
  type Flat = 'Flat';
}

/** HooverCanvassingApi.Features.Finance.ResolutionType */
export const ResolutionType = {
  PresumptiveRedesignation: 'PresumptiveRedesignation',
  WrittenRedesignation: 'WrittenRedesignation',
  Reattribution: 'Reattribution',
  Refund: 'Refund',
} as const;
export type ResolutionType = (typeof ResolutionType)[keyof typeof ResolutionType];
export declare namespace ResolutionType {
  type PresumptiveRedesignation = 'PresumptiveRedesignation';
  type WrittenRedesignation = 'WrittenRedesignation';
  type Reattribution = 'Reattribution';
  type Refund = 'Refund';
}

/** HooverCanvassingApi.Features.Volunteers.ResourceCategory */
export const ResourceCategory = {
  VoterResources: 'VoterResources',
  CampaignInformation: 'CampaignInformation',
  VolunteerResources: 'VolunteerResources',
  GeneralResources: 'GeneralResources',
} as const;
export type ResourceCategory = (typeof ResourceCategory)[keyof typeof ResourceCategory];
export declare namespace ResourceCategory {
  type VoterResources = 'VoterResources';
  type CampaignInformation = 'CampaignInformation';
  type VolunteerResources = 'VolunteerResources';
  type GeneralResources = 'GeneralResources';
}

/**
 * HooverCanvassingApi.Features.Assistant.RunOutcome
 *
 * `RunOutcome.Preempted`: a background run the member's message ended, since chat wins; its claims go back.
 */
export const RunOutcome = {
  Completed: 'Completed',
  Failed: 'Failed',
  TimedOut: 'TimedOut',
  DispatchFailed: 'DispatchFailed',
  Preempted: 'Preempted',
} as const;
export type RunOutcome = (typeof RunOutcome)[keyof typeof RunOutcome];
export declare namespace RunOutcome {
  type Completed = 'Completed';
  type Failed = 'Failed';
  type TimedOut = 'TimedOut';
  type DispatchFailed = 'DispatchFailed';
  type Preempted = 'Preempted';
}

/**
 * HooverCanvassingApi.Features.Assistant.RunTrigger
 *
 * `RunTrigger.MemoryTidy`: nightly housekeeping on the member's notes; nobody reads it and it posts nothing.
 */
export const RunTrigger = {
  UserMessage: 'UserMessage',
  Wakeup: 'Wakeup',
  Event: 'Event',
  DailyFloor: 'DailyFloor',
  MemoryTidy: 'MemoryTidy',
} as const;
export type RunTrigger = (typeof RunTrigger)[keyof typeof RunTrigger];
export declare namespace RunTrigger {
  type UserMessage = 'UserMessage';
  type Wakeup = 'Wakeup';
  type Event = 'Event';
  type DailyFloor = 'DailyFloor';
  type MemoryTidy = 'MemoryTidy';
}

/**
 * HooverCanvassingApi.Features.Finance.ScheduleCadence
 *
 * How a schedule entry repeats across its pre-election window. `Single` (the default) is the original one-deadline-per-entry behavior. `Monthly` / `Weekly` expand the window into a per-period cadence (AL pre-election monthly/weekly reports) via `ReportingCadenceExpander`.
 */
export const ScheduleCadence = {
  Single: 'Single',
  Monthly: 'Monthly',
  Weekly: 'Weekly',
} as const;
export type ScheduleCadence = (typeof ScheduleCadence)[keyof typeof ScheduleCadence];
export declare namespace ScheduleCadence {
  type Single = 'Single';
  type Monthly = 'Monthly';
  type Weekly = 'Weekly';
}

/**
 * HooverCanvassingApi.Features.Finance.ScheduleConfigurationGapType
 *
 * Filing-schedule configuration the chosen ruleset needs but the campaign hasn't set, so part of the calendar silently doesn't generate. Alert, don't block: it is surfaced on the deadline list so the treasurer sees WHY reports are missing.
 */
export const ScheduleConfigurationGapType = {
  /** The ruleset schedules election-relative reports, but no election dates are set. */
  MissingElectionDates: 'MissingElectionDates',
  /** The ruleset schedules recurring reports, but no committee formation date is set. */
  MissingCommitteeFormationDate: 'MissingCommitteeFormationDate',
  /** ADR-035: the ruleset has general-election-month-tagged entries and a general date is set, but its month matches no variant's tag (e.g. an August date for VA's May/November schedules). An unsupported-schedule notice (special election), NOT an invalid date. */
  UnrecognizedGeneralElectionMonth: 'UnrecognizedGeneralElectionMonth',
  /** ADR-035: the ruleset has general-election-month-tagged entries and another election date is set, but the general date — which routes the schedule variant (VA's §947.6 vs §947.7) — is blank, so the periodic calendar can't generate. Scoped so it does not double-fire with `ScheduleConfigurationGapType.MissingElectionDates` (the all-blank case). */
  GeneralDateNeededForScheduleRouting: 'GeneralDateNeededForScheduleRouting',
  /** ADR-055 (jurisdiction-agnostic): the campaign generates deadlines dated beyond the stored schedule's `verifiedThroughYear` — the derivation may have outgrown the last cycle we confirmed against the regulator's published schedule. A non-blocking "re-check" nudge. */
  ScheduleVerificationStale: 'ScheduleVerificationStale',
  /** §163-278.40B–E: the ruleset has municipal-election-method-gated entries applicable to this campaign (a municipal committee), but the treasurer hasn't declared the method — so the method-specific pre-election report set can't be built. The reviewable-setup nudge that makes the missing pick loud instead of silently generating a wrong/empty schedule; the deadlines tab regenerates on save. */
  MissingMunicipalElectionMethod: 'MissingMunicipalElectionMethod',
} as const;
export type ScheduleConfigurationGapType = (typeof ScheduleConfigurationGapType)[keyof typeof ScheduleConfigurationGapType];
export declare namespace ScheduleConfigurationGapType {
  type MissingElectionDates = 'MissingElectionDates';
  type MissingCommitteeFormationDate = 'MissingCommitteeFormationDate';
  type UnrecognizedGeneralElectionMonth = 'UnrecognizedGeneralElectionMonth';
  type GeneralDateNeededForScheduleRouting = 'GeneralDateNeededForScheduleRouting';
  type ScheduleVerificationStale = 'ScheduleVerificationStale';
  type MissingMunicipalElectionMethod = 'MissingMunicipalElectionMethod';
}

/** HooverCanvassingApi.Features.Finance.ScheduleKind */
export const ScheduleKind = {
  Primary: 'Primary',
  PreElection: 'PreElection',
  PostElection: 'PostElection',
  RapidReport: 'RapidReport',
} as const;
export type ScheduleKind = (typeof ScheduleKind)[keyof typeof ScheduleKind];
export declare namespace ScheduleKind {
  type Primary = 'Primary';
  type PreElection = 'PreElection';
  type PostElection = 'PostElection';
  type RapidReport = 'RapidReport';
}

/**
 * HooverCanvassingApi.Features.Assistant.ScoutRole
 *
 * Who a Scout route is for. A member is anyone Scout admits; a planner is Manage in an admitted domain.
 */
export const ScoutRole = {
  Member: 'Member',
  Planner: 'Planner',
} as const;
export type ScoutRole = (typeof ScoutRole)[keyof typeof ScoutRole];
export declare namespace ScoutRole {
  type Member = 'Member';
  type Planner = 'Planner';
}

/**
 * HooverCanvassingApi.Features.Calendar.Sync.ShiftCalendarAction
 *
 * Action to perform on a shift's calendar event.
 */
export const ShiftCalendarAction = {
  /** Create or update the calendar event for this shift's current claimer. */
  Sync: 'Sync',
  /** Remove the calendar event(s) for this shift. */
  Remove: 'Remove',
} as const;
export type ShiftCalendarAction = (typeof ShiftCalendarAction)[keyof typeof ShiftCalendarAction];
export declare namespace ShiftCalendarAction {
  type Sync = 'Sync';
  type Remove = 'Remove';
}

/**
 * HooverCanvassingApi.Features.Shifts.ShiftCalendarSyncStatus
 *
 * User-visible rollup of a shift's external-calendar sync, surfaced on the shift DTO. Distinct from `ShiftSyncStatus` (which is per `ShiftCalendarLink`/connection and internal to the retry loop); this is the single status a manager or volunteer sees on the shift itself.
 */
export const ShiftCalendarSyncStatus = {
  /** No calendar sync is required — the shift has no assignee/claimer. */
  None: 'None',
  /** A provider write has been enqueued and is awaiting the background processor. */
  Pending: 'Pending',
  /** The provider write succeeded, or was a no-op (user has no connected calendar). */
  Synced: 'Synced',
  /** Background retries were exhausted; the calendar is out of sync with the shift. */
  Failed: 'Failed',
} as const;
export type ShiftCalendarSyncStatus = (typeof ShiftCalendarSyncStatus)[keyof typeof ShiftCalendarSyncStatus];
export declare namespace ShiftCalendarSyncStatus {
  type None = 'None';
  type Pending = 'Pending';
  type Synced = 'Synced';
  type Failed = 'Failed';
}

/**
 * HooverCanvassingApi.Features.Shifts.ShiftEffectiveStatus
 *
 * The status a shift shows: its `ShiftStatus`, except that a shift claimed but never started whose date has passed shows as `ShiftEffectiveStatus.NoShow`.
 */
export const ShiftEffectiveStatus = {
  /** Nobody has claimed it. */
  Available: 'Available',
  /** A user has signed up but not started. */
  Claimed: 'Claimed',
  /** The user is canvassing now. */
  InProgress: 'InProgress',
  /** The shift is over. */
  Completed: 'Completed',
  /** Claimed, never started, and its date has passed. */
  NoShow: 'NoShow',
} as const;
export type ShiftEffectiveStatus = (typeof ShiftEffectiveStatus)[keyof typeof ShiftEffectiveStatus];
export declare namespace ShiftEffectiveStatus {
  type Available = 'Available';
  type Claimed = 'Claimed';
  type InProgress = 'InProgress';
  type Completed = 'Completed';
  type NoShow = 'NoShow';
}

/**
 * HooverCanvassingApi.Features.Shifts.ShiftStartOverrideReason
 *
 * Why a shift was started without a passing location check.
 */
export const ShiftStartOverrideReason = {
  /** The canvasser tapped "Start anyway" on the proximity gate. */
  SelfOverride: 'SelfOverride',
  /** A platform admin's "Skip Location Check" toggle bypassed the gate. Reserved — the admin-skip path does not record yet; recording it later reuses this row. */
  AdminSkip: 'AdminSkip',
} as const;
export type ShiftStartOverrideReason = (typeof ShiftStartOverrideReason)[keyof typeof ShiftStartOverrideReason];
export declare namespace ShiftStartOverrideReason {
  type SelfOverride = 'SelfOverride';
  type AdminSkip = 'AdminSkip';
}

/**
 * HooverCanvassingApi.Features.Shifts.ShiftStatus
 *
 * Where a shift is in its lifecycle.
 */
export const ShiftStatus = {
  /** Nobody has claimed it. The default. */
  Available: 'Available',
  /** A user has signed up but not started. */
  Claimed: 'Claimed',
  /** The user is canvassing now. */
  InProgress: 'InProgress',
  /** The shift is over: the user ended it or its time passed. */
  Completed: 'Completed',
} as const;
export type ShiftStatus = (typeof ShiftStatus)[keyof typeof ShiftStatus];
export declare namespace ShiftStatus {
  type Available = 'Available';
  type Claimed = 'Claimed';
  type InProgress = 'InProgress';
  type Completed = 'Completed';
}

/**
 * HooverCanvassingApi.Features.Shifts.ShiftSyncStatus
 *
 * Sync status for a shift-calendar link.
 */
export const ShiftSyncStatus = {
  /** Initial state or waiting for first sync attempt. */
  Pending: 'Pending',
  /** Successfully synced to calendar. */
  Synced: 'Synced',
  /** Sync failed, will be retried. */
  Failed: 'Failed',
} as const;
export type ShiftSyncStatus = (typeof ShiftSyncStatus)[keyof typeof ShiftSyncStatus];
export declare namespace ShiftSyncStatus {
  type Pending = 'Pending';
  type Synced = 'Synced';
  type Failed = 'Failed';
}

/**
 * HooverCanvassingApi.Features.Finance.SignConvention
 *
 * How a bank's export encodes credit vs. debit amounts. SignedAmount = single column, negative = debit. DebitCreditColumns = two columns. ParensForNegatives = single column with "(amount)" denoting debits.
 */
export const SignConvention = {
  SignedAmount: 'SignedAmount',
  DebitCreditColumns: 'DebitCreditColumns',
  ParensForNegatives: 'ParensForNegatives',
} as const;
export type SignConvention = (typeof SignConvention)[keyof typeof SignConvention];
export declare namespace SignConvention {
  type SignedAmount = 'SignedAmount';
  type DebitCreditColumns = 'DebitCreditColumns';
  type ParensForNegatives = 'ParensForNegatives';
}

/**
 * HooverCanvassingApi.Features.Fundraising.SortDirection
 *
 * Which way a column sorts: ascending (A–Z, smallest first) or descending.
 */
export const SortDirection = {
  Asc: 'Asc',
  Desc: 'Desc',
} as const;
export type SortDirection = (typeof SortDirection)[keyof typeof SortDirection];
export declare namespace SortDirection {
  type Asc = 'Asc';
  type Desc = 'Desc';
}

/**
 * HooverCanvassingApi.Features.Finance.Sort.SortInboxStatusFilter
 *
 * ADR-026 (PR-A) — which slice of the ledger `ISortInboxService.GetAsync` returns in its `Items`. `SortInboxStatusFilter.All` is the full all-time ledger (the display surface — unchanged; paginated in a later PR). `SortInboxStatusFilter.NeedsAction` is the bounded WORK surface: the NeedsAction-only slice, unpaginated, bounded by un-triaged work rather than history, so a NeedsAction row dated deep in history is never stranded off a display page. The envelope scalars are the same either way.
 */
export const SortInboxStatusFilter = {
  /** The full all-time ledger (NeedsAction + Done + Reported). */
  All: 'All',
  /** The NeedsAction-only work feed — every NeedsAction row, no paging. */
  NeedsAction: 'NeedsAction',
} as const;
export type SortInboxStatusFilter = (typeof SortInboxStatusFilter)[keyof typeof SortInboxStatusFilter];
export declare namespace SortInboxStatusFilter {
  type All = 'All';
  type NeedsAction = 'NeedsAction';
}

/**
 * HooverCanvassingApi.Features.Finance.StripeConnect.Onboarding.StripeConnectBusinessType
 *
 * Committee legal shape for the Stripe account. Political committees (527s) are owner-less, so they onboard as non_profit — never as a private company, which would wrongly trigger beneficial-owner (>25%) collection. The only committee-level choice is whether it is formally incorporated; unincorporated (most local/state PACs + candidate committees) is the default.
 */
export const StripeConnectBusinessType = {
  NonProfitUnincorporated: 'NonProfitUnincorporated',
  NonProfitIncorporated: 'NonProfitIncorporated',
  /** The lone non-committee fallback. */
  Individual: 'Individual',
} as const;
export type StripeConnectBusinessType = (typeof StripeConnectBusinessType)[keyof typeof StripeConnectBusinessType];
export declare namespace StripeConnectBusinessType {
  type NonProfitUnincorporated = 'NonProfitUnincorporated';
  type NonProfitIncorporated = 'NonProfitIncorporated';
  type Individual = 'Individual';
}

/**
 * HooverCanvassingApi.Features.Finance.StripeConnect.Onboarding.StripeConnectPanelState
 *
 * The treasurer-facing health of a campaign's Stripe Connect donation account. The wire exposes these enum NAMES (PascalCase) on `PaymentsStatusDto.PanelState`; the frontend translates each to treasurer copy at the render boundary (never leak an engineer enum name into treasurer text). Ordered from best to worst.
 */
export const StripeConnectPanelState = {
  /** Charges + payouts live and nothing outstanding — the page can accept gifts. */
  Healthy: 'Healthy',
  /** Requirements are due but not yet overdue — the treasurer should finish setup. */
  ActionNeeded: 'ActionNeeded',
  /** A required detail is past its deadline — payments are (or will be) paused. */
  PastDue: 'PastDue',
  /** Stripe has restricted / rejected / is reviewing the account — platform-assisted escalation. */
  Restricted: 'Restricted',
} as const;
export type StripeConnectPanelState = (typeof StripeConnectPanelState)[keyof typeof StripeConnectPanelState];
export declare namespace StripeConnectPanelState {
  type Healthy = 'Healthy';
  type ActionNeeded = 'ActionNeeded';
  type PastDue = 'PastDue';
  type Restricted = 'Restricted';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.SubmissionPackageCycleRowKind
 *
 * Which ledger table a `SubmissionPackageCycleRow` points at. Stored by name, so reordering the enum cannot relabel existing evidence.
 */
export const SubmissionPackageCycleRowKind = {
  Contribution: 'Contribution',
  Expenditure: 'Expenditure',
  Refund: 'Refund',
  LoanIncurred: 'LoanIncurred',
} as const;
export type SubmissionPackageCycleRowKind = (typeof SubmissionPackageCycleRowKind)[keyof typeof SubmissionPackageCycleRowKind];
export declare namespace SubmissionPackageCycleRowKind {
  type Contribution = 'Contribution';
  type Expenditure = 'Expenditure';
  type Refund = 'Refund';
  type LoanIncurred = 'LoanIncurred';
}

/**
 * HooverCanvassingApi.Features.Finance.SubmissionPackageExceptionType
 *
 * FPD-001 — kind of blocking exception a treasurer attested to during preflight. Carried on `SubmissionPackageAttestation` and on the `FilingExceptionAttested` audit action. Adding a value = new entry here + document in `apps/api/Features/Finance/CLAUDE.md`.
 */
export const SubmissionPackageExceptionType = {
  /** Aged-overdue contribution past the settlement-aging threshold; the treasurer attested with a reason (FPD-007 § Closure obligation). */
  AgedOverdueUnsettled: 'AgedOverdueUnsettled',
  /** Aged-overdue contribution where the treasurer manually recorded a void via `ContributionRefund` — the audit trail records "treasurer chose to write this off." */
  AgedOverdueDismissed: 'AgedOverdueDismissed',
  /** FPD-020 — RETIRED as an attestable classifier. Donor identity is now a Confirmed-evidence prerequisite per Ala. Code §17-5-8: a contribution whose donor identity is incomplete cannot be filed at all, and no attestation closes that gap. The value is retained so historical `FilingExceptionAttested(DonorIdentityUnresolved)` audit rows + existing `SubmissionPackageAttestation` rows referenced for re-file byte-identity (FPD-018 transitional dual-gate) remain readable. `FilingPreflightService` does not surface it; the Heads-up card classifier picker does not offer it. */
  DonorIdentityUnresolved: 'DonorIdentityUnresolved',
  /** Manual `Pending` contribution included before the bank deposit arrived; treasurer typed the relevant identifier (check #, deposit slip, wire reference) in the reason. Narrow meaning post-FPD-020 — processor-imported Pending rows whose transaction hasn't completed use `SubmissionPackageExceptionType.ProcessorPreSettlement` instead. */
  ManualEntryPreDeposit: 'ManualEntryPreDeposit',
  /** FPD-020 — processor-imported `Pending` contribution whose transaction has not completed yet (the Anderson case — e.g., a pending ACH/echeck that could still bounce or be cancelled). Distinct from `SubmissionPackageExceptionType.ManualEntryPreDeposit`, which targets hand-entered paper checks / wires / cash. The Heads-up card's per-row classifier inference selects between the two based on whether the contribution is processor-imported (`ProcessorContributionId IS NOT NULL`). */
  ProcessorPreSettlement: 'ProcessorPreSettlement',
  /** FPD-053 (RETIRED by ADR-011) — the preflight balance-confirmation step is gone. The value is retained only so historical `FilingExceptionAttested` audit rows + existing `SubmissionPackageAttestation` rows referencing it remain readable; no preflight collector surfaces it and no new attestation writes it. */
  BalanceVarianceAtFiling: 'BalanceVarianceAtFiling',
} as const;
export type SubmissionPackageExceptionType = (typeof SubmissionPackageExceptionType)[keyof typeof SubmissionPackageExceptionType];
export declare namespace SubmissionPackageExceptionType {
  type AgedOverdueUnsettled = 'AgedOverdueUnsettled';
  type AgedOverdueDismissed = 'AgedOverdueDismissed';
  type DonorIdentityUnresolved = 'DonorIdentityUnresolved';
  type ManualEntryPreDeposit = 'ManualEntryPreDeposit';
  type ProcessorPreSettlement = 'ProcessorPreSettlement';
  type BalanceVarianceAtFiling = 'BalanceVarianceAtFiling';
}

/** HooverCanvassingApi.Common.Models.SubscriptionStatus */
export const SubscriptionStatus = {
  Trialing: 'Trialing',
  Active: 'Active',
  PastDue: 'PastDue',
  Canceled: 'Canceled',
  Unpaid: 'Unpaid',
} as const;
export type SubscriptionStatus = (typeof SubscriptionStatus)[keyof typeof SubscriptionStatus];
export declare namespace SubscriptionStatus {
  type Trialing = 'Trialing';
  type Active = 'Active';
  type PastDue = 'PastDue';
  type Canceled = 'Canceled';
  type Unpaid = 'Unpaid';
}

/**
 * HooverCanvassingApi.Features.Finance.Plaid.SyncNowStatus
 *
 * How an on-demand "sync now" resolved.
 */
export const SyncNowStatus = {
  /** The sync ran; the result carries the pull counts. */
  Ran: 'Ran',
  /** A sync was already in flight for this connection (the per-connection gate refused); the API returns 409. */
  AlreadyInProgress: 'AlreadyInProgress',
  /** The connection isn't in this campaign; the API returns 404. */
  ConnectionNotFound: 'ConnectionNotFound',
} as const;
export type SyncNowStatus = (typeof SyncNowStatus)[keyof typeof SyncNowStatus];
export declare namespace SyncNowStatus {
  type Ran = 'Ran';
  type AlreadyInProgress = 'AlreadyInProgress';
  type ConnectionNotFound = 'ConnectionNotFound';
}

/**
 * HooverCanvassingApi.Features.Calendar.Sync.TaskCalendarAction
 *
 * Action to perform on a task's calendar event.
 */
export const TaskCalendarAction = {
  /** Create or update the calendar event for this task. */
  Sync: 'Sync',
  /** Remove the calendar event for this task. */
  Remove: 'Remove',
  /** Mark the calendar event as completed (title prefix). */
  Complete: 'Complete',
} as const;
export type TaskCalendarAction = (typeof TaskCalendarAction)[keyof typeof TaskCalendarAction];
export declare namespace TaskCalendarAction {
  type Sync = 'Sync';
  type Remove = 'Remove';
  type Complete = 'Complete';
}

/** HooverCanvassingApi.Features.Tasks.TaskSource */
export const TaskSource = {
  ManagerAssigned: 'ManagerAssigned',
  SystemGenerated: 'SystemGenerated',
} as const;
export type TaskSource = (typeof TaskSource)[keyof typeof TaskSource];
export declare namespace TaskSource {
  type ManagerAssigned = 'ManagerAssigned';
  type SystemGenerated = 'SystemGenerated';
}

/** HooverCanvassingApi.Features.Tasks.TaskStatus */
export const TaskStatus = {
  Pending: 'Pending',
  Accepted: 'Accepted',
  Declined: 'Declined',
  Completed: 'Completed',
} as const;
export type TaskStatus = (typeof TaskStatus)[keyof typeof TaskStatus];
export declare namespace TaskStatus {
  type Pending = 'Pending';
  type Accepted = 'Accepted';
  type Declined = 'Declined';
  type Completed = 'Completed';
}

/** HooverCanvassingApi.Common.Models.TeamSize */
export const TeamSize = {
  Solo: 'Solo',
  Small: 'Small',
  Medium: 'Medium',
  Large: 'Large',
} as const;
export type TeamSize = (typeof TeamSize)[keyof typeof TeamSize];
export declare namespace TeamSize {
  type Solo = 'Solo';
  type Small = 'Small';
  type Medium = 'Medium';
  type Large = 'Large';
}

/** HooverCanvassingApi.Common.Models.TenantCampaignStatus */
export const TenantCampaignStatus = {
  Active: 'Active',
  Suspended: 'Suspended',
  Cancelled: 'Cancelled',
} as const;
export type TenantCampaignStatus = (typeof TenantCampaignStatus)[keyof typeof TenantCampaignStatus];
export declare namespace TenantCampaignStatus {
  type Active = 'Active';
  type Suspended = 'Suspended';
  type Cancelled = 'Cancelled';
}

/**
 * HooverCanvassingApi.Common.Tenancy.TenantIsolationMode
 *
 * Defines the database isolation strategy for multi-tenant data. This determines how tenant data is physically separated in the database layer.
 */
export const TenantIsolationMode = {
  /** Phase 1 (Launch): Single PostgreSQL server with separate databases per subsidiary. Each tenant gets its own database (canvassing_gop, canvassing_democrat, canvassing_nonpartisan). Provides strong isolation at low cost (~$15/month for one server). All databases use the "public" schema (standard EF Core behavior). */
  DatabasePerTenant: 'DatabasePerTenant',
  /** Phase 2 (Enterprise/Scale): Completely separate database servers per subsidiary. Maximum isolation for compliance, data residency, or contractual requirements. Each subsidiary can be hosted in different regions. Migration from DatabasePerTenant is a configuration-only change. */
  ServerPerTenant: 'ServerPerTenant',
} as const;
export type TenantIsolationMode = (typeof TenantIsolationMode)[keyof typeof TenantIsolationMode];
export declare namespace TenantIsolationMode {
  type DatabasePerTenant = 'DatabasePerTenant';
  type ServerPerTenant = 'ServerPerTenant';
}

/**
 * HooverCanvassingApi.Features.WorkOrders.TextSendWaitingOn
 *
 * What a Paid Text send waits on before the Vendor handoff. `TextSendWaitingOn.None` for every order that isn't a Paid Text send. When both are pending, `TextSendWaitingOn.Registration` is reported.
 */
export const TextSendWaitingOn = {
  None: 'None',
  /** The campaign's messaging profile isn't Provisioned yet. */
  Registration: 'Registration',
  /** The Recipient list isn't frozen yet: the paired Voter data order hasn't been fulfilled. */
  List: 'List',
} as const;
export type TextSendWaitingOn = (typeof TextSendWaitingOn)[keyof typeof TextSendWaitingOn];
export declare namespace TextSendWaitingOn {
  type None = 'None';
  type Registration = 'Registration';
  type List = 'List';
}

/**
 * HooverCanvassingApi.Features.Finance.ThresholdCommitteeApplicability
 *
 * How a reporting-schedule entry relates to the NC under-$1,000 "threshold committee" latch (§163-278.9(a), `CampaignFinanceSettings.IsThresholdCommittee`) — the fourth applicability gate DeadlineService applies, sibling to office-level / general-election-month / election-method.
 *
 * - `Any` — the entry generates regardless of the latch (the organizational report, the 48-hour rapid notice, the municipal pre-election set). The default, so every jurisdiction that leaves it unset is inert w.r.t. the latch.
 * - `NonThresholdCommittee` — the periodic family (QTR1–QTR4 / MYSA / YESA) a certified threshold committee does NOT owe; suppressed when the latch is set.
 * - `ThresholdCommittee` — the Certification of Threshold itself; generated ONLY when the latch is set.
 *
 * General-purpose, but NC is the only consumer. `Any = 0` so an absent JSON value defaults to it (no silent behavior change for any other jurisdiction).
 */
export const ThresholdCommitteeApplicability = {
  Any: 'Any',
  NonThresholdCommittee: 'NonThresholdCommittee',
  ThresholdCommittee: 'ThresholdCommittee',
} as const;
export type ThresholdCommitteeApplicability = (typeof ThresholdCommitteeApplicability)[keyof typeof ThresholdCommitteeApplicability];
export declare namespace ThresholdCommitteeApplicability {
  type Any = 'Any';
  type NonThresholdCommittee = 'NonThresholdCommittee';
  type ThresholdCommittee = 'ThresholdCommittee';
}

/**
 * HooverCanvassingApi.Features.Finance.Sort.TriageActionNeeded
 *
 * ATL-001 — what the actions column renders for a `TriageItemStatus.NeedsAction` row. Null for Done / Reported rows (their action column is the check / lock, not a triage affordance).
 */
export const TriageActionNeeded = {
  /** Pending contribution → Confirm flip. */
  Confirm: 'Confirm',
  /** Contribution missing donor occupation/employer. */
  ProvideDonorInfo: 'ProvideDonorInfo',
  /** Unclassified bank row → debit/credit picker. */
  Classify: 'Classify',
  /** Orphan refund → pick-parent / standalone. */
  MatchRefund: 'MatchRefund',
  /** ULL-002 — an at/above-threshold expenditure whose payee identity is incomplete (no full address). The amount-aware completeness predicate (D3) surfaces it as "add payee info"; the ULL-004 file-time hard-fail (`MissingExpenditurePayeeAddress`) is the safety net for the same condition. */
  ProvideExpenditureInfo: 'ProvideExpenditureInfo',
  /** An expenditure still sitting in the "Uncategorized" placeholder category — the treasurer hasn't picked a real category yet. It HARD-BLOCKS filing at any amount (`UncategorizedExpenditureEnforceCheck`), so the ledger surfaces it as an amber early-warning ("choose a category") rather than letting it read Done and ambush the treasurer at file time. Amount- independent, so it takes precedence over `TriageActionNeeded.ProvideExpenditureInfo` (the amount-gated payee-address blocker) when both apply. The File-button gate counts it, matching the file-time hard-fail. */
  CategorizeExpenditure: 'CategorizeExpenditure',
  /** RETIRED-AS-A-PRODUCER (ADR-057, #344). This action surfaced a person-vs-organization disagreement between a gift's `ReceiptNature` and its contact's `ContactKind`. That disagreement is now structurally impossible — the person/org gift members were collapsed into the identity-less `Cash` receipt, so a gift carries no shape to contradict — and nothing produces this action (`LedgerStatusProjection.ProjectContribution` no longer receives a disagreement flag). The value is retained on the wire enum; the soft "maybe a person" ledger nudge (now unsuppressed) is the surviving person-vs-org heads-up. */
  ResolveContributorShape: 'ResolveContributorShape',
  /** An organization donor whose `OrganizationType` subtype is unset in a jurisdiction that requires it (AL: PAC / party / corporation / …). It HARD-BLOCKS filing (`MissingContributorSubtype` at export/preflight), so the ledger surfaces it at review time as "classify this organization" rather than letting the row read Done and ambush the treasurer at the filing gate — the same early-warning posture as `TriageActionNeeded.CategorizeExpenditure`. An unfileable blocker, so it takes precedence over the amount-gated `TriageActionNeeded.ProvideDonorInfo` nag (but not the harder `TriageActionNeeded.ResolveContributorShape` contradiction) when several apply. It opens the contributor editor to pick the subtype, and the File-button gate counts it. */
  ClassifyOrganization: 'ClassifyOrganization',
} as const;
export type TriageActionNeeded = (typeof TriageActionNeeded)[keyof typeof TriageActionNeeded];
export declare namespace TriageActionNeeded {
  type Confirm = 'Confirm';
  type ProvideDonorInfo = 'ProvideDonorInfo';
  type Classify = 'Classify';
  type MatchRefund = 'MatchRefund';
  type ProvideExpenditureInfo = 'ProvideExpenditureInfo';
  type CategorizeExpenditure = 'CategorizeExpenditure';
  type ResolveContributorShape = 'ResolveContributorShape';
  type ClassifyOrganization = 'ClassifyOrganization';
}

/**
 * HooverCanvassingApi.Features.Finance.Sort.TriageItemKind
 *
 * ATL-001 (design doc § Wire shape) — the row's *entity*, no longer the triage reason. Pre-ATL the discriminator encoded the triage reason (PendingContribution / MissingInfoContribution / OrphanRefund); post-ATL it encodes what the row *is* and the triage reason rides on `TriageItem.ActionNeeded`.
 *
 * Ordinal order is load-bearing: the same-date tie-break sorts by `SortInboxService.KindOrdinal` (BankLine=0, Contribution=1, Expenditure=2, Refund=3), and that map throws on unmapped kinds — which is why every kind in this enum must carry an ordinal. Expenditure is declared here (ordinal slot 2) but does not surface until ATL-002.
 */
export const TriageItemKind = {
  /** A bank row. It carries no account-type discriminator: ADR-011 dropped the payout-aggregate machinery, so every bank transaction is on the campaign's checking account. */
  BankLine: 'BankLine',
  Contribution: 'Contribution',
  Expenditure: 'Expenditure',
  Refund: 'Refund',
} as const;
export type TriageItemKind = (typeof TriageItemKind)[keyof typeof TriageItemKind];
export declare namespace TriageItemKind {
  type BankLine = 'BankLine';
  type Contribution = 'Contribution';
  type Expenditure = 'Expenditure';
  type Refund = 'Refund';
}

/**
 * HooverCanvassingApi.Features.Finance.Sort.TriageItemStatus
 *
 * ATL-001 — which of the three render states a row carries (design doc § The three row states). Status is computed server-side in `SortInboxService` so the "State rules per kind" matrix lives in exactly one place; the frontend never derives "is this done" from raw fields.
 */
export const TriageItemStatus = {
  /** Action buttons show — today's triage behavior. */
  NeedsAction: 'NeedsAction',
  /** Green check + a resolution word; the row resolved. */
  Done: 'Done',
  /** Lock + "Reported"; inert because it is on a filed report. */
  Reported: 'Reported',
} as const;
export type TriageItemStatus = (typeof TriageItemStatus)[keyof typeof TriageItemStatus];
export declare namespace TriageItemStatus {
  type NeedsAction = 'NeedsAction';
  type Done = 'Done';
  type Reported = 'Reported';
}

/**
 * HooverCanvassingApi.Features.Finance.Sort.TriageResolvedAs
 *
 * ATL-001 — the resolution word rendered next to the check on a `TriageItemStatus.Done` row. Null for NeedsAction / Reported rows. Engineer enums translate to treasurer copy at the FE render boundary (`sortInboxCopy.ts`), never rendered verbatim (feedback_treasurer_first_copy.md).
 */
export const TriageResolvedAs = {
  Confirmed: 'Confirmed',
  Linked: 'Linked',
  Standalone: 'Standalone',
  /** CAP-009 — a lump bank credit classified as a deposit of N already-recorded paper checks. Its OWN born-Done bank row; the contributions report independently, so the deposit line never becomes Reported (terminal Done). */
  DepositOfRecordedChecks: 'DepositOfRecordedChecks',
  /** ADR-014 — the single bank-line non-reporting resolution ("Exclude from report"; treasurer copy "Not reported"). Terminal Done, never Reported; reopenable. Collapses the former Transfer / Dismissed resolution words (their enum members were removed in EFR-003 after the data remap). */
  Excluded: 'Excluded',
  /** ADR-016 — a treasurer-voided money row (Contribution / Expenditure with `LedgerStatus.Voided`). Shares the "Not reported" treasurer word with `TriageResolvedAs.Excluded` (the bank-side sibling) but is kept a DISTINCT value so the two off-report stories stay forensically separable: Voided cancels a money row that really existed (reversible via un-void), Excluded resolves a raw bank line that never became money. Terminal Done, never Reported; the un-void path restores it to Pending. */
  Voided: 'Voided',
} as const;
export type TriageResolvedAs = (typeof TriageResolvedAs)[keyof typeof TriageResolvedAs];
export declare namespace TriageResolvedAs {
  type Confirmed = 'Confirmed';
  type Linked = 'Linked';
  type Standalone = 'Standalone';
  type DepositOfRecordedChecks = 'DepositOfRecordedChecks';
  type Excluded = 'Excluded';
  type Voided = 'Voided';
}

/**
 * HooverCanvassingApi.Features.Billing.TrialEmailType
 *
 * Identifies one of the three trial nudge emails. Matches the "type" field on `POST /api/billing/trial-emails/send` (EML-007) and the three sent-at columns on `TenantCampaign`.
 */
export const TrialEmailType = {
  T3d: 'T3d',
  T1d: 'T1d',
  T1dAfter: 'T1dAfter',
} as const;
export type TrialEmailType = (typeof TrialEmailType)[keyof typeof TrialEmailType];
export declare namespace TrialEmailType {
  type T3d = 'T3d';
  type T1d = 'T1d';
  type T1dAfter = 'T1dAfter';
}

/**
 * HooverCanvassingApi.Features.WorkOrders.VendorHandoffOutcome
 *
 * The result of an `IWorkOrderVendorHandoffService.TrySendHandoffAsync` call.
 */
export const VendorHandoffOutcome = {
  /** Package zipped + uploaded, 24h link emailed to the vendor, order advanced to SentToVendor. */
  Sent: 'Sent',
  /** The order was already SentToVendor — idempotent no-op (no re-zip, no re-email). */
  AlreadySent: 'AlreadySent',
  /** The order was not at Paid — nothing to hand off (defensive no-op). */
  NotPaid: 'NotPaid',
  /** No vendor address configured (`WorkOrders:VendorEmail`) — left at Paid. */
  VendorNotConfigured: 'VendorNotConfigured',
  /** The transactional email returned false — left at Paid (retryable). */
  EmailNotSent: 'EmailNotSent',
  /** The Recipient list isn't frozen yet — left at Paid until it is. */
  ListNotFrozen: 'ListNotFrozen',
} as const;
export type VendorHandoffOutcome = (typeof VendorHandoffOutcome)[keyof typeof VendorHandoffOutcome];
export declare namespace VendorHandoffOutcome {
  type Sent = 'Sent';
  type AlreadySent = 'AlreadySent';
  type NotPaid = 'NotPaid';
  type VendorNotConfigured = 'VendorNotConfigured';
  type EmailNotSent = 'EmailNotSent';
  type ListNotFrozen = 'ListNotFrozen';
}

/**
 * HooverCanvassingApi.Features.Fundraising.Enrichment.VerdictState
 *
 * Whether a durable Match Verdict re-applies to one `Match` at read, and in which direction. The verdict is recorded against a Corpus Identity's fields as adjudicated (never a reference — the identity is replaced wholesale every run), so re-application is a field comparison the matcher already makes, not a lookup. A verdict outranks the score in both directions and permanently.
 */
export const VerdictState = {
  /** No verdict re-applies — the disposition band stands. */
  None: 'None',
  /** An accept verdict re-applies — accepted even if the score later fell below review. */
  Accept: 'Accept',
  /** A reject verdict re-applies — rejected even if the score later rose above auto-accept. */
  Reject: 'Reject',
} as const;
export type VerdictState = (typeof VerdictState)[keyof typeof VerdictState];
export declare namespace VerdictState {
  type None = 'None';
  type Accept = 'Accept';
  type Reject = 'Reject';
}

/**
 * HooverCanvassingApi.Features.Finance.VersionAdoption
 *
 * Whether a ruleset version is law yet.
 */
export const VersionAdoption = {
  Adopted: 'Adopted',
  /** Published as a proposal and not yet in force; never selected. */
  Proposed: 'Proposed',
} as const;
export type VersionAdoption = (typeof VersionAdoption)[keyof typeof VersionAdoption];
export declare namespace VersionAdoption {
  type Adopted = 'Adopted';
  type Proposed = 'Proposed';
}

/**
 * HooverCanvassingApi.Features.Finance.Reporting.Virginia.CometXml.VirginiaContributionCompletenessResult
 *
 * The first-missing-dimension outcome of a COMET contribution completeness check.
 */
export const VirginiaContributionCompletenessResult = {
  Complete: 'Complete',
  MissingName: 'MissingName',
  MissingAddress: 'MissingAddress',
  MissingInKindValuation: 'MissingInKindValuation',
} as const;
export type VirginiaContributionCompletenessResult = (typeof VirginiaContributionCompletenessResult)[keyof typeof VirginiaContributionCompletenessResult];
export declare namespace VirginiaContributionCompletenessResult {
  type Complete = 'Complete';
  type MissingName = 'MissingName';
  type MissingAddress = 'MissingAddress';
  type MissingInKindValuation = 'MissingInKindValuation';
}

/** HooverCanvassingApi.Features.Voice.VoiceExtractionStatus */
export const VoiceExtractionStatus = {
  /** Gemini ran and returned a valid (possibly empty) extraction. */
  Succeeded: 'Succeeded',
  /** Extraction did not produce usable answers (HTTP error, truncation, malformed/empty response). The hub surfaces this so the canvasser knows auto-fill was unavailable rather than seeing it as "no answers given". */
  Failed: 'Failed',
} as const;
export type VoiceExtractionStatus = (typeof VoiceExtractionStatus)[keyof typeof VoiceExtractionStatus];
export declare namespace VoiceExtractionStatus {
  type Succeeded = 'Succeeded';
  type Failed = 'Failed';
}

/**
 * HooverCanvassingApi.Features.Finance.StripeConnect.Money.VoluntaryRefundOutcomeKind
 *
 * What happened, for the controller to map to a wire status.
 */
export const VoluntaryRefundOutcomeKind = {
  /** The refund landed and exactly one `ContributionRefund` is on file. */
  Success: 'Success',
  /** No refundable portal donation matched the id (unknown, non-portal, or Voided) → 404. */
  NotFound: 'NotFound',
  /** Stripe rejected the transfer reversal for lack of connected-account balance (already paid out) → the support hatch. Distinct from a generic failure. */
  InsufficientBalance: 'InsufficientBalance',
  /** Any other Stripe error → a distinct generic failure (never an opaque 500). */
  StripeFailure: 'StripeFailure',
} as const;
export type VoluntaryRefundOutcomeKind = (typeof VoluntaryRefundOutcomeKind)[keyof typeof VoluntaryRefundOutcomeKind];
export declare namespace VoluntaryRefundOutcomeKind {
  type Success = 'Success';
  type NotFound = 'NotFound';
  type InsufficientBalance = 'InsufficientBalance';
  type StripeFailure = 'StripeFailure';
}

/** HooverCanvassingApi.Features.Voters.VoteFrequency */
export const VoteFrequency = {
  NonVoter: 'NonVoter',
  Infrequent: 'Infrequent',
  Frequent: 'Frequent',
} as const;
export type VoteFrequency = (typeof VoteFrequency)[keyof typeof VoteFrequency];
export declare namespace VoteFrequency {
  type NonVoter = 'NonVoter';
  type Infrequent = 'Infrequent';
  type Frequent = 'Frequent';
}

/**
 * HooverCanvassingApi.Features.WorkOrders.VoterDataExportHoldReason
 *
 * Why an automated export was held for a platform admin (ADR-096).
 */
export const VoterDataExportHoldReason = {
  /** L2 refused to start the export. */
  StartRefused: 'StartRefused',
  /** L2 reported the job as `ERROR`. */
  JobError: 'JobError',
  /** The job was still processing past the export timeout. */
  TimedOut: 'TimedOut',
  /** The finished file couldn't be downloaded. */
  DownloadFailed: 'DownloadFailed',
  /** The file's records fell outside the Delivered count allowance of the quote. */
  DeliveredCountOutsideAllowance: 'DeliveredCountOutsideAllowance',
  /** The file lacked a required column, such as the cell phone column for a list bought with a send. */
  MissingColumns: 'MissingColumns',
  /** The list was bought with a Text send and held no valid cell phone. */
  NoValidRecipients: 'NoValidRecipients',
  /** A one-per-household order, which L2's export can't produce. */
  Household: 'Household',
  /** More voters than the driver buys automatically (`MaxVotersPerAutomaticExport`); L2 was never asked. */
  TooLargeForAutomaticExport: 'TooLargeForAutomaticExport',
} as const;
export type VoterDataExportHoldReason = (typeof VoterDataExportHoldReason)[keyof typeof VoterDataExportHoldReason];
export declare namespace VoterDataExportHoldReason {
  type StartRefused = 'StartRefused';
  type JobError = 'JobError';
  type TimedOut = 'TimedOut';
  type DownloadFailed = 'DownloadFailed';
  type DeliveredCountOutsideAllowance = 'DeliveredCountOutsideAllowance';
  type MissingColumns = 'MissingColumns';
  type NoValidRecipients = 'NoValidRecipients';
  type Household = 'Household';
  type TooLargeForAutomaticExport = 'TooLargeForAutomaticExport';
}

/**
 * HooverCanvassingApi.Features.WorkOrders.VoterDataExportState
 *
 * How far a paid Voter data order's automated L2 export has got (ADR-096). The order itself stays Paid until it is fulfilled; this records the export's own progress beside it. Stored as a PascalCase string column.
 */
export const VoterDataExportState = {
  /** No export has been claimed. Every order starts here. */
  NotStarted: 'NotStarted',
  /** Claimed, and L2's start may have been called, but no job id is saved. An order left here was interrupted mid-start and is never retried automatically: a retry could buy twice. */
  Starting: 'Starting',
  /** L2 accepted the start and its job id is saved; the job is processing. */
  Exporting: 'Exporting',
  /** L2 reported the job finished; the file is being downloaded and fulfilled. */
  Downloading: 'Downloading',
  /** The export stopped for a reason a platform admin must review. The order waits for a hand fulfil. */
  Held: 'Held',
} as const;
export type VoterDataExportState = (typeof VoterDataExportState)[keyof typeof VoterDataExportState];
export declare namespace VoterDataExportState {
  type NotStarted = 'NotStarted';
  type Starting = 'Starting';
  type Exporting = 'Exporting';
  type Downloading = 'Downloading';
  type Held = 'Held';
}

/** HooverCanvassingApi.Features.VoterData.VoterDataFieldKind */
export const VoterDataFieldKind = {
  /** Several options, each a checkbox. Every box starts checked. */
  Enum: 'Enum',
  /** Two exclusive options offered as Any · one · the other. */
  Flag: 'Flag',
  /** Vote history in one election, offered as Any · Voted · Didn't vote. It has no options: a rule says whether they voted, and L2 filters the election column with 1 or 0. Election columns can't be stats views, so its counts come from the estimate endpoint, on demand. A rule on it is a `SegmentVotedRule`. */
  Vote: 'Vote',
} as const;
export type VoterDataFieldKind = (typeof VoterDataFieldKind)[keyof typeof VoterDataFieldKind];
export declare namespace VoterDataFieldKind {
  type Enum = 'Enum';
  type Flag = 'Flag';
  type Vote = 'Vote';
}

/**
 * HooverCanvassingApi.Features.VoterData.VoterDataGeography
 *
 * A kind of district or place the builder can scope a list to. The first four are listed whole in the catalog; the rest are below-county places, too many to list, which the picker searches.
 */
export const VoterDataGeography = {
  StateHouse: 'StateHouse',
  StateSenate: 'StateSenate',
  Congressional: 'Congressional',
  County: 'County',
  Precinct: 'Precinct',
  City: 'City',
  CityWard: 'CityWard',
  CityCouncilDistrict: 'CityCouncilDistrict',
  Township: 'Township',
  TownshipWard: 'TownshipWard',
  Borough: 'Borough',
  BoroughWard: 'BoroughWard',
  Village: 'Village',
  VillageWard: 'VillageWard',
  Town: 'Town',
  TownWard: 'TownWard',
  Hamlet: 'Hamlet',
  CountyCommissionerDistrict: 'CountyCommissionerDistrict',
  CountySupervisorialDistrict: 'CountySupervisorialDistrict',
  SchoolDistrict: 'SchoolDistrict',
  CitySchoolDistrict: 'CitySchoolDistrict',
  BoardOfEducationDistrict: 'BoardOfEducationDistrict',
  SchoolBoardDistrict: 'SchoolBoardDistrict',
  LibraryDistrict: 'LibraryDistrict',
  ParkDistrict: 'ParkDistrict',
} as const;
export type VoterDataGeography = (typeof VoterDataGeography)[keyof typeof VoterDataGeography];
export declare namespace VoterDataGeography {
  type StateHouse = 'StateHouse';
  type StateSenate = 'StateSenate';
  type Congressional = 'Congressional';
  type County = 'County';
  type Precinct = 'Precinct';
  type City = 'City';
  type CityWard = 'CityWard';
  type CityCouncilDistrict = 'CityCouncilDistrict';
  type Township = 'Township';
  type TownshipWard = 'TownshipWard';
  type Borough = 'Borough';
  type BoroughWard = 'BoroughWard';
  type Village = 'Village';
  type VillageWard = 'VillageWard';
  type Town = 'Town';
  type TownWard = 'TownWard';
  type Hamlet = 'Hamlet';
  type CountyCommissionerDistrict = 'CountyCommissionerDistrict';
  type CountySupervisorialDistrict = 'CountySupervisorialDistrict';
  type SchoolDistrict = 'SchoolDistrict';
  type CitySchoolDistrict = 'CitySchoolDistrict';
  type BoardOfEducationDistrict = 'BoardOfEducationDistrict';
  type SchoolBoardDistrict = 'SchoolBoardDistrict';
  type LibraryDistrict = 'LibraryDistrict';
  type ParkDistrict = 'ParkDistrict';
}

/**
 * HooverCanvassingApi.Features.VoterData.VoterDataOrderingBlock
 *
 * Why a subsidiary can't take a Voter data order.
 */
export const VoterDataOrderingBlock = {
  /** No per-record Stripe price is configured. */
  PriceNotConfigured: 'PriceNotConfigured',
  /** The Stripe price doesn't charge `VoterDataConstants.PricePerRecordCents`. */
  PriceMismatch: 'PriceMismatch',
  /** Stripe couldn't be asked what the price charges. */
  StripeUnavailable: 'StripeUnavailable',
  /** The campaign's `WorkOrdersEnabled` switch is off, so it can't order from us (ADR-096). */
  WorkOrdersDisabled: 'WorkOrdersDisabled',
} as const;
export type VoterDataOrderingBlock = (typeof VoterDataOrderingBlock)[keyof typeof VoterDataOrderingBlock];
export declare namespace VoterDataOrderingBlock {
  type PriceNotConfigured = 'PriceNotConfigured';
  type PriceMismatch = 'PriceMismatch';
  type StripeUnavailable = 'StripeUnavailable';
  type WorkOrdersDisabled = 'WorkOrdersDisabled';
}

/** HooverCanvassingApi.Features.VoterData.VoterDataRaceMatchSource */
export const VoterDataRaceMatchSource = {
  /** The campaign's linked Race says where its voters are. */
  LinkedRace: 'LinkedRace',
  /** Jev picked a place for the race's name from the cross-kind search. */
  NameMatch: 'NameMatch',
} as const;
export type VoterDataRaceMatchSource = (typeof VoterDataRaceMatchSource)[keyof typeof VoterDataRaceMatchSource];
export declare namespace VoterDataRaceMatchSource {
  type LinkedRace = 'LinkedRace';
  type NameMatch = 'NameMatch';
}

/** HooverCanvassingApi.Features.Voters.VoterElectionKind */
export const VoterElectionKind = {
  General: 'General',
  Primary: 'Primary',
  PresidentialPrimary: 'PresidentialPrimary',
  Local: 'Local',
  Special: 'Special',
  Runoff: 'Runoff',
} as const;
export type VoterElectionKind = (typeof VoterElectionKind)[keyof typeof VoterElectionKind];
export declare namespace VoterElectionKind {
  type General = 'General';
  type Primary = 'Primary';
  type PresidentialPrimary = 'PresidentialPrimary';
  type Local = 'Local';
  type Special = 'Special';
  type Runoff = 'Runoff';
}

/**
 * HooverCanvassingApi.Features.Voters.VoterField
 *
 * A voter field an import can write. Names, address and voter id are always written, so they aren't listed.
 */
export const VoterField = {
  MiddleName: 'MiddleName',
  City: 'City',
  State: 'State',
  Zip: 'Zip',
  Age: 'Age',
  Gender: 'Gender',
  CellPhone: 'CellPhone',
  Email: 'Email',
  Party: 'Party',
  Ethnicity: 'Ethnicity',
  Religion: 'Religion',
  Income: 'Income',
  VoteFrequency: 'VoteFrequency',
  VoteFrequencyRaw: 'VoteFrequencyRaw',
  Education: 'Education',
  ConservativeDonor: 'ConservativeDonor',
  LiberalDonor: 'LiberalDonor',
  StateVoterId: 'StateVoterId',
  CountyVoterId: 'CountyVoterId',
  NameSuffix: 'NameSuffix',
  BirthDate: 'BirthDate',
  Landline: 'Landline',
  CellPhoneConfidence: 'CellPhoneConfidence',
  LandlineConfidence: 'LandlineConfidence',
  DoNotCall: 'DoNotCall',
  WalkSequenceOddEven: 'WalkSequenceOddEven',
  WalkSequenceZigZag: 'WalkSequenceZigZag',
  GeneralTurnoutPercent: 'GeneralTurnoutPercent',
  PrimaryTurnoutPercent: 'PrimaryTurnoutPercent',
  County: 'County',
  Precinct: 'Precinct',
  CongressionalDistrict: 'CongressionalDistrict',
  StateHouseDistrict: 'StateHouseDistrict',
  StateSenateDistrict: 'StateSenateDistrict',
  CityWard: 'CityWard',
  Township: 'Township',
  Homeowner: 'Homeowner',
  DwellingType: 'DwellingType',
} as const;
export type VoterField = (typeof VoterField)[keyof typeof VoterField];
export declare namespace VoterField {
  type MiddleName = 'MiddleName';
  type City = 'City';
  type State = 'State';
  type Zip = 'Zip';
  type Age = 'Age';
  type Gender = 'Gender';
  type CellPhone = 'CellPhone';
  type Email = 'Email';
  type Party = 'Party';
  type Ethnicity = 'Ethnicity';
  type Religion = 'Religion';
  type Income = 'Income';
  type VoteFrequency = 'VoteFrequency';
  type VoteFrequencyRaw = 'VoteFrequencyRaw';
  type Education = 'Education';
  type ConservativeDonor = 'ConservativeDonor';
  type LiberalDonor = 'LiberalDonor';
  type StateVoterId = 'StateVoterId';
  type CountyVoterId = 'CountyVoterId';
  type NameSuffix = 'NameSuffix';
  type BirthDate = 'BirthDate';
  type Landline = 'Landline';
  type CellPhoneConfidence = 'CellPhoneConfidence';
  type LandlineConfidence = 'LandlineConfidence';
  type DoNotCall = 'DoNotCall';
  type WalkSequenceOddEven = 'WalkSequenceOddEven';
  type WalkSequenceZigZag = 'WalkSequenceZigZag';
  type GeneralTurnoutPercent = 'GeneralTurnoutPercent';
  type PrimaryTurnoutPercent = 'PrimaryTurnoutPercent';
  type County = 'County';
  type Precinct = 'Precinct';
  type CongressionalDistrict = 'CongressionalDistrict';
  type StateHouseDistrict = 'StateHouseDistrict';
  type StateSenateDistrict = 'StateSenateDistrict';
  type CityWard = 'CityWard';
  type Township = 'Township';
  type Homeowner = 'Homeowner';
  type DwellingType = 'DwellingType';
}

/** HooverCanvassingApi.Features.Voters.VoterSource */
export const VoterSource = {
  VoterFile: 'VoterFile',
  CanvasserAdded: 'CanvasserAdded',
} as const;
export type VoterSource = (typeof VoterSource)[keyof typeof VoterSource];
export declare namespace VoterSource {
  type VoterFile = 'VoterFile';
  type CanvasserAdded = 'CanvasserAdded';
}

/** HooverCanvassingApi.Features.Voters.VoterSupport */
export const VoterSupport = {
  StrongYes: 'StrongYes',
  LeaningYes: 'LeaningYes',
  Undecided: 'Undecided',
  LeaningNo: 'LeaningNo',
  StrongNo: 'StrongNo',
} as const;
export type VoterSupport = (typeof VoterSupport)[keyof typeof VoterSupport];
export declare namespace VoterSupport {
  type StrongYes = 'StrongYes';
  type LeaningYes = 'LeaningYes';
  type Undecided = 'Undecided';
  type LeaningNo = 'LeaningNo';
  type StrongNo = 'StrongNo';
}

/**
 * HooverCanvassingApi.Features.Assistant.WakeupState
 *
 * A pending wake-up is consumed by the background run it wakes, or cancelled; either frees its slot.
 */
export const WakeupState = {
  Pending: 'Pending',
  Consumed: 'Consumed',
  Cancelled: 'Cancelled',
} as const;
export type WakeupState = (typeof WakeupState)[keyof typeof WakeupState];
export declare namespace WakeupState {
  type Pending = 'Pending';
  type Consumed = 'Consumed';
  type Cancelled = 'Cancelled';
}

/** HooverCanvassingApi.Features.Walkbooks.WalkbookStatus */
export const WalkbookStatus = {
  Active: 'Active',
  Archived: 'Archived',
} as const;
export type WalkbookStatus = (typeof WalkbookStatus)[keyof typeof WalkbookStatus];
export declare namespace WalkbookStatus {
  type Active = 'Active';
  type Archived = 'Archived';
}

/**
 * HooverCanvassingApi.Features.Finance.Filing.WarningAction
 *
 * What the Heads-up card lets the treasurer do with a warning. Presence/absence in `PreflightWarning.AvailableActions` encodes severity.
 */
export const WarningAction = {
  /** Concrete fix path is available (e.g., upload a file). */
  Resolve: 'Resolve',
  /** Treasurer can proceed with a typed-reason attestation. */
  Attest: 'Attest',
  /** Treasurer can defer; warning re-surfaces next period. */
  Dismiss: 'Dismiss',
  /** ADR-025 — the "mark as written off" affordance. Encoded as an action member (severity-and-affordances-are-actions-presence, never a wire type-identity check): a warning offering this lets the treasurer write off the affected contribution via the atomic manual-void flow. Emitted only by the aged-overdue check; the frontend gates the button on this action's presence, not on a hard-coded `AgedOverdueUnsettled` identity compare. */
  WriteOff: 'WriteOff',
  /** Issue #110 — the "I'll handle it myself" step-aside affordance on the armed processor-fee pin. The treasurer consciously opts out of the must-record fee gate for a period + processor (recording it by hand, a comped/no-fee arrangement, or deferring to a later amendment/upload) via `POST /api/finance/processor-fees/step-aside`. Encoded as an action member (same affordances-are-actions-presence discipline as `WarningAction.WriteOff`) so the fee pin's secondary action rides the shared wire vocabulary rather than a bespoke type. The disposition it captures (reason + optional note) is `ProcessorFeeStepAsideReason`, mirrored in the shared TypeScript preflight types. */
  StepAside: 'StepAside',
} as const;
export type WarningAction = (typeof WarningAction)[keyof typeof WarningAction];
export declare namespace WarningAction {
  type Resolve = 'Resolve';
  type Attest = 'Attest';
  type Dismiss = 'Dismiss';
  type WriteOff = 'WriteOff';
  type StepAside = 'StepAside';
}

/**
 * HooverCanvassingApi.Features.Finance.Filing.WarningDisposition
 *
 * Treasurer's chosen outcome for a single warning, captured in the `SubmissionPackageSigned` audit payload.
 */
export const WarningDisposition = {
  Dismissed: 'Dismissed',
  Attested: 'Attested',
} as const;
export type WarningDisposition = (typeof WarningDisposition)[keyof typeof WarningDisposition];
export declare namespace WarningDisposition {
  type Dismissed = 'Dismissed';
  type Attested = 'Attested';
}

/**
 * HooverCanvassingApi.Features.Finance.StripeConnect.Webhooks.WebhookDispatchOutcome
 *
 * The outcome a handler (or the no-handler fallback) reports for one event. Maps 1:1 onto the durable `WebhookEventStatus` stamped on the persisted event row.
 */
export const WebhookDispatchOutcome = {
  Processed: 'Processed',
  Parked: 'Parked',
  Quarantined: 'Quarantined',
} as const;
export type WebhookDispatchOutcome = (typeof WebhookDispatchOutcome)[keyof typeof WebhookDispatchOutcome];
export declare namespace WebhookDispatchOutcome {
  type Processed = 'Processed';
  type Parked = 'Parked';
  type Quarantined = 'Quarantined';
}

/**
 * HooverCanvassingApi.Features.Finance.WebhookEventStatus
 *
 * Lifecycle of a `StripeConnectWebhookEvent`. Received = persisted raw, not yet applied. Processed = applied to the account/contribution projection. Parked = deferred (its target campaign/account is not yet resolvable and it should be retried). Quarantined = repeatedly unprocessable / suspect, held out of the retry loop for manual review. Stored as string.
 */
export const WebhookEventStatus = {
  Received: 'Received',
  Processed: 'Processed',
  Parked: 'Parked',
  Quarantined: 'Quarantined',
} as const;
export type WebhookEventStatus = (typeof WebhookEventStatus)[keyof typeof WebhookEventStatus];
export declare namespace WebhookEventStatus {
  type Received = 'Received';
  type Processed = 'Processed';
  type Parked = 'Parked';
  type Quarantined = 'Quarantined';
}

/**
 * HooverCanvassingApi.Features.Finance.StripeConnect.Webhooks.WebhookProcessingResult
 *
 * How the processor resolved one event — the controller maps this to an HTTP status.
 */
export const WebhookProcessingResult = {
  /** Persisted + dispatched (processed / parked / quarantined) and committed. → 2xx. */
  Acknowledged: 'Acknowledged',
  /** A row for this event_id already existed (or lost the insert race). → 2xx, no re-processing. */
  Duplicate: 'Duplicate',
  /** A recoverable failure; nothing committed. → non-2xx so Stripe retries. */
  TransientFailure: 'TransientFailure',
} as const;
export type WebhookProcessingResult = (typeof WebhookProcessingResult)[keyof typeof WebhookProcessingResult];
export declare namespace WebhookProcessingResult {
  type Acknowledged = 'Acknowledged';
  type Duplicate = 'Duplicate';
  type TransientFailure = 'TransientFailure';
}

/**
 * HooverCanvassingApi.Features.WorkOrders.WorkOrderAction
 *
 * The hand-driven edge an operator asks the transition endpoint to take on the Work Order spine. Each value maps to one guarded method on `WorkOrder`. This tier exposes only the operator-driven manual tail plus the terminal off-ramps — the Invoice / MarkPaid / SendToVendor edges are driven by the Stripe / webhook / handoff chain, not the operator, so they are deliberately NOT modeled here. Carried only on the wire (request body), never persisted.
 */
export const WorkOrderAction = {
  /** SentToVendor → VendorPaid (operator marks the manual ACH settled). */
  MarkVendorPaid: 'MarkVendorPaid',
  /** VendorPaid → Complete (operator marks the send finished). */
  Complete: 'Complete',
  /** Draft → Cancelled (operator aborts a never-invoiced draft). Draft-only. */
  Cancel: 'Cancel',
  /** Any non-terminal state → Failed (operator marks a send that could not complete). */
  Fail: 'Fail',
} as const;
export type WorkOrderAction = (typeof WorkOrderAction)[keyof typeof WorkOrderAction];
export declare namespace WorkOrderAction {
  type MarkVendorPaid = 'MarkVendorPaid';
  type Complete = 'Complete';
  type Cancel = 'Cancel';
  type Fail = 'Fail';
}

/**
 * HooverCanvassingApi.Features.WorkOrders.WorkOrderCheckoutLineKind
 *
 * A line on a Work Order invoice, in the order a checkout lists them.
 */
export const WorkOrderCheckoutLineKind = {
  /** A Voter data list: its per-record Price × its quoted records. */
  VoterData: 'VoterData',
  /** A Text send: the per-Recipient Price × its Recipients. */
  TextSend: 'TextSend',
  /** The one-time 10DLC registration on a campaign's first Text send. */
  Registration: 'Registration',
} as const;
export type WorkOrderCheckoutLineKind = (typeof WorkOrderCheckoutLineKind)[keyof typeof WorkOrderCheckoutLineKind];
export declare namespace WorkOrderCheckoutLineKind {
  type VoterData = 'VoterData';
  type TextSend = 'TextSend';
  type Registration = 'Registration';
}

/**
 * HooverCanvassingApi.Features.WorkOrders.WorkOrderPaidOutcome
 *
 * The result of an `IWorkOrderPaymentService.AdvanceToPaidAsync` call.
 */
export const WorkOrderPaidOutcome = {
  /** No Work Order matched the invoice (a subscription/unknown invoice) — logged no-op. */
  NotFound: 'NotFound',
  /** The Work Order advanced `Invoiced → Paid` on this call. */
  Advanced: 'Advanced',
  /** The Work Order was already past `Invoiced` — a redelivery no-op. */
  AlreadyPaid: 'AlreadyPaid',
} as const;
export type WorkOrderPaidOutcome = (typeof WorkOrderPaidOutcome)[keyof typeof WorkOrderPaidOutcome];
export declare namespace WorkOrderPaidOutcome {
  type NotFound = 'NotFound';
  type Advanced = 'Advanced';
  type AlreadyPaid = 'AlreadyPaid';
}

/**
 * HooverCanvassingApi.Features.WorkOrders.WorkOrderStallKind
 *
 * A way an order can sit still past its threshold in `AtMostOnceMonitor`.
 */
export const WorkOrderStallKind = {
  /** A Paid send nothing waits on, an hour after payment: the release sweep didn't hand it off. */
  ReadySendNotHandedOff: 'ReadySendNotHandedOff',
  /** A Paid first send waiting on a registration that failed, isn't moving, or won't finish before its send date. */
  RegistrationStalled: 'RegistrationStalled',
  /** A send at the Vendor whose send date is more than a day past. */
  SentToVendorPastSendDate: 'SentToVendorPastSendDate',
  /** A paid list the export driver never started. */
  ExportNotStarted: 'ExportNotStarted',
  /** An export L2 finished whose file never downloaded. */
  ExportDownloadStuck: 'ExportDownloadStuck',
  /** An export held for a platform admin who hasn't acted. */
  ExportHeld: 'ExportHeld',
  /** A delivered list whose import never finished. */
  ImportStalled: 'ImportStalled',
  /** A bought list still Invoiced though its Text send was paid on the same invoice: the list's arm of that invoice.paid never committed, and the export driver fulfils only Paid lists. */
  BoughtListLeftInvoiced: 'BoughtListLeftInvoiced',
  /** A Complete Text send whose results report no one attached: the customer is still waiting for it. */
  ResultsReportMissing: 'ResultsReportMissing',
} as const;
export type WorkOrderStallKind = (typeof WorkOrderStallKind)[keyof typeof WorkOrderStallKind];
export declare namespace WorkOrderStallKind {
  type ReadySendNotHandedOff = 'ReadySendNotHandedOff';
  type RegistrationStalled = 'RegistrationStalled';
  type SentToVendorPastSendDate = 'SentToVendorPastSendDate';
  type ExportNotStarted = 'ExportNotStarted';
  type ExportDownloadStuck = 'ExportDownloadStuck';
  type ExportHeld = 'ExportHeld';
  type ImportStalled = 'ImportStalled';
  type BoughtListLeftInvoiced = 'BoughtListLeftInvoiced';
  type ResultsReportMissing = 'ResultsReportMissing';
}

/**
 * HooverCanvassingApi.Features.WorkOrders.WorkOrderStatus
 *
 * Every status a Work Order can hold. Stored as a PascalCase string column (mirrors the Finance enum convention) so the values are stable across the API and the shared TS mirror. Which of these a type actually walks is its `WorkOrderSpine`; the terminal off-ramps `Cancelled` / `Failed` apply to every type.
 */
export const WorkOrderStatus = {
  /** Operator is building the send. The only state this ticket creates. */
  Draft: 'Draft',
  /** A Stripe invoice has been created; the recipient list is frozen. */
  Invoiced: 'Invoiced',
  /** The `invoice.paid` webhook confirmed payment cleared. */
  Paid: 'Paid',
  /** The handoff package was compiled and emailed to the vendor. */
  SentToVendor: 'SentToVendor',
  /** The operator manually ACH'd the vendor and marked it settled. */
  VendorPaid: 'VendorPaid',
  /** The operator marked the send finished. */
  Complete: 'Complete',
  /** Terminal — the operator cancelled the Work Order. */
  Cancelled: 'Cancelled',
  /** Terminal — the Work Order failed and will not be delivered. */
  Failed: 'Failed',
} as const;
export type WorkOrderStatus = (typeof WorkOrderStatus)[keyof typeof WorkOrderStatus];
export declare namespace WorkOrderStatus {
  type Draft = 'Draft';
  type Invoiced = 'Invoiced';
  type Paid = 'Paid';
  type SentToVendor = 'SentToVendor';
  type VendorPaid = 'VendorPaid';
  type Complete = 'Complete';
  type Cancelled = 'Cancelled';
  type Failed = 'Failed';
}

/**
 * HooverCanvassingApi.Features.WorkOrders.WorkOrderType
 *
 * Discriminator for the concrete Work Order type on the single concrete `WorkOrder` table (not EF TPH inheritance — future types stay additive). Stored as a PascalCase string column.
 */
export const WorkOrderType = {
  /** A mass SMS/MMS send fulfilled through an external messaging vendor. */
  TextSend: 'TextSend',
  /** A voter list bought from L2 at a per-record price, fulfilled by a platform admin — no outside vendor, so its spine skips `SentToVendor` and `VendorPaid`. */
  VoterData: 'VoterData',
} as const;
export type WorkOrderType = (typeof WorkOrderType)[keyof typeof WorkOrderType];
export declare namespace WorkOrderType {
  type TextSend = 'TextSend';
  type VoterData = 'VoterData';
}
